<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'pa-0' : ''">
			<div v-if="loading" class="d-flex justify-center align-center py-12">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<template v-else>
				<!-- Header / Toolbar -->
				<div
					class="ticketing-toolbar mb-3"
					:class="{ 'ticketing-toolbar--desktop': !isMobile }"
				>
					<div class="ticketing-toolbar__row ticketing-toolbar__filters d-flex align-center flex-wrap" style="gap: 8px">
						<v-btn-toggle
							v-model="filterTab"
							mandatory
							dense
							color="primary"
							group
							class="ticketing-filter-toggle"
						>
							<v-btn small :value="'active'">
								{{ $t('ticketing.active') }} ({{ counts.active }})
								<v-icon v-if="tabsWithUnseenMentions.active" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'mine'">
								{{ $t('ticketing.mine') }} ({{ counts.mine }})
								<v-icon v-if="tabsWithUnseenMentions.mine" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'mentioned'">
								{{ $t('ticketing.mentioned') }} ({{ counts.mentioned }})
								<v-icon
									v-if="mentionedHasUnseen"
									x-small
									color="amber darken-2"
									class="ml-1"
								>
									mdi-circle
								</v-icon>
							</v-btn>
							<v-btn small :value="'watching'">
								{{ $t('ticketing.watching') }} ({{ counts.watching }})
								<v-icon v-if="tabsWithUnseenMentions.watching" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'urgent'">
								{{ $t('ticketing.urgent') }} ({{ counts.urgent }})
								<v-icon v-if="tabsWithUnseenMentions.urgent" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'unassigned'" :class="{ 'red--text': counts.unassigned > 0 }">
								{{ $t('ticketing.unassigned') }} ({{ counts.unassigned }})
								<v-icon v-if="tabsWithUnseenMentions.unassigned" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'needs-support'" :class="{ 'red--text': counts.needsSupport > 0 }">
								{{ $t('ticketing.status.needs-support-action') }} ({{ counts.needsSupport }})
								<v-icon v-if="tabsWithUnseenMentions.needsSupport" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'hardware'">
								{{ $t('ticketing.type.hardware') }} ({{ counts.hardware }})
								<v-icon v-if="tabsWithUnseenMentions.hardware" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'porting'">
								{{ $t('ticketing.type.porting') }} ({{ counts.porting }})
								<v-icon v-if="tabsWithUnseenMentions.porting" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'resolved'">
								{{ $t('ticketing.resolved') }} ({{ counts.resolved }})
								<v-icon v-if="tabsWithUnseenMentions.resolved" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
							<v-btn small :value="'all'">
								{{ $t('ticketing.all') }} ({{ tickets.length }})
								<v-icon v-if="tabsWithUnseenMentions.all" x-small color="amber darken-2" class="ml-1">mdi-circle</v-icon>
							</v-btn>
						</v-btn-toggle>
					</div>

					<div class="ticketing-toolbar__row ticketing-toolbar__row--secondary d-flex align-center flex-wrap" style="gap: 8px">
						<v-text-field
							v-model="search"
							:placeholder="$t('search') + '...'"
							prepend-inner-icon="mdi-magnify"
							hide-details
							dense
							outlined
							clearable
							style="max-width: 480px; flex: 1 1 320px"
						/>

						<v-autocomplete
							v-model="orgFilter"
							:items="organizationItems"
							item-text="text"
							item-value="value"
							:filter="filterOrganization"
							:label="$t('ticketing.organization')"
							hide-details
							dense
							outlined
							clearable
							:class="{ 'ml-3': !$vuetify.breakpoint.xs }"
							style="max-width: 280px"
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.text }}</v-list-item-title>
									<v-list-item-subtitle class="text-caption grey--text">
										{{ item.value }}
									</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<v-spacer />

						<v-menu
							v-if="!$vuetify.breakpoint.xs"
							v-model="notifMenuOpen"
							offset-y
							:close-on-content-click="false"
							max-height="500"
						>
							<template #activator="{ on, attrs }">
								<v-btn icon v-bind="attrs" v-on="on">
									<v-badge
										:value="unreadCount > 0"
										:content="unreadCount"
										color="red"
										overlap
									>
										<v-icon>mdi-bell-outline</v-icon>
									</v-badge>
								</v-btn>
							</template>
							<ticketing-notifications-panel
								:notifications="notifications"
								:unread-count="unreadCount"
								@notification-click="handleNotificationClick"
								@mark-all-read="markAllRead"
								@clear-all="clearAllConfirmOpen = true"
								@remove="removeNotification"
							/>
						</v-menu>

						<template v-else>
							<v-btn icon @click="notifMenuOpen = true">
								<v-badge
									:value="unreadCount > 0"
									:content="unreadCount"
									color="red"
									overlap
								>
									<v-icon>mdi-bell-outline</v-icon>
								</v-badge>
							</v-btn>
							<v-dialog
								v-model="notifMenuOpen"
								fullscreen
								scrollable
								transition="dialog-bottom-transition"
							>
								<ticketing-notifications-panel
									:notifications="notifications"
									:unread-count="unreadCount"
									mobile
									@notification-click="handleNotificationClick"
									@mark-all-read="markAllRead"
									@clear-all="clearAllConfirmOpen = true"
									@remove="removeNotification"
									@close="notifMenuOpen = false"
								/>
							</v-dialog>
						</template>

						<v-btn color="primary" small class="ml-3" @click="openQuickAdd()">
							<v-icon left small>mdi-plus</v-icon>
							{{ $t('ticketing.new-ticket') }}
						</v-btn>
					</div>
				</div>

				<div :class="{ 'ticketing-table-wrap': !isMobile }">
					<v-data-table
						v-if="!isMobile"
						:headers="headers"
						:items="filteredTickets"
						:items-per-page="-1"
						:footer-props="{ 'items-per-page-options': [10, 15, 25, 50, -1] }"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
						:item-class="rowClass"
						dense
						class="ticketing-table"
						@click:row="openTicket"
					>
						<template #item.__indicators="{ item }">
							<div class="d-flex align-center indicators-cell">
								<v-tooltip v-if="hasUnseenMentions(item)" bottom>
									<template #activator="{ on, attrs }">
										<v-icon
											x-small
											color="amber darken-2"
											class="unread-dot flex-shrink-0"
											v-bind="attrs"
											v-on="on"
										>
											mdi-circle
										</v-icon>
									</template>
									<span>{{ $t('ticketing.unseen-mention') }}</span>
								</v-tooltip>
								<v-tooltip v-if="hasUnseenMessages(item)" bottom>
									<template #activator="{ on, attrs }">
										<v-icon
											x-small
											color="primary"
											class="unread-dot flex-shrink-0"
											v-bind="attrs"
											v-on="on"
										>
											mdi-circle
										</v-icon>
									</template>
									<span>{{ $t('ticketing.unseen-messages') }}</span>
								</v-tooltip>
								<v-icon
									v-if="hasUnreadFor(item.id)"
									x-small
									color="red"
									class="unread-dot unread-dot--unread flex-shrink-0"
								>
									mdi-circle
								</v-icon>
							</div>
						</template>

						<template #item.title="{ item }">
							<div class="d-flex align-center">
								<span class="text-caption" :title="item.title">{{ item.title }}</span>
								<v-tooltip
									v-for="(v, vIdx) in liveViewersForTicket(item)"
									:key="`live-${item.id}-${v.userId}`"
									bottom
								>
									<template #activator="{ on, attrs }">
										<v-avatar
											size="16"
											:color="userColor(v.userId)"
											class="live-viewer-avatar flex-shrink-0"
											:class="vIdx === 0 ? 'ml-3' : 'ml-1'"
											:style="{ '--live-color': userColor(v.userId) }"
											v-bind="attrs"
											v-on="on"
										>
											<span class="white--text live-viewer-avatar__initials">{{ viewerInitials(v.name) }}</span>
										</v-avatar>
									</template>
									<span>{{ $t('ticketing.viewing-now', { name: v.name || v.userId }) }}</span>
								</v-tooltip>
							</div>
						</template>

						<template #item.status="{ item }">
							<span class="text-caption">
								{{ $t(`ticketing.status.${item.status}`) }}
							</span>
						</template>

						<template #item.priority="{ item }">
							<span class="text-caption font-weight-medium" :style="{ color: priorityHex(item.priority) }">
								{{ priorityEmoji(item.priority) }}&nbsp; {{ $t(`ticketing.priority.${item.priority}`) }}
							</span>
						</template>

						<template #item.type="{ item }">
							<span class="text-caption">{{ $t(`ticketing.type.${item.type}`) }}</span>
						</template>

						<template #item.organizationName="{ item }">
							<div class="d-flex align-center" :title="organizationCellTooltip(item)">
								<v-avatar size="20" class="mr-2 org-logo-avatar">
									<v-img v-if="organizationLogo(item.organizationId)" :src="organizationLogo(item.organizationId)" />
									<v-icon v-else x-small color="grey">mdi-domain</v-icon>
								</v-avatar>
								<span class="text-caption text-truncate">{{ item.organizationName }}</span>
								<v-tooltip v-if="extraOrgCount(item) > 0" bottom>
									<template #activator="{ on, attrs }">
										<v-chip
											v-bind="attrs"
											x-small
											class="ml-2 extra-orgs-chip"
											color="primary"
											outlined
											v-on="on"
										>
											+{{ extraOrgCount(item) }}
										</v-chip>
									</template>
									<div class="text-caption">
										<div class="font-weight-medium mb-1">
											{{ $t('ticketing.also-affected', { count: extraOrgCount(item) }) }}
										</div>
										<div v-for="o in extraOrgNames(item)" :key="o">
											• {{ o }}
										</div>
									</div>
								</v-tooltip>
							</div>
						</template>

						<template #item.assignedUserName="{ item }">
							<div v-if="item.assignedUserName" class="d-flex align-center" :title="item.assignedUserName">
								<v-avatar
									size="20"
									:color="userColor(item.assignedUserId)"
									class="mr-2 white--text text-caption"
								>
									{{ viewerInitials(item.assignedUserName) }}
								</v-avatar>
								<span class="text-caption text-truncate">{{ item.assignedUserName }}</span>
							</div>
							<span v-else class="text-caption">-</span>
						</template>

						<template #item.assignedDevopsNames="{ item }">
							<div class="d-flex align-center" style="overflow: hidden;">
								<v-tooltip
									v-for="(uid, idx) in (item.assignedDevopsIds || []).slice(0, 3)"
									:key="idx"
									bottom
								>
									<template #activator="{ on, attrs }">
										<v-avatar
											size="22"
											:color="userColor(uid)"
											class="mr-1 flex-shrink-0"
											v-bind="attrs"
											v-on="on"
										>
											<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
										</v-avatar>
									</template>
									<span>{{ devopsName(uid) }}</span>
								</v-tooltip>
								<v-tooltip
									v-if="(item.assignedDevopsIds || []).length > 3"
									bottom
								>
									<template #activator="{ on, attrs }">
										<span
											class="text-caption grey--text"
											v-bind="attrs"
											v-on="on"
										>+{{ item.assignedDevopsIds.length - 3 }}</span>
									</template>
									<span>{{ item.assignedDevopsIds.slice(3).map(devopsName).join(', ') }}</span>
								</v-tooltip>
								<span v-if="!item.assignedDevopsIds?.length" class="text-caption grey--text">-</span>
							</div>
						</template>

						<template #item.watcherIds="{ item }">
							<div class="d-flex align-center" style="overflow: hidden;">
								<v-tooltip
									v-for="(uid, idx) in (item.watcherIds || []).slice(0, 3)"
									:key="idx"
									bottom
								>
									<template #activator="{ on, attrs }">
										<v-avatar
											size="22"
											:color="userColor(uid)"
											class="mr-1 flex-shrink-0"
											v-bind="attrs"
											v-on="on"
										>
											<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
										</v-avatar>
									</template>
									<span>{{ devopsName(uid) }}</span>
								</v-tooltip>
								<v-tooltip
									v-if="(item.watcherIds || []).length > 3"
									bottom
								>
									<template #activator="{ on, attrs }">
										<span
											class="text-caption grey--text"
											v-bind="attrs"
											v-on="on"
										>+{{ item.watcherIds.length - 3 }}</span>
									</template>
									<span>{{ item.watcherIds.slice(3).map(devopsName).join(', ') }}</span>
								</v-tooltip>
								<span v-if="!item.watcherIds?.length" class="text-caption grey--text">-</span>
							</div>
						</template>

						<template #item.lastFollowUp="{ item }">
							<v-tooltip bottom open-delay="0" content-class="follow-up-tooltip-dark">
								<template #activator="{ on, attrs }">
									<div
										class="d-flex align-center text-no-wrap"
										style="gap: 4px"
										v-bind="attrs"
										v-on="on"
									>
										<template v-if="ticketFollowUpDisplay(item).lastFollowUp">
											<v-icon x-small :color="ticketFollowUpDisplay(item).isStale ? 'red' : undefined">{{ followUpIcon(ticketFollowUpDisplay(item).lastFollowUp.method) }}</v-icon>
											<span
												class="text-caption font-weight-medium"
												:class="{ 'red--text': ticketFollowUpDisplay(item).isStale }"
											>
												{{ formatTime(ticketFollowUpDisplay(item).lastFollowUp.at) }}
											</span>
										</template>
										<span v-else class="text-caption red--text font-weight-medium">{{ $t('ticketing.never-follow-up') }}</span>
										<span
											v-if="ticketFollowUpEntries(item).length > 1"
											class="text-caption grey--text"
										>
											({{ ticketFollowUpFollowedCount(item) }}/{{ ticketFollowUpEntries(item).length }})
										</span>
									</div>
								</template>
								<div>
									<div
										v-for="entry in ticketFollowUpEntries(item)"
										:key="entry.org.id || '_'"
										class="mb-1"
									>
										<div class="font-weight-medium">
											{{ entry.org.name || '—' }}
										</div>
										<div v-if="entry.lastFollowUp" :class="{ 'red--text': isFollowUpStale(entry.lastFollowUp.at) }">
											{{ followUpTooltip(entry.lastFollowUp) }}
										</div>
										<div v-else class="red--text">
											{{ $t('ticketing.never-follow-up') }}
										</div>
									</div>
								</div>
							</v-tooltip>
						</template>

						<template #item.createdAt="{ item }">
							<span v-cursor-tip="formatTimeAbsolute(item.createdAt)" class="text-caption">{{ formatTime(item.createdAt) }}</span>
						</template>

						<template #item.updatedAt="{ item }">
							<span v-cursor-tip="formatTimeAbsolute(item.updatedAt)" class="text-caption">{{ formatTime(item.updatedAt) }}</span>
						</template>

						<template #item.resolvedOrClosedAt="{ item }">
							<span v-if="resolvedOrClosedAt(item)" v-cursor-tip="resolvedOrClosedTooltip(item)" class="text-caption">{{ formatTime(resolvedOrClosedAt(item)) }}</span>
							<span v-else class="text-caption grey--text">—</span>
						</template>
					</v-data-table>
				</div>

				<!-- Mobile compact list -->
				<div v-if="isMobile" class="mobile-ticket-list">
					<div
						v-for="item in filteredTickets"
						:key="item.id"
						class="mobile-ticket-row"
						:class="{ 'ticket-row--mention': hasUnseenMentions(item) }"
						@click="openTicket(item)"
					>
						<div
							class="mobile-ticket-priority"
							:style="{ backgroundColor: priorityHex(item.priority) }"
						/>
						<div class="mobile-ticket-body">
							<div class="d-flex align-center">
								<v-icon
									v-if="hasUnseenMentions(item)"
									x-small
									color="amber darken-2"
									class="unread-dot"
								>
									mdi-circle
								</v-icon>
								<v-icon
									v-if="hasUnseenMessages(item)"
									x-small
									color="primary"
									class="unread-dot"
								>
									mdi-circle
								</v-icon>
								<v-icon
									v-if="hasUnreadFor(item.id)"
									x-small
									color="red"
									class="unread-dot unread-dot--unread"
								>
									mdi-circle
								</v-icon>
								<span class="mobile-ticket-title text-truncate">{{ item.title }}</span>
								<v-avatar
									v-for="(v, vIdx) in liveViewersForTicket(item)"
									:key="`mlive-${item.id}-${v.userId}`"
									size="16"
									:color="userColor(v.userId)"
									class="live-viewer-avatar flex-shrink-0"
									:class="vIdx === 0 ? 'ml-3' : 'ml-1'"
									:style="{ '--live-color': userColor(v.userId) }"
									:title="$t('ticketing.viewing-now', { name: v.name || v.userId })"
								>
									<span class="white--text live-viewer-avatar__initials">{{ viewerInitials(v.name) }}</span>
								</v-avatar>
								<span class="text-caption ml-2 flex-shrink-0 mobile-ticket-status">
									{{ $t(`ticketing.status.${item.status}`) }}
								</span>
							</div>
							<div class="mobile-ticket-meta text-caption grey--text text--darken-1 d-flex align-center">
								<span class="text-truncate">
									<span>{{ $t(`ticketing.type.${item.type}`) }}</span>
									<span v-if="item.organizationName"> · {{ item.organizationName }}</span>
									<span
										v-if="extraOrgCount(item) > 0"
										class="primary--text font-weight-medium ml-1"
										:title="extraOrgNames(item).join(', ')"
									> +{{ extraOrgCount(item) }}</span>
								</span>
								<v-spacer />
								<span v-cursor-tip="formatTimeAbsolute(item.updatedAt)" class="flex-shrink-0 ml-2">{{ formatTime(item.updatedAt) }}</span>
							</div>
						</div>
					</div>
					<div v-if="!filteredTickets.length" class="text-caption grey--text text-center pa-4">
						{{ $t('ticketing.no-tickets') }}
					</div>
				</div>
			</template>

			<!-- Quick add / Edit Ticket Dialog -->
			<v-dialog
				v-model="formDialog"
				:max-width="isMobile ? undefined : 640"
				:fullscreen="isMobile"
				persistent
			>
				<v-card>
					<v-card-title>
						{{ editingId ? $t('ticketing.edit-ticket') : $t('ticketing.new-ticket') }}
						<v-spacer />
						<v-btn icon @click="closeForm">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-divider />
					<v-card-text class="pt-4">
						<v-form ref="form" v-model="formValid">
							<v-text-field
								v-model="form.title"
								:label="$t('ticketing.title')"
								outlined
								dense
								:rules="[v => !!v || $t('required')]"
							/>
							<v-textarea
								v-model="form.description"
								:label="$t('ticketing.description')"
								rows="3"
								outlined
								dense
							/>

							<div class="mb-3">
								<input
									ref="formFileInput"
									type="file"
									multiple
									style="display: none"
									@change="onFormFilesPicked"
								>
								<v-btn
									small
									outlined
									:loading="formAttachmentsUploading"
									@click="$refs.formFileInput.click()"
								>
									<v-icon left small>mdi-paperclip</v-icon>
									{{ $t('ticketing.attach') }}
								</v-btn>
								<div v-if="form.attachments && form.attachments.length" class="mt-2 d-flex flex-wrap" style="gap: 6px">
									<v-chip
										v-for="(att, idx) in form.attachments"
										:key="att.path || idx"
										small
										close
										@click:close="removeFormAttachment(idx)"
									>
										<v-icon left small>{{ attachmentIcon(att) }}</v-icon>
										<span class="text-truncate" style="max-width: 200px">{{ att.name }}</span>
									</v-chip>
								</div>
							</div>
							<v-row dense>
								<v-col cols="12" sm="6">
									<v-autocomplete
										v-model="form.organizationId"
										:items="organizationItems"
										item-text="text"
										item-value="value"
										:filter="filterOrganization"
										:label="$t('ticketing.organization')"
										outlined
										dense
										:rules="[v => !!v || $t('required')]"
										@change="onOrgChange"
									>
										<template #item="{ item }">
											<v-list-item-content>
												<v-list-item-title>{{ item.text }}</v-list-item-title>
												<v-list-item-subtitle class="text-caption grey--text">
													{{ item.value }}
												</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</v-autocomplete>
								</v-col>
								<v-col cols="12" sm="6">
									<v-autocomplete
										v-model="form.assignedUserId"
										:items="orgUsers"
										item-text="fullName"
										item-value="id"
										:filter="filterUser"
										:label="$t('ticketing.assigned-user')"
										outlined
										dense
										:loading="orgUsersLoading"
										:no-data-text="form.organizationId ? $t('ticketing.no-users') : $t('ticketing.pick-org-first')"
										clearable
									>
										<template #item="{ item }">
											<v-list-item-content>
												<v-list-item-title>{{ item.fullName }}</v-list-item-title>
												<v-list-item-subtitle v-if="item.email" class="text-caption grey--text">
													{{ item.email }}
												</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</v-autocomplete>
								</v-col>
								<v-col cols="12">
									<div class="additional-orgs-section pa-3 mb-2">
										<div class="d-flex align-center mb-2">
											<v-icon small color="primary" class="mr-2">
												mdi-account-group-outline
											</v-icon>
											<span class="text-caption font-weight-medium primary--text">
												{{ $t('ticketing.also-affected-organizations') }}
											</span>
											<v-chip
												v-if="form.additionalOrgs.length"
												x-small
												class="ml-2"
												color="primary"
												outlined
											>
												+{{ form.additionalOrgs.length }}
											</v-chip>
											<v-spacer />
										</div>
										<div
											v-for="entry in form.additionalOrgs"
											:key="entry.id"
											class="additional-org-row d-flex align-center mb-2"
										>
											<v-avatar size="24" class="mr-2">
												<v-img
													v-if="organizationLogo(entry.id)"
													:src="organizationLogo(entry.id)"
												/>
												<v-icon v-else small>
													mdi-domain
												</v-icon>
											</v-avatar>
											<div class="mr-3 text-truncate" style="min-width: 140px; max-width: 220px;">
												<span class="text-body-2 font-weight-medium">{{ entry.name }}</span>
											</div>
											<v-autocomplete
												:value="entry.assignedUserId"
												:items="entry.users || []"
												item-text="fullName"
												item-value="id"
												:filter="filterUser"
												:label="$t('ticketing.contact')"
												outlined
												dense
												hide-details
												:loading="entry.usersLoading"
												:no-data-text="$t('ticketing.no-users')"
												clearable
												class="flex-grow-1"
												@change="(uid) => onAdditionalOrgUserChange(entry, uid)"
											>
												<template #item="{ item }">
													<v-list-item-content>
														<v-list-item-title>{{ item.fullName }}</v-list-item-title>
														<v-list-item-subtitle v-if="item.email" class="text-caption grey--text">
															{{ item.email }}
														</v-list-item-subtitle>
													</v-list-item-content>
												</template>
											</v-autocomplete>
											<v-btn
												icon
												small
												class="ml-1"
												:title="$t('ticketing.remove-organization')"
												@click="removeAdditionalOrg(entry.id)"
											>
												<v-icon small>
													mdi-close
												</v-icon>
											</v-btn>
										</div>
										<v-autocomplete
											v-model="additionalOrgPick"
											:items="additionalOrgPickerItems"
											item-text="text"
											item-value="value"
											:filter="filterOrganization"
											:label="$t('ticketing.add-organization')"
											prepend-inner-icon="mdi-plus"
											outlined
											dense
											hide-details
											clearable
											@change="addAdditionalOrgFromPicker"
										>
											<template #item="{ item }">
												<v-list-item-content>
													<v-list-item-title>{{ item.text }}</v-list-item-title>
													<v-list-item-subtitle class="text-caption grey--text">
														{{ item.value }}
													</v-list-item-subtitle>
												</v-list-item-content>
											</template>
										</v-autocomplete>
									</div>
								</v-col>
								<v-col cols="12" sm="6">
									<v-select
										v-model="form.type"
										:items="typeOptions"
										:label="$t('ticketing.type-label')"
										outlined
										dense
										:rules="[v => !!v || $t('required')]"
									/>
								</v-col>
								<v-col cols="12" sm="6">
									<v-select
										v-model="form.priority"
										:items="priorityOptions"
										:label="$t('ticketing.priority-label')"
										outlined
										dense
										:rules="[v => !!v || $t('required')]"
									/>
								</v-col>
								<v-col cols="12" sm="6">
									<v-select
										v-model="form.status"
										:items="statusOptions"
										:label="$t('ticketing.status-label')"
										outlined
										dense
										:rules="[v => !!v || $t('required')]"
									/>
								</v-col>
								<v-col cols="12" sm="6">
									<v-autocomplete
										v-model="form.assignedDevopsIds"
										:items="devopsUsers"
										item-text="fullName"
										item-value="id"
										:label="$t('ticketing.assigned-devops')"
										outlined
										dense
										multiple
										small-chips
										deletable-chips
									/>
								</v-col>
							</v-row>
						</v-form>
					</v-card-text>
					<v-divider />
					<v-card-actions>
						<v-spacer />
						<v-btn text @click="closeForm">
							{{ $t('cancel') }}
						</v-btn>
						<v-btn color="primary" :loading="formSaving" :disabled="!formValid" @click="submitForm">
							{{ editingId ? $t('save') : $t('create') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Ticket Detail Dialog -->
			<v-dialog
				v-model="detailDialog"
				fullscreen
				scrollable
				persistent
				no-click-animation
				transition="dialog-bottom-transition"
				content-class="dialog-ticket-detail"
				@input="onDetailDialogInput"
			>
				<v-card v-if="activeTicket" class="d-flex flex-column ticket-detail-card" style="height: 100vh">
					<v-btn
						icon
						class="ticket-detail-close"
						@click="closeDetail"
					>
						<v-icon>mdi-close</v-icon>
					</v-btn>
					<v-card-title class="d-flex align-center flex-shrink-0 flex-wrap ticket-detail-title" style="gap: 6px">
						<span class="text-truncate">{{ activeTicket.title }}</span>
						<div class="ticket-detail-nav-break" />
						<v-chip x-small class="ticket-detail-type-chip">
							{{ $t(`ticketing.type.${activeTicket.type}`) }}
						</v-chip>
						<v-spacer />
						<div v-if="navTicketIds.length" class="d-flex align-center ticket-detail-nav flex-shrink-0" style="gap: 2px">
							<v-chip
								v-if="currentNavFilterLabel"
								x-small
								outlined
								class="mr-1"
								:title="$t('ticketing.iterating-filter', { filter: currentNavFilterLabel })"
							>
								{{ currentNavFilterLabel }}
							</v-chip>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										:disabled="!hasPrevTicket"
										v-bind="attrs"
										v-on="on"
										@click="goToTicketDelta(-1)"
									>
										<v-icon>mdi-chevron-left</v-icon>
									</v-btn>
								</template>
								<span>{{ $t('ticketing.prev-ticket') }}</span>
							</v-tooltip>
							<span class="text-caption grey--text text--darken-1 ticket-detail-nav__count">
								{{ navIndex >= 0 ? navIndex + 1 : '–' }} / {{ navTicketIds.length }}
							</span>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										:disabled="!hasNextTicket"
										v-bind="attrs"
										v-on="on"
										@click="goToTicketDelta(1)"
									>
										<v-icon>mdi-chevron-right</v-icon>
									</v-btn>
								</template>
								<span>{{ $t('ticketing.next-ticket') }}</span>
							</v-tooltip>
						</div>
						<v-tooltip
							v-for="(v, vIdx) in activeViewers"
							:key="`headlive-${v.userId}`"
							bottom
						>
							<template #activator="{ on, attrs }">
								<v-avatar
									size="24"
									:color="userColor(v.userId)"
									class="live-viewer-avatar live-viewer-avatar--lg flex-shrink-0"
									:class="vIdx === 0 ? 'ml-2' : 'ml-1'"
									:style="{ '--live-color': userColor(v.userId) }"
									v-bind="attrs"
									v-on="on"
								>
									<span class="white--text text-caption">{{ viewerInitials(v.name) }}</span>
								</v-avatar>
							</template>
							<span>{{ $t('ticketing.viewing-now', { name: v.name || v.userId }) }}</span>
						</v-tooltip>
						<div class="ticket-status-break" />
						<div class="ticket-status-actions d-flex align-center" style="gap: 6px">
							<v-btn
								v-if="isTicketClosed"
								small
								outlined
								:loading="closeBtnLoading"
								color="success"
								class="ticket-close-btn"
								@click="setTicketStatus('open')"
							>
								<v-icon small left>mdi-restore</v-icon>
								{{ $t('ticketing.reopen-ticket') }}
							</v-btn>
							<template v-else>
								<v-btn
									small
									outlined
									:loading="closeBtnLoading === 'resolved'"
									:disabled="!!closeBtnLoading"
									color="green darken-2"
									class="ticket-close-btn"
									@click="setTicketStatus('resolved')"
								>
									<v-icon small left>mdi-check-circle-outline</v-icon>
									{{ $t('ticketing.resolve-ticket') }}
								</v-btn>
								<v-btn
									small
									outlined
									:loading="closeBtnLoading === 'closed'"
									:disabled="!!closeBtnLoading"
									color="grey darken-2"
									class="ticket-close-btn"
									@click="setTicketStatus('closed')"
								>
									<v-icon small left>mdi-close-circle-outline</v-icon>
									{{ $t('ticketing.close-ticket') }}
								</v-btn>
							</template>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn icon v-bind="attrs" v-on="on" @click="shareTicket(activeTicket)">
										<v-icon small>mdi-share-variant</v-icon>
									</v-btn>
								</template>
								<span>{{ $t('ticketing.share-ticket') }}</span>
							</v-tooltip>
							<v-btn icon @click="openQuickAdd(activeTicket)">
								<v-icon small>mdi-pencil</v-icon>
							</v-btn>
						</div>
					</v-card-title>
					<v-divider />
					<v-card-text class="px-3 ticket-detail-card-text" style="flex: 1 1 auto; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch;">
						<div class="ticket-detail-grid">
							<div class="td-left">
								<div class="td-tabs">
									<v-tabs v-model="detailTab" class="ticket-tabs mb-2" grow show-arrows>
										<v-tab href="#description">
											<v-icon small left>mdi-text-box-outline</v-icon>
											{{ $t('ticketing.description') }}
											<span v-if="activeTicket.attachments && activeTicket.attachments.length" class="text-caption ml-1">({{ activeTicket.attachments.length }})</span>
										</v-tab>
										<v-tab href="#conversation">
											<v-icon small left>mdi-message-text-outline</v-icon>
											{{ $t('ticketing.conversation') }}
											<span v-if="messages.length" class="text-caption ml-1">({{ messages.length }})</span>
											<v-icon
												v-if="hasUnseenMentions(activeTicket)"
												x-small
												color="amber darken-2"
												class="unread-dot ml-1"
											>
												mdi-circle
											</v-icon>
											<v-icon
												v-if="hasUnseenMessages(activeTicket)"
												x-small
												color="primary"
												class="unread-dot"
											>
												mdi-circle
											</v-icon>
										</v-tab>
										<v-tab href="#activity">
											<v-icon small left>mdi-history</v-icon>
											{{ $t('ticketing.activity') }}
											<span v-if="activity.length" class="text-caption ml-1">({{ activity.length }})</span>
										</v-tab>
									</v-tabs>
									<v-tabs-items v-model="detailTab" class="ticket-tabs-items">
										<v-tab-item value="description">
											<div class="field-block">
												<!-- eslint-disable vue/max-attributes-per-line, vue/html-indent, vue/singleline-html-element-content-newline -->
											<div v-if="activeTicket.description" class="field-value text-body-2" style="white-space: pre-wrap">{{ activeTicket.description }}</div>
											<!-- eslint-enable vue/max-attributes-per-line, vue/html-indent, vue/singleline-html-element-content-newline -->
												<div v-else class="field-value field-value--empty text-body-2">
													{{ $t('ticketing.no-description') }}
												</div>
											</div>

											<div v-if="activeTicket.attachments && activeTicket.attachments.length" class="field-block">
												<div class="field-label">
													{{ $t('ticketing.attachments') }}
												</div>
												<div class="attachment-grid">
													<template v-for="(att, idx) in activeTicket.attachments">
														<a
															v-if="isImage(att)"
															:key="'timg-' + idx"
															href="#"
															class="attachment-image-link"
															@click.prevent="openImagePreview(activeTicket.attachments, idx)"
														>
															<img :src="att.url" :alt="att.name" class="attachment-image">
														</a>
														<a
															v-else
															:key="'tfile-' + idx"
															:href="att.url"
															target="_blank"
															rel="noopener"
															class="attachment-file attachment-file--tile"
														>
															<v-icon class="attachment-file__icon-large mb-2">{{ attachmentIcon(att) }}</v-icon>
															<span class="attachment-file__name" :title="att.name">
																<span class="attachment-file__name-base">{{ attachmentBaseName(att.name) }}</span><span class="attachment-file__name-ext">{{ attachmentExtension(att.name) }}</span>
															</span>
															<span v-if="att.size" class="attachment-file__size text-caption grey--text">({{ formatSize(att.size) }})</span>
														</a>
													</template>
												</div>
											</div>

											<div class="field-block todos-block">
												<div class="field-label">
													{{ $t('ticketing.todos') }}
												</div>
												<div v-if="!todos.length" class="text-caption grey--text mb-2">
													{{ $t('ticketing.no-todos') }}
												</div>
												<div
													v-for="todo in todos"
													:key="todo.id"
													class="todo-item"
													:class="{ 'todo-item--completed': todo.completed }"
												>
													<v-checkbox
														:input-value="!!todo.completed"
														hide-details
														dense
														class="todo-item__check ma-0 pa-0"
														@change="toggleTodo(todo)"
													/>
													<div class="todo-item__body">
														<template v-if="editingTodoId === todo.id">
															<v-text-field
																v-model="editingTodoText"
																dense
																hide-details
																autofocus
																class="todo-item__edit"
																@keydown.enter.prevent="saveEditTodo(todo)"
																@keydown.esc.prevent="cancelEditTodo"
																@blur="saveEditTodo(todo)"
															/>
														</template>
														<template v-else>
															<div
																class="todo-item__text text-body-2"
																@dblclick="startEditTodo(todo)"
															>
																{{ todo.text }}
															</div>
														</template>
														<div class="todo-item__meta text-caption grey--text">
															<div v-cursor-tip="formatTimeAbsolute(todo.createdAt)">
																{{ $t('ticketing.todo-added-by', { name: todo.createdByName || $t('ticketing.someone'), time: formatTime(todo.createdAt) }) }}
															</div>
															<div v-if="todo.completed" v-cursor-tip="formatTimeAbsolute(todo.completedAt)">
																{{ $t('ticketing.todo-completed-by', { name: todo.completedByName || $t('ticketing.someone'), time: formatTime(todo.completedAt) }) }}
															</div>
														</div>
													</div>
													<div class="todo-item__actions">
														<v-tooltip v-if="!todo.completed && editingTodoId !== todo.id" bottom>
															<template #activator="{ on, attrs }">
																<v-btn
																	icon
																	x-small
																	v-bind="attrs"
																	v-on="on"
																	@click="startEditTodo(todo)"
																>
																	<v-icon x-small>mdi-pencil</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('ticketing.todo-edit') }}</span>
														</v-tooltip>
														<v-tooltip v-if="!todo.completed" bottom>
															<template #activator="{ on, attrs }">
																<v-btn
																	icon
																	x-small
																	v-bind="attrs"
																	v-on="on"
																	@click="removeTodo(todo)"
																>
																	<v-icon x-small>mdi-close</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('ticketing.todo-delete') }}</span>
														</v-tooltip>
													</div>
												</div>
												<div class="todo-add d-flex align-center mt-2">
													<v-text-field
														v-model="newTodoText"
														dense
														hide-details
														:placeholder="$t('ticketing.todo-placeholder')"
														class="todo-add__input"
														@keydown.enter.prevent="submitNewTodo"
													/>
													<v-btn
														icon
														small
														class="ml-2"
														:disabled="!newTodoText.trim()"
														@click="submitNewTodo"
													>
														<v-icon small>mdi-plus</v-icon>
													</v-btn>
												</div>
											</div>
										</v-tab-item>
										<v-tab-item value="conversation">
											<div class="messages-list-wrap">
												<div
													ref="messagesList"
													class="messages-list mb-3"
													@scroll="onMessagesScroll"
													@wheel.passive="releaseUnreadPin"
													@touchmove.passive="releaseUnreadPin"
													@click="onMessagesClick"
												>
													<div v-if="!conversationItems.length" class="text-caption grey--text text-center pa-4">
														{{ $t('ticketing.no-messages') }}
													</div>
													<template v-for="m in conversationItems">
														<div
															v-if="m.kind === 'message' && m.id === firstUnreadMessageId"
															:key="`unread-divider-${m.id}`"
															class="unread-divider"
														>
															<span class="unread-divider__label">{{ $t('ticketing.unread-below') }}</span>
														</div>
														<div
															v-if="m.kind === 'follow-up'"
															:key="`fu-${m.id}`"
															class="message-row message-row--follow-up mb-2"
														>
															<div class="follow-up-bubble">
																<div class="follow-up-bubble__icon">
																	<v-icon small color="white">{{ followUpIcon(m.toValue) }}</v-icon>
																</div>
																<div class="follow-up-bubble__content">
																	<div class="follow-up-bubble__line">
																		<span class="font-weight-medium">{{ m.actorName || $t('ticketing.someone') }}</span>
																		<span class="follow-up-bubble__verb">{{ ' ' + $t('ticketing.activity-follow-up') + ' ' }}</span>
																		<span class="follow-up-bubble__method">{{ $t('ticketing.follow-up-method.' + m.toValue) }}</span>
																	</div>
																	<div v-if="followUpItemContactLabel(m) || m.organizationName" class="follow-up-bubble__meta">
																		<v-icon x-small class="mr-1">mdi-account-outline</v-icon>
																		<span v-if="followUpItemContactLabel(m)" class="text-truncate">{{ followUpItemContactLabel(m) }}</span>
																		<template v-if="m.organizationName">
																			<v-icon x-small class="mx-1">mdi-domain</v-icon>
																			<span class="text-truncate">{{ m.organizationName }}</span>
																		</template>
																	</div>
																</div>
																<span v-cursor-tip="formatTimeAbsolute(m.createdAt)" class="follow-up-bubble__time text-caption">{{ formatTime(m.createdAt) }}</span>
															</div>
															<div :key="`furx-${m.id}-${reactionList(m).length}`" class="reactions-row reactions-row--center">
																<v-tooltip
																	v-for="r in reactionList(m)"
																	:key="`fur-${r.emoji}`"
																	top
																>
																	<template #activator="{ on, attrs }">
																		<button
																			type="button"
																			class="reaction-chip"
																			:class="{ 'reaction-chip--mine': r.mine }"
																			v-bind="attrs"
																			v-on="on"
																			@click="onToggleReaction(m, r.emoji)"
																		>
																			<span class="reaction-chip__emoji">{{ r.emoji }}</span>
																			<span class="reaction-chip__count">{{ r.count }}</span>
																		</button>
																	</template>
																	<span style="white-space: pre-line">{{ r.names.join('\n') }}</span>
																</v-tooltip>
																<v-menu :key="`fu-add-${m.id}`" offset-y :close-on-content-click="true">
																	<template #activator="{ on, attrs }">
																		<button
																			type="button"
																			class="reaction-chip reaction-chip--add reaction-chip--picker"
																			v-bind="attrs"
																			v-on="on"
																		>
																			<v-icon>mdi-emoticon-plus-outline</v-icon>
																		</button>
																	</template>
																	<div class="emoji-picker">
																		<button
																			v-for="e in emojiPalette"
																			:key="e"
																			type="button"
																			class="emoji-picker__item"
																			@click="onToggleReaction(m, e)"
																		>
																			{{ e }}
																		</button>
																	</div>
																</v-menu>
															</div>
														</div>
														<div
															v-else
															:key="`msg-${m.id}`"
															class="message-row mb-2"
															:class="{ 'message-row--mine': m.authorId === currentUser.id }"
														>
															<div v-if="m.authorId !== currentUser.id" class="message-meta-other">
																<span class="font-weight-medium text-body-2">{{ m.authorName }}</span>
																<span v-cursor-tip="formatTimeAbsolute(m.createdAt)" class="text-caption grey--text ml-2">{{ formatTime(m.createdAt) }}</span>
															</div>
															<v-avatar
																v-if="m.authorId !== currentUser.id"
																size="32"
																:color="userColor(m.authorId)"
																class="message-avatar"
															>
																<span class="white--text text-caption">{{ initials(m.authorName) }}</span>
															</v-avatar>
															<div class="message-column">
																<div v-if="m.authorId === currentUser.id" class="message-meta-mine">
																	<span v-cursor-tip="formatTimeAbsolute(m.createdAt)" class="text-caption grey--text">{{ formatTime(m.createdAt) }}</span>
																</div>
																<div class="bubble-row">
																	<div class="message-item" :class="{ 'message-item--mine': m.authorId === currentUser.id }">
																		<!-- eslint-disable-next-line vue/no-v-html -->
																		<div class="text-body-2 message-body" v-html="renderMessage(m.body)" />
																		<div v-if="m.attachments && m.attachments.length" class="attachment-grid mt-2">
																			<template v-for="(att, idx) in m.attachments">
																				<a
																					v-if="isImage(att)"
																					:key="'img-' + idx"
																					href="#"
																					class="attachment-image-link"
																					@click.prevent="openImagePreview(m.attachments, idx)"
																				>
																					<img :src="att.url" :alt="att.name" class="attachment-image" @load="onMessageImageLoad">
																				</a>
																				<a
																					v-else
																					:key="'file-' + idx"
																					:href="att.url"
																					target="_blank"
																					rel="noopener"
																					class="attachment-file"
																				>
																					<v-icon small class="attachment-file__icon mr-1">{{ attachmentIcon(att) }}</v-icon>
																					<span class="attachment-file__name">{{ att.name }}</span>
																					<span v-if="att.size" class="attachment-file__size text-caption grey--text ml-1">({{ formatSize(att.size) }})</span>
																				</a>
																			</template>
																		</div>
																	</div>
																	<div
																		v-if="readersByMessageId[m.id] && readersByMessageId[m.id].length"
																		class="read-receipts"
																	>
																		<v-tooltip
																			v-for="r in readersByMessageId[m.id]"
																			:key="`rd-${m.id}-${r.userId}`"
																			top
																		>
																			<template #activator="{ on, attrs }">
																				<v-avatar
																					size="16"
																					:color="userColor(r.userId)"
																					class="read-receipt-avatar"
																					v-bind="attrs"
																					v-on="on"
																				>
																					<span class="white--text read-receipt-avatar__initials">{{ viewerInitials(r.name) }}</span>
																				</v-avatar>
																			</template>
																			<span>{{ $t('ticketing.read-by', { name: r.name, time: formatTimeAbsolute(r.seenMs) }) }}</span>
																		</v-tooltip>
																	</div>
																</div>										<div :key="`rx-${m.id}-${reactionList(m).length}`" class="reactions-row">
																	<template v-for="r in reactionList(m)">
																		<v-tooltip :key="`r-${r.emoji}`" top>
																			<template #activator="{ on, attrs }">
																				<button
																					type="button"
																					class="reaction-chip"
																					:class="{ 'reaction-chip--mine': r.mine }"
																					v-bind="attrs"
																					v-on="on"
																					@click="onToggleReaction(m, r.emoji)"
																				>
																					<span class="reaction-chip__emoji">{{ r.emoji }}</span>
																					<span class="reaction-chip__count">{{ r.count }}</span>
																				</button>
																			</template>
																			<span style="white-space: pre-line">{{ r.names.join('\n') }}</span>
																		</v-tooltip>
																	</template>
																	<v-menu key="reaction-add" offset-y :close-on-content-click="true">
																		<template #activator="{ on, attrs }">
																			<button
																				type="button"
																				class="reaction-chip reaction-chip--add reaction-chip--picker"
																				v-bind="attrs"
																				v-on="on"
																			>
																				<v-icon>mdi-emoticon-plus-outline</v-icon>
																			</button>
																		</template>
																		<div class="emoji-picker">
																			<button
																				v-for="e in emojiPalette"
																				:key="e"
																				type="button"
																				class="emoji-picker__item"
																				@click="onToggleReaction(m, e)"
																			>
																				{{ e }}
																			</button>
																		</div>
																	</v-menu>
																</div>
															</div>
														</div>
													</template>
												</div>

												<v-btn
													v-if="unreadMessageCount > 0"
													small
													rounded
													color="primary"
													class="unread-banner elevation-3"
													@click="onUnreadBannerClick"
												>
													<v-icon left small>mdi-arrow-down</v-icon>
													{{ $tc('ticketing.new-messages-banner', unreadMessageCount, { count: unreadMessageCount }) }}
												</v-btn>
											</div>

											<div v-if="typingLabel" class="typing-indicator text-caption grey--text">
												<v-icon x-small class="mr-1">mdi-pencil-outline</v-icon>
												{{ typingLabel }}
											</div>

											<div class="message-composer">
												<v-textarea
													ref="messageInput"
													v-model="newMessage"
													:placeholder="$t('ticketing.message-placeholder')"
													rows="2"
													auto-grow
													outlined
													dense
													hide-details
													@keyup="onMessageKeyup"
													@keydown="onMessageKeydown"
												/>
												<v-menu
													v-model="mentionMenuOpen"
													:position-x="mentionMenuX"
													:position-y="mentionMenuY"
													absolute
													offset-y
													:close-on-content-click="false"
													:close-on-click="false"
												>
													<v-list dense max-height="240" min-width="220">
														<v-list-item
															v-for="(u, idx) in filteredMentionCandidates"
															:key="u.id"
															:input-value="idx === mentionIndex"
															@click="insertMention(u)"
														>
															<v-list-item-avatar size="22" :color="userColor(u.id)">
																<span class="white--text text-caption">{{ initials(u.fullName) }}</span>
															</v-list-item-avatar>
															<v-list-item-title>{{ u.fullName }}</v-list-item-title>
														</v-list-item>
														<v-list-item v-if="!filteredMentionCandidates.length">
															<v-list-item-title class="text-caption grey--text">
																{{ $t('ticketing.no-users') }}
															</v-list-item-title>
														</v-list-item>
													</v-list>
												</v-menu>
												<div v-if="messageAttachments.length" class="mt-2 d-flex flex-wrap" style="gap: 6px">
													<v-chip
														v-for="(att, idx) in messageAttachments"
														:key="att.path || idx"
														small
														close
														@click:close="removeMessageAttachment(idx)"
													>
														<v-icon left small>{{ attachmentIcon(att) }}</v-icon>
														<span class="text-truncate" style="max-width: 200px">{{ att.name }}</span>
													</v-chip>
												</div>
												<div class="d-flex align-center mt-2">
													<input
														ref="messageFileInput"
														type="file"
														multiple
														style="display: none"
														@change="onMessageFilesPicked"
													>
													<v-btn
														icon
														small
														:loading="messageAttachmentsUploading"
														:title="$t('ticketing.attach')"
														@click="$refs.messageFileInput.click()"
													>
														<v-icon small>mdi-paperclip</v-icon>
													</v-btn>
													<span class="text-caption grey--text ml-2">{{ $t('ticketing.mention-hint') }}</span>
													<v-spacer />
													<v-btn
														small
														color="primary"
														:loading="sendingMessage"
														:disabled="!newMessage.trim() && !messageAttachments.length"
														@click="sendMessage"
													>
														<v-icon left small>mdi-send</v-icon>
														{{ $t('ticketing.send') }}
													</v-btn>
												</div>
											</div>
										</v-tab-item>
										<v-tab-item value="activity">
											<div ref="activityList" class="activity-list">
												<div v-if="!activity.length" class="text-caption grey--text text-center pa-3">
													{{ $t('ticketing.no-activity') }}
												</div>
												<div
													v-for="a in activity"
													:key="a.id"
													class="activity-item"
												>
													<v-icon x-small class="activity-dot" :color="activityIconColor(a)">
														{{ activityIcon(a) }}
													</v-icon>
													<div class="activity-body">
														<div class="text-body-2">
															<span class="font-weight-medium">{{ a.actorName || $t('ticketing.someone') }}</span>
															<span v-if="a.type === 'created'">
																{{ ' ' + $t('ticketing.activity-created') }}
															</span>
															<span v-else-if="a.type === 'visited'" class="grey--text text--darken-1">
																{{ ' ' + $t('ticketing.activity-visited') }}
															</span>
															<span v-else-if="a.type === 'follow-up'">
																{{ ' ' + $t('ticketing.activity-follow-up') }}
																<span class="activity-value">{{ $t('ticketing.follow-up-method.' + a.toValue) }}</span>
																<span v-if="a.contactName" class="grey--text text--darken-1">
																	— {{ a.contactName }}
																</span>
																<span v-if="a.organizationName" class="grey--text text--darken-1">
																	— {{ a.organizationName }}
																</span>
															</span>
															<span v-else-if="a.type === 'attachments-added'">
																{{ ' ' + $t('ticketing.activity-attachments-added') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'attachments-removed'">
																{{ ' ' + $t('ticketing.activity-attachments-removed') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'watcher-added'">
																{{ ' ' + $t('ticketing.activity-watcher-added') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'watcher-removed'">
																{{ ' ' + $t('ticketing.activity-watcher-removed') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'todo-added'">
																{{ ' ' + $t('ticketing.activity-todo-added') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'todo-edited'">
																{{ ' ' + $t('ticketing.activity-todo-edited') }}
																<span v-if="a.fromValue != null && a.fromValue !== ''" class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-from') }}
																</span>
																<span v-if="a.fromValue != null && a.fromValue !== ''" class="activity-value">{{ a.fromValue }}</span>
																<span class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-to') }}
																</span>
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'todo-completed'">
																{{ ' ' + $t('ticketing.activity-todo-completed') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'todo-uncompleted'">
																{{ ' ' + $t('ticketing.activity-todo-uncompleted') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'todo-deleted'">
																{{ ' ' + $t('ticketing.activity-todo-deleted') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'org-added'">
																{{ ' ' + $t('ticketing.activity-org-added') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'org-removed'">
																{{ ' ' + $t('ticketing.activity-org-removed') }}
																<span class="activity-value">{{ a.toValue }}</span>
															</span>
															<span v-else-if="a.type === 'org-contact-updated'">
																{{ ' ' + $t('ticketing.activity-org-contact-updated') }}
																<span v-if="a.fromValue" class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-from') }}
																</span>
																<span v-if="a.fromValue" class="activity-value">{{ a.fromValue }}</span>
																<span class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-to') }}
																</span>
																<span class="activity-value">{{ a.toValue || '—' }}</span>
															</span>
															<span v-else>
																{{ ' ' + $t('ticketing.activity-changed', { field: $t('ticketing.field.' + a.field) }) }}
																<span v-if="a.fromValue != null && a.fromValue !== ''" class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-from') }}
																</span>
																<span v-if="a.fromValue != null && a.fromValue !== ''" class="activity-value">{{ a.fromValue }}</span>
																<span class="grey--text text--darken-1">
																	{{ ' ' + $t('ticketing.activity-to') }}
																</span>
																<span class="activity-value">{{ a.toValue || '—' }}</span>
															</span>
														</div>
														<div v-cursor-tip="formatTimeAbsolute(a.createdAt)" class="text-caption grey--text">
															{{ formatTime(a.createdAt) }}
														</div>
													</div>
												</div>
											</div>
										</v-tab-item>
									</v-tabs-items>
								</div>
							</div>

							<div class="td-side">
								<v-card outlined class="pa-3">
									<div class="field-block">
										<div class="d-flex align-center">
											<v-icon small class="mr-1 primary--text">mdi-domain</v-icon>
											<span class="field-label mb-0">{{ $t('ticketing.affected-organizations') }}</span>
											<v-chip x-small class="ml-2" color="primary" outlined>
												{{ activeTicketOrganizations.length }}
											</v-chip>
										</div>
										<div class="mt-2">
											<div :class="['affected-orgs-list', { 'is-expanded': expandedAffectedOrgId }]">
												<div
													v-for="o in visibleAffectedOrganizations"
													:key="o.id || o.name"
													class="affected-org-card mb-2"
												>
													<div
														class="affected-org-row d-flex align-center"
														@click="toggleAffectedOrgExpand(o.id)"
													>
														<v-icon small class="mr-1 grey--text">
															{{ expandedAffectedOrgs[o.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
														</v-icon>
														<v-avatar size="28" class="mr-2 org-logo-avatar">
															<v-img v-if="organizationLogo(o.id)" :src="organizationLogo(o.id)" />
															<v-icon v-else small color="grey">mdi-domain</v-icon>
														</v-avatar>
														<div class="flex-grow-1" style="min-width: 0;">
															<div class="d-flex align-center" style="min-width: 0;">
																<span class="text-body-2 text-truncate font-weight-medium">{{ o.name }}</span>
																<v-chip
																	v-if="o.id === activeTicket.organizationId"
																	x-small
																	class="ml-2 flex-shrink-0"
																	color="primary"
																	outlined
																>
																	{{ $t('ticketing.primary-organization') }}
																</v-chip>
															</div>
															<div class="text-caption d-flex align-center" :class="{ 'red--text font-weight-medium': !o.assignedUserName }" style="min-width: 0;">
																<v-icon x-small class="mr-1 flex-shrink-0" :color="!o.assignedUserName ? 'red' : undefined">mdi-account-outline</v-icon>
																<span class="text-truncate">{{ o.assignedUserName || $t('ticketing.no-contact') }}</span>
																<template v-if="o.assignedUserName && affectedOrgUserRole(o)">
																	<span class="grey--text mx-1 flex-shrink-0">·</span>
																	<span class="flex-shrink-0" :class="userRoleColor(affectedOrgUserRole(o))" style="font-size: 0.7rem;">{{ affectedOrgUserRole(o) }}</span>
																</template>
															</div>
															<div v-if="o.id" class="text-caption grey--text d-flex align-center" style="min-width: 0;">
																<v-icon x-small class="mr-1 flex-shrink-0">mdi-identifier</v-icon>
																<span class="text-truncate affected-org-id" @click.stop="copyText(o.id)">{{ o.id }}</span>
																<v-btn
																	icon
																	x-small
																	class="ml-1 flex-shrink-0"
																	:title="$t('copy')"
																	@click.stop="copyText(o.id)"
																>
																	<v-icon x-small>mdi-content-copy</v-icon>
																</v-btn>
															</div>
														</div>
														<v-menu offset-y :close-on-content-click="true" max-height="420">
															<template #activator="{ on, attrs }">
																<v-tooltip top>
																	<template #activator="{ on: tOn, attrs: tAttrs }">
																		<v-btn
																			icon
																			x-small
																			:loading="loggingFollowUpOrgId === (o.id || '_')"
																			v-bind="{ ...attrs, ...tAttrs }"
																			v-on="{ ...on, ...tOn }"
																			@click.stop
																		>
																			<v-icon x-small :color="!activeTicketOrgFollowUp(o.id) ? 'red' : undefined">mdi-phone-log-outline</v-icon>
																		</v-btn>
																	</template>
																	<span>{{ $t('ticketing.log-follow-up') }}</span>
																</v-tooltip>
															</template>
															<v-list dense class="follow-up-contacts-menu">
																<v-subheader class="text-caption">
																	{{ $t('ticketing.follow-up-with') }}
																</v-subheader>
																<v-list-item
																	v-for="(c, ci) in orgFollowUpContacts(o)"
																	:key="`${o.id || '_'}-c-${ci}`"
																	class="follow-up-contact-item"
																	:class="{ 'follow-up-contact-item--missing': !c.name }"
																>
																	<v-list-item-avatar
																		size="28"
																		:color="c.name ? userColor(c.name) : 'grey lighten-2'"
																		class="white--text text-caption mr-2"
																	>
																		<template v-if="c.name">
																			{{ viewerInitials(c.name) }}
																		</template>
																		<v-icon v-else x-small color="grey darken-1">mdi-account-off-outline</v-icon>
																	</v-list-item-avatar>
																	<v-list-item-content>
																		<template v-if="c.name">
																			<v-list-item-title class="text-body-2">
																				{{ c.name }}
																			</v-list-item-title>
																			<v-list-item-subtitle class="text-caption">
																				{{ c.kindLabel }}
																			</v-list-item-subtitle>
																		</template>
																		<template v-else>
																			<v-list-item-title class="text-body-2 grey--text text--darken-1">
																				{{ c.kindLabel }}
																			</v-list-item-title>
																			<v-list-item-subtitle class="text-caption grey--text">
																				<a
																					href="#"
																					class="primary--text follow-up-contact-link"
																					@click.prevent="expandAffectedOrgFromMenu(o.id, c.kind)"
																				>
																					{{ $t('ticketing.follow-up-contact-missing.' + c.kind) }}
																				</a>
																			</v-list-item-subtitle>
																		</template>
																	</v-list-item-content>
																	<v-list-item-action class="ma-0">
																		<div class="d-flex align-center" style="gap: 2px">
																			<v-tooltip
																				v-for="m in followUpMethods"
																				:key="m.value"
																				top
																				:disabled="!c.name"
																			>
																				<template #activator="{ on: mOn, attrs: mAttrs }">
																					<v-btn
																						icon
																						x-small
																						:disabled="!c.name"
																						v-bind="mAttrs"
																						v-on="mOn"
																						@click.stop="logFollowUpClick(m.value, o, c)"
																					>
																						<v-icon small>{{ m.icon }}</v-icon>
																					</v-btn>
																				</template>
																				<span>{{ m.text }}</span>
																			</v-tooltip>
																		</div>
																	</v-list-item-action>
																</v-list-item>
															</v-list>
														</v-menu>
														<v-tooltip top>
															<template #activator="{ on, attrs }">
																<v-btn
																	icon
																	x-small
																	v-bind="attrs"
																	v-on="on"
																	@click.stop="openOrgTicketsFor(o.id)"
																>
																	<v-icon x-small>mdi-format-list-bulleted</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('ticketing.org-tickets') }}</span>
														</v-tooltip>
														<v-tooltip top>
															<template #activator="{ on, attrs }">
																<v-btn
																	icon
																	x-small
																	v-bind="attrs"
																	v-on="on"
																	@click.stop="impersonateOrganization(o.id)"
																>
																	<v-icon x-small>mdi-login-variant</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('impersonate') }}</span>
														</v-tooltip>
														<v-tooltip
															v-if="o.id !== activeTicket.organizationId || activeTicketOrganizations.length > 1"
															top
														>
															<template #activator="{ on, attrs }">
																<v-btn
																	icon
																	x-small
																	v-bind="attrs"
																	v-on="on"
																	@click.stop="removeAffectedOrg(o.id)"
																>
																	<v-icon x-small>mdi-close</v-icon>
																</v-btn>
															</template>
															<span>{{ $t('ticketing.remove-organization') }}</span>
														</v-tooltip>
													</div>
													<div class="affected-org-follow-up pb-1" style="padding-left: 56px; padding-right: 8px;">
														<div
															class="text-caption"
															style="width: 100%; display: flex; align-items: center;"
															:class="activeTicketOrgFollowUp(o.id) && !isFollowUpStale(activeTicketOrgFollowUp(o.id).at) ? 'primary--text' : 'red--text font-weight-medium'"
														>
															<v-icon
																x-small
																class="mr-1 flex-shrink-0"
																:color="activeTicketOrgFollowUp(o.id) && !isFollowUpStale(activeTicketOrgFollowUp(o.id).at) ? 'primary' : 'red'"
															>
																mdi-phone-log-outline
															</v-icon>
															<div v-if="activeTicketOrgFollowUp(o.id)" v-cursor-tip="followUpTooltip(activeTicketOrgFollowUp(o.id))" style="flex: 1 1 0; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
																<span v-if="followUpContactLabel(activeTicketOrgFollowUp(o.id))">· {{ followUpContactLabel(activeTicketOrgFollowUp(o.id)) }} </span>· {{ $t('ticketing.follow-up-method.' + activeTicketOrgFollowUp(o.id).method) }} · {{ formatTime(activeTicketOrgFollowUp(o.id).at) }}<span v-if="activeTicketOrgFollowUp(o.id).byUserName"> · {{ $t('ticketing.follow-up-by', { name: activeTicketOrgFollowUp(o.id).byUserName }) }}</span>
															</div>
															<span v-else style="flex: 1 1 0; min-width: 0;">{{ $t('ticketing.never-follow-up') }}</span>
															<v-btn
																v-if="activeTicketOrgFollowUp(o.id) && previousFollowUpsForOrg(o.id).length"
																text
																x-small
																class="px-1 text-caption text-none follow-up-toggle-btn"
																style="flex-shrink: 0; margin-left: 4px;"
																@click.stop="togglePrevFollowUps(o.id, $event)"
															>
																{{ expandedPrevFollowUpsByOrg[o.id || '_'] ? $t('ticketing.show-less') : $t('ticketing.show-more') }}
															</v-btn>
														</div>
														<div
															v-if="activeTicketOrgFollowUp(o.id) && expandedPrevFollowUpsByOrg[o.id || '_'] && previousFollowUpsForOrg(o.id).length"
															v-click-outside="() => collapsePrevFollowUps(o.id)"
															class="prev-follow-ups grey--text text--darken-1"
															@click.stop
														>
															<div
																v-for="(entry, idx) in previousFollowUpsForOrg(o.id)"
																:key="idx"
																class="text-caption d-flex align-center"
															>
																<v-icon x-small class="mr-1">mdi-phone-log-outline</v-icon>
																<span v-cursor-tip="followUpTooltip(entry)" class="text-truncate">
																	<span v-if="followUpContactLabel(entry)">· {{ followUpContactLabel(entry) }} </span>· {{ $t('ticketing.follow-up-method.' + entry.method) }} · {{ formatTime(entry.at) }}<span v-if="entry.byUserName"> · {{ $t('ticketing.follow-up-by', { name: entry.byUserName }) }}</span>
																</span>
															</div>
														</div>
													</div>
													<v-expand-transition>
														<div v-if="expandedAffectedOrgs[o.id]" class="affected-org-details pa-3">
															<!-- Customer user: header collapses label + empty hint into one row so the + button centers vertically -->
															<div class="d-flex align-center mb-1">
																<div v-if="!o.assignedUserName && !changeContactOpen[o.id]">
																	<div class="text-caption font-weight-medium grey--text text--darken-1">
																		{{ $t('ticketing.assigned-user') }}
																	</div>
																	<div class="text-caption grey--text">
																		{{ $t('ticketing.no-contact-yet') }}
																	</div>
																</div>
																<span v-else class="text-caption font-weight-medium grey--text text--darken-1">
																	{{ $t('ticketing.assigned-user') }}
																</span>
																<v-spacer />
																<v-tooltip v-if="!o.assignedUserName && !changeContactOpen[o.id]" top>
																	<template #activator="{ on, attrs }">
																		<v-btn
																			icon
																			x-small
																			color="primary"
																			v-bind="attrs"
																			v-on="on"
																			@click="openChangeContact(o.id)"
																		>
																			<v-icon small>mdi-plus</v-icon>
																		</v-btn>
																	</template>
																	<span>{{ $t('ticketing.set-contact') }}</span>
																</v-tooltip>
															</div>
															<div v-if="o.assignedUserName && !changeContactOpen[o.id]" class="affected-contact-row d-flex align-center mb-2">
																<v-avatar
																	size="26"
																	:color="userColor(o.assignedUserId)"
																	class="mr-2 white--text text-caption flex-shrink-0"
																>
																	{{ viewerInitials(o.assignedUserName) }}
																</v-avatar>
																<div class="flex-grow-1" style="min-width: 0;">
																	<div class="text-body-2 text-truncate">
																		{{ o.assignedUserName }}
																		<template v-if="affectedOrgUserRole(o)">
																			<span class="grey--text mx-1">·</span>
																			<span :class="userRoleColor(affectedOrgUserRole(o))">{{ affectedOrgUserRole(o) }}</span>
																		</template>
																	</div>
																	<div v-if="o.assignedUserEmail" class="d-flex align-center">
																		<a
																			href="#"
																			class="text-caption primary--text contact-phone-link text-truncate"
																			@click.prevent="copyText(o.assignedUserEmail)"
																		>{{ o.assignedUserEmail }}</a>
																		<v-btn
																			icon
																			x-small
																			class="ml-1"
																			:title="$t('copy')"
																			@click="copyText(o.assignedUserEmail)"
																		>
																			<v-icon x-small>mdi-content-copy</v-icon>
																		</v-btn>
																	</div>
																	<div v-if="o.assignedUserId" class="text-caption grey--text d-flex align-center">
																		<span class="text-truncate">{{ o.assignedUserId }}</span>
																		<v-btn
																			icon
																			x-small
																			class="ml-1"
																			:title="$t('copy')"
																			@click="copyText(o.assignedUserId)"
																		>
																			<v-icon x-small>mdi-content-copy</v-icon>
																		</v-btn>
																	</div>
																</div>
																<div class="d-flex align-center flex-shrink-0 ml-1">
																	<v-btn icon x-small color="primary" @click="openChangeContact(o.id)">
																		<v-icon x-small>mdi-account-switch-outline</v-icon>
																	</v-btn>
																</div>
															</div>
															<!-- Contact picker: shown only when explicitly opened. -->
															<v-autocomplete
																v-if="changeContactOpen[o.id]"
																:value="o.assignedUserId"
																:items="viewOrgUsers[o.id] && viewOrgUsers[o.id].items || []"
																item-text="fullName"
																item-value="id"
																:filter="filterUser"
																:label="o.assignedUserName ? $t('ticketing.change-contact') : $t('ticketing.set-contact')"
																outlined
																dense
																hide-details
																clearable
																:loading="viewOrgUsers[o.id] && viewOrgUsers[o.id].loading"
																:no-data-text="$t('ticketing.no-users')"
																append-outer-icon="mdi-close"
																class="mb-2"
																@change="(uid) => onAffectedOrgContactChange(o, uid)"
																@click:append-outer="closeChangeContact(o.id)"
															>
																<template #item="{ item }">
																	<v-list-item-content>
																		<v-list-item-title>{{ item.fullName }}</v-list-item-title>
																		<v-list-item-subtitle v-if="item.email" class="text-caption grey--text">
																			{{ item.email }}
																		</v-list-item-subtitle>
																	</v-list-item-content>
																</template>
															</v-autocomplete>

															<!-- Admin / technical contacts for this org with full CRUD. -->
															<v-divider class="my-2" />
															<div class="d-flex align-center mb-1">
																<div v-if="!orgAdminContacts(o.id).length">
																	<div class="text-caption font-weight-medium grey--text text--darken-1">
																		{{ $t('ticketing.contacts.admin-contacts') }}
																	</div>
																	<div class="text-caption grey--text">
																		{{ $t('ticketing.contacts.no-admin') }}
																	</div>
																</div>
																<span v-else class="text-caption font-weight-medium grey--text text--darken-1">
																	{{ $t('ticketing.contacts.admin-contacts') }}
																</span>
																<v-spacer />
																<v-tooltip top>
																	<template #activator="{ on, attrs }">
																		<v-btn
																			icon
																			x-small
																			color="primary"
																			v-bind="attrs"
																			v-on="on"
																			@click="addContact(o.id, 'admin')"
																		>
																			<v-icon small>mdi-plus</v-icon>
																		</v-btn>
																	</template>
																	<span>{{ $t('ticketing.contacts.add-admin') }}</span>
																</v-tooltip>
															</div>
															<div
																v-for="(c, idx) in orgAdminContacts(o.id)"
																:key="`${o.id}-admin-${idx}`"
																class="affected-contact-row d-flex align-center mb-2"
															>
																<v-avatar
																	size="26"
																	:color="userColor(c.fullName || `admin-${idx}`)"
																	class="mr-2 white--text text-caption flex-shrink-0"
																>
																	{{ viewerInitials(c.fullName || $t('ticketing.contacts.unnamed')) }}
																</v-avatar>
																<div class="flex-grow-1" style="min-width: 0;">
																	<div class="text-body-2 text-truncate">
																		{{ c.fullName || $t('ticketing.contacts.unnamed') }}
																		<span v-if="c.title" class="text-caption grey--text">· {{ c.title }}</span>
																	</div>
																	<div
																		v-for="(method, mIdx) in contactMethodsOf(c)"
																		:key="mIdx"
																		class="d-flex align-center"
																	>
																		<v-icon x-small class="mr-1">{{ contactMethodIcon(method.method) }}</v-icon>
																		<a
																			href="#"
																			class="text-caption text-truncate primary--text contact-phone-link"
																			@click.prevent="copyText(method.value)"
																		>
																			{{ formatContactValue(method) }}
																			<span v-if="method.method === 'call' && method.extension" class="grey--text">
																				ext. {{ method.extension }}
																			</span>
																		</a>
																	</div>
																</div>
																<div class="d-flex align-center flex-shrink-0 ml-1">
																	<v-btn icon x-small @click="editContact(o.id, 'admin', idx)">
																		<v-icon x-small>mdi-pencil</v-icon>
																	</v-btn>
																	<v-btn icon x-small @click="confirmDeleteContact(o.id, 'admin', idx)">
																		<v-icon x-small>mdi-delete</v-icon>
																	</v-btn>
																</div>
															</div>

															<div class="d-flex align-center mb-1 mt-2">
																<div v-if="!orgTechContacts(o.id).length">
																	<div class="text-caption font-weight-medium grey--text text--darken-1">
																		{{ $t('ticketing.contacts.tech-contacts') }}
																	</div>
																	<div class="text-caption grey--text">
																		{{ $t('ticketing.contacts.no-tech') }}
																	</div>
																</div>
																<span v-else class="text-caption font-weight-medium grey--text text--darken-1">
																	{{ $t('ticketing.contacts.tech-contacts') }}
																</span>
																<v-spacer />
																<v-tooltip top>
																	<template #activator="{ on, attrs }">
																		<v-btn
																			icon
																			x-small
																			color="primary"
																			v-bind="attrs"
																			v-on="on"
																			@click="addContact(o.id, 'tech')"
																		>
																			<v-icon small>mdi-plus</v-icon>
																		</v-btn>
																	</template>
																	<span>{{ $t('ticketing.contacts.add-tech') }}</span>
																</v-tooltip>
															</div>
															<div
																v-for="(c, idx) in orgTechContacts(o.id)"
																:key="`${o.id}-tech-${idx}`"
																class="affected-contact-row d-flex align-center mb-2"
															>
																<v-avatar
																	size="26"
																	:color="userColor(c.fullName || c.businessName || `tech-${idx}`)"
																	class="mr-2 white--text text-caption flex-shrink-0"
																>
																	{{ viewerInitials(c.fullName || c.businessName || $t('ticketing.contacts.unnamed')) }}
																</v-avatar>
																<div class="flex-grow-1" style="min-width: 0;">
																	<div class="text-body-2 text-truncate">
																		{{ c.fullName || c.businessName || $t('ticketing.contacts.unnamed') }}
																		<span v-if="c.fullName && c.businessName" class="text-caption grey--text">· {{ c.businessName }}</span>
																	</div>
																	<div
																		v-for="(method, mIdx) in contactMethodsOf(c)"
																		:key="mIdx"
																		class="d-flex align-center"
																	>
																		<v-icon x-small class="mr-1">{{ contactMethodIcon(method.method) }}</v-icon>
																		<a
																			href="#"
																			class="text-caption text-truncate primary--text contact-phone-link"
																			@click.prevent="copyText(method.value)"
																		>
																			{{ formatContactValue(method) }}
																			<span v-if="method.method === 'call' && method.extension" class="grey--text">
																				ext. {{ method.extension }}
																			</span>
																		</a>
																	</div>
																	<div v-if="c.consent" class="d-flex align-center mt-1">
																		<v-icon x-small class="mr-1">mdi-shield-check-outline</v-icon>
																		<v-chip
																			x-small
																			:color="consentColor(c.consent)"
																			dark
																		>
																			{{ $t(`ticketing.contacts.consent-options.${c.consent || 'unknown'}`) }}
																		</v-chip>
																	</div>
																</div>
																<div class="d-flex align-center flex-shrink-0 ml-1">
																	<v-btn icon x-small @click="editContact(o.id, 'tech', idx)">
																		<v-icon x-small>mdi-pencil</v-icon>
																	</v-btn>
																	<v-btn icon x-small @click="confirmDeleteContact(o.id, 'tech', idx)">
																		<v-icon x-small>mdi-delete</v-icon>
																	</v-btn>
																</div>
															</div>
														</div>
													</v-expand-transition>
												</div>
											</div>
											<v-autocomplete
												ref="affectedOrgPicker"
												v-model="affectedOrgPick"
												:search-input.sync="affectedOrgPickSearch"
												:items="affectedOrgPickerItems"
												item-text="text"
												item-value="value"
												:filter="filterOrganization"
												:label="$t('ticketing.add-organization')"
												prepend-inner-icon="mdi-plus"
												outlined
												dense
												hide-details
												clearable
												class="mt-2"
												@change="onAffectedOrgAdd"
											>
												<template #item="{ item }">
													<v-list-item-content>
														<v-list-item-title>{{ item.text }}</v-list-item-title>
														<v-list-item-subtitle class="text-caption grey--text">
															{{ item.value }}
														</v-list-item-subtitle>
													</v-list-item-content>
												</template>
											</v-autocomplete>
										</div>
									</div>

									<v-divider class="my-2" />

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.assigned-devops') }}
										</div>
										<v-autocomplete
											:value="activeTicket.assignedDevopsIds || []"
											:items="devopsUsers"
											item-text="fullName"
											item-value="id"
											multiple
											small-chips
											deletable-chips
											dense
											outlined
											hide-details
											:loading="savingDevops"
											@change="changeAssignedDevops"
										>
											<template #append>
												<v-btn
													v-if="canTakeDevops"
													x-small
													text
													color="primary"
													:loading="savingDevops"
													class="take-devops-btn"
													@mousedown.stop.prevent="takeDevops"
												>
													<v-icon x-small left>{{ takeDevopsIcon }}</v-icon>
													{{ takeDevopsLabel }}
												</v-btn>
											</template>
										</v-autocomplete>
									</div>

									<div class="field-block">
										<div class="d-flex align-center">
											<span class="field-label mb-0">{{ $t('ticketing.watchers') }}</span>
											<v-spacer />
											<v-menu
												v-model="watchersMenuOpen"
												:close-on-content-click="false"
												offset-y
												left
												max-width="320"
												@input="onWatchersMenuToggle"
											>
												<template #activator="{ on, attrs }">
													<v-btn
														icon
														x-small
														color="primary"
														v-bind="attrs"
														v-on="on"
													>
														<v-icon small>mdi-plus</v-icon>
													</v-btn>
												</template>
												<v-card width="320">
													<v-card-text class="pa-2">
														<v-autocomplete
															ref="watchersAutocomplete"
															:value="activeTicket.watcherIds || []"
															:items="devopsUsers"
															item-text="fullName"
															item-value="id"
															multiple
															small-chips
															deletable-chips
															dense
															outlined
															hide-details
															autofocus
															:loading="savingWatchers"
															:label="$t('ticketing.watchers')"
															@change="changeWatchers"
														/>
													</v-card-text>
												</v-card>
											</v-menu>
											<v-btn
												v-if="!isWatching"
												x-small
												text
												color="primary"
												@click="watchTicket"
											>
												<v-icon x-small left>mdi-eye-plus</v-icon>
												{{ $t('ticketing.watch') }}
											</v-btn>
											<v-btn
												v-else
												x-small
												text
												@click="unwatchTicket"
											>
												<v-icon x-small left>mdi-eye-off</v-icon>
												{{ $t('ticketing.unwatch') }}
											</v-btn>
										</div>
										<div
											class="field-value"
											:class="{ 'field-value--empty': !activeTicket.watcherIds?.length }"
										>
											<div v-if="activeTicket.watcherIds?.length" class="viewers-stack d-flex">
												<v-tooltip
													v-for="uid in activeTicket.watcherIds"
													:key="uid"
													top
												>
													<template #activator="{ on, attrs }">
														<v-avatar
															size="24"
															:color="userColor(uid)"
															class="viewer-avatar white--text text-caption"
															v-bind="attrs"
															v-on="on"
															@click="removeWatcherClick(uid)"
														>
															{{ viewerInitials(devopsName(uid)) }}
														</v-avatar>
													</template>
													<span>{{ devopsName(uid) }}</span>
												</v-tooltip>
											</div>
											<span v-else>-</span>
										</div>
									</div>

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.seen-by') }}
										</div>
										<div
											class="field-value"
											:class="{ 'field-value--empty': !ticketSeenBy.length }"
										>
											<div v-if="ticketSeenBy.length" class="viewers-stack d-flex flex-wrap">
												<v-tooltip
													v-for="v in ticketSeenBy"
													:key="`seen-${v.userId}`"
													top
												>
													<template #activator="{ on, attrs }">
														<v-avatar
															size="24"
															:color="userColor(v.userId)"
															class="viewer-avatar white--text text-caption"
															v-bind="attrs"
															v-on="on"
														>
															{{ viewerInitials(devopsName(v.userId)) }}
														</v-avatar>
													</template>
													<span>{{ $t('ticketing.seen-by-last-visit', { name: devopsName(v.userId), time: formatTimeAbsolute(v.lastMs) }) }}</span>
												</v-tooltip>
											</div>
											<span v-else>{{ $t('ticketing.seen-by-empty') }}</span>
										</div>
									</div>

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.status-label') }}
										</div>
										<v-select
											v-model="quickStatus"
											:items="statusOptions"
											dense
											outlined
											hide-details
											@change="changeStatus"
										>
											<template #selection="{ item }">
												<span>{{ item.text }}</span>
											</template>
											<template #item="{ item }">
												<span>{{ item.text }}</span>
											</template>
										</v-select>
									</div>

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.priority-label') }}
										</div>
										<v-select
											v-model="quickPriority"
											:items="priorityOptions"
											dense
											outlined
											hide-details
											@change="changePriority"
										>
											<template #selection="{ item }">
												<span class="font-weight-medium" :style="{ color: priorityHex(item.value) }">
													{{ priorityEmoji(item.value) }}&nbsp; {{ item.text }}
												</span>
											</template>
											<template #item="{ item }">
												<span class="font-weight-medium" :style="{ color: priorityHex(item.value) }">
													{{ priorityEmoji(item.value) }}&nbsp; {{ item.text }}
												</span>
											</template>
										</v-select>
									</div>

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.due-date') }}
										</div>
										<v-menu
											v-model="dueDateMenu"
											:close-on-content-click="false"
											offset-y
											min-width="auto"
											transition="scale-transition"
										>
											<template #activator="{ on, attrs }">
												<v-text-field
													:value="activeTicket.dueAt ? formatDueDate(activeTicket.dueAt) : ''"
													:placeholder="$t('ticketing.due-date-placeholder')"
													dense
													outlined
													hide-details
													readonly
													clearable
													:class="{ 'due-date--overdue': isOverdue(activeTicket) }"
													prepend-inner-icon="mdi-calendar"
													v-bind="attrs"
													v-on="on"
													@click:clear="changeDueDate(null)"
												/>
											</template>
											<v-date-picker
												:value="activeTicket.dueAt || null"
												:locale="$i18n.locale"
												no-title
												@input="(v) => { dueDateMenu = false; changeDueDate(v); }"
											/>
										</v-menu>
									</div>

									<v-divider class="my-2" />

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.created-by') }}
										</div>
										<div class="field-value text-body-2">
											{{ activeTicket.createdByName }}
											<div v-cursor-tip="formatTimeAbsolute(activeTicket.createdAt)" class="text-caption grey--text">
												{{ formatTime(activeTicket.createdAt) }}
											</div>
										</div>
									</div>

									<div class="field-block">
										<div class="field-label">
											{{ $t('ticketing.last-update') }}
										</div>
										<div v-cursor-tip="formatTimeAbsolute(activeTicket.updatedAt)" class="field-value text-body-2">
											{{ formatTime(activeTicket.updatedAt) }}
										</div>
									</div>

									<div v-if="lastStatusEvent" class="field-block">
										<div class="field-label">
											{{ $t('ticketing.status-event.' + lastStatusEvent.kind) }}
										</div>
										<div v-cursor-tip="formatTimeAbsolute(lastStatusEvent.at)" class="field-value text-body-2">
											{{ formatTime(lastStatusEvent.at) }}
											<span v-if="lastStatusEvent.actorName" class="grey--text">
												· {{ $t('ticketing.follow-up-by', { name: lastStatusEvent.actorName }) }}
											</span>
										</div>
									</div>
								</v-card>
							</div>
						</div>
					</v-card-text>
				</v-card>
			</v-dialog>

			<v-dialog
				v-model="imagePreviewOpen"
				fullscreen
				hide-overlay
				transition="dialog-bottom-transition"
				content-class="image-preview-dialog"
				@keydown.native="onImagePreviewKey"
			>
				<div v-if="imagePreviewList.length" class="image-preview-container">
					<v-btn icon dark class="image-preview-close" @click="imagePreviewOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
					<v-btn
						v-if="imagePreviewList.length > 1"
						icon
						dark
						class="image-preview-nav image-preview-nav--prev"
						:disabled="imagePreviewIndex === 0"
						@click="prevImagePreview"
					>
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
					<v-btn
						v-if="imagePreviewList.length > 1"
						icon
						dark
						class="image-preview-nav image-preview-nav--next"
						:disabled="imagePreviewIndex >= imagePreviewList.length - 1"
						@click="nextImagePreview"
					>
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
					<img
						:src="imagePreviewList[imagePreviewIndex].url"
						:alt="imagePreviewList[imagePreviewIndex].name"
						class="image-preview-img"
					>
					<div class="image-preview-caption">
						<span class="text-truncate">{{ imagePreviewList[imagePreviewIndex].name }}</span>
						<span v-if="imagePreviewList.length > 1" class="ml-2 text-caption">{{ imagePreviewIndex + 1 }} / {{ imagePreviewList.length }}</span>
						<v-spacer />
						<a :href="imagePreviewList[imagePreviewIndex].url" target="_blank" rel="noopener" class="white--text text-caption">
							<v-icon small color="white">mdi-open-in-new</v-icon>
						</a>
					</div>
				</div>
			</v-dialog>

			<v-dialog v-model="externalLinkConfirmOpen" max-width="460" content-class="external-link-dialog">
				<v-card class="external-link-card">
					<div class="external-link-header">
						<div class="external-link-icon-wrap">
							<v-icon class="external-link-icon">mdi-shield-link-variant-outline</v-icon>
						</div>
						<div class="external-link-titles">
							<div class="external-link-title">
								{{ $t('ticketing.external-link-title') }}
							</div>
							<div class="external-link-subtitle">
								{{ $t('ticketing.external-link-subtitle') }}
							</div>
						</div>
					</div>
					<v-divider />
					<v-card-text class="external-link-body">
						<p class="external-link-warning">
							{{ $t('ticketing.external-link-warning') }}
						</p>
						<div class="external-link-url-wrap">
							<v-icon small class="external-link-url-icon">mdi-link-variant</v-icon>
							<!-- eslint-disable-next-line vue/no-v-html -->
							<span class="external-link-url" v-html="formatUrlForBreaking(pendingExternalLink)" />
						</div>
					</v-card-text>
					<v-divider />
					<v-card-actions class="px-4 py-3">
						<v-spacer />
						<v-btn text @click="externalLinkConfirmOpen = false">
							{{ $t('ticketing.cancel') }}
						</v-btn>
						<v-btn color="primary" depressed @click="confirmExternalLink">
							<v-icon small left>mdi-open-in-new</v-icon>
							{{ $t('ticketing.external-link-open') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog v-model="clearAllConfirmOpen" max-width="420">
				<v-card>
					<v-card-title class="text-h6">
						{{ $t('ticketing.clear-all-confirm-title') }}
					</v-card-title>
					<v-card-text class="text-body-2">
						{{ $t('ticketing.clear-all-confirm-text') }}
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn text @click="clearAllConfirmOpen = false">
							{{ $t('ticketing.cancel') }}
						</v-btn>
						<v-btn color="red" dark @click="clearAllNotifications">
							{{ $t('ticketing.clear-all') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog v-model="orgTicketsDialog" max-width="900" scrollable>
				<v-card v-if="activeTicket">
					<v-tabs v-model="orgTicketsTab" grow>
						<v-tab>{{ $t('ticketing.org-tickets-open') }} ({{ orgOpenTickets.length }})</v-tab>
						<v-tab>{{ $t('ticketing.org-tickets-closed') }} ({{ orgClosedTickets.length }})</v-tab>
					</v-tabs>
					<v-divider />
					<v-card-text style="max-height: 70vh;" class="pa-4">
						<v-tabs-items v-model="orgTicketsTab">
							<v-tab-item>
								<v-data-table
									:headers="orgOpenHeaders"
									:items="orgOpenTickets"
									:items-per-page="-1"
									sort-by="createdAt"
									:sort-desc="false"
									hide-default-footer
									dense
									no-data-text=""
									class="ticketing-table"
									@click:row="openTicketFromOrgList"
								>
									<template #item.type="{ item }">
										<span class="text-caption">{{ $t(`ticketing.type.${item.type}`) }}</span>
									</template>
									<template #item.assignedDevopsIds="{ item }">
										<div class="d-flex align-center" style="overflow: hidden;">
											<v-tooltip
												v-for="(uid, idx) in (item.assignedDevopsIds || []).slice(0, 3)"
												:key="idx"
												bottom
											>
												<template #activator="{ on, attrs }">
													<v-avatar
														size="22"
														:color="userColor(uid)"
														class="mr-1 flex-shrink-0"
														v-bind="attrs"
														v-on="on"
													>
														<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
													</v-avatar>
												</template>
												<span>{{ devopsName(uid) }}</span>
											</v-tooltip>
											<v-tooltip
												v-if="(item.assignedDevopsIds || []).length > 3"
												bottom
											>
												<template #activator="{ on, attrs }">
													<span
														class="text-caption grey--text"
														v-bind="attrs"
														v-on="on"
													>+{{ item.assignedDevopsIds.length - 3 }}</span>
												</template>
												<span>{{ item.assignedDevopsIds.slice(3).map(devopsName).join(', ') }}</span>
											</v-tooltip>
											<span v-if="!item.assignedDevopsIds?.length" class="text-caption grey--text">-</span>
										</div>
									</template>
									<template #item.watcherIds="{ item }">
										<div class="d-flex align-center" style="overflow: hidden;">
											<v-tooltip
												v-for="(uid, idx) in (item.watcherIds || []).slice(0, 3)"
												:key="idx"
												bottom
											>
												<template #activator="{ on, attrs }">
													<v-avatar
														size="22"
														:color="userColor(uid)"
														class="mr-1 flex-shrink-0"
														v-bind="attrs"
														v-on="on"
													>
														<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
													</v-avatar>
												</template>
												<span>{{ devopsName(uid) }}</span>
											</v-tooltip>
											<v-tooltip
												v-if="(item.watcherIds || []).length > 3"
												bottom
											>
												<template #activator="{ on, attrs }">
													<span
														class="text-caption grey--text"
														v-bind="attrs"
														v-on="on"
													>+{{ item.watcherIds.length - 3 }}</span>
												</template>
												<span>{{ item.watcherIds.slice(3).map(devopsName).join(', ') }}</span>
											</v-tooltip>
											<span v-if="!item.watcherIds?.length" class="text-caption grey--text">-</span>
										</div>
									</template>
									<template #item.status="{ item }">
										<span class="text-caption">
											{{ $t(`ticketing.status.${item.status}`) }}
										</span>
									</template>
									<template #item.priority="{ item }">
										<span class="text-caption font-weight-medium" :style="{ color: priorityHex(item.priority) }">
											{{ priorityEmoji(item.priority) }}&nbsp; {{ $t(`ticketing.priority.${item.priority}`) }}
										</span>
									</template>
									<template #item.createdAt="{ item }">
										<span v-cursor-tip="formatTimeAbsolute(item.createdAt)" class="text-caption">{{ formatTime(item.createdAt) }}</span>
									</template>
								</v-data-table>
							</v-tab-item>
							<v-tab-item>
								<v-data-table
									:headers="orgClosedHeaders"
									:items="orgClosedTickets"
									:items-per-page="-1"
									sort-by="createdAt"
									:sort-desc="false"
									hide-default-footer
									dense
									no-data-text=""
									class="ticketing-table"
									@click:row="openTicketFromOrgList"
								>
									<template #item.type="{ item }">
										<span class="text-caption">{{ $t(`ticketing.type.${item.type}`) }}</span>
									</template>
									<template #item.assignedDevopsIds="{ item }">
										<div class="d-flex align-center" style="overflow: hidden;">
											<v-tooltip
												v-for="(uid, idx) in (item.assignedDevopsIds || []).slice(0, 3)"
												:key="idx"
												bottom
											>
												<template #activator="{ on, attrs }">
													<v-avatar
														size="22"
														:color="userColor(uid)"
														class="mr-1 flex-shrink-0"
														v-bind="attrs"
														v-on="on"
													>
														<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
													</v-avatar>
												</template>
												<span>{{ devopsName(uid) }}</span>
											</v-tooltip>
											<v-tooltip
												v-if="(item.assignedDevopsIds || []).length > 3"
												bottom
											>
												<template #activator="{ on, attrs }">
													<span
														class="text-caption grey--text"
														v-bind="attrs"
														v-on="on"
													>+{{ item.assignedDevopsIds.length - 3 }}</span>
												</template>
												<span>{{ item.assignedDevopsIds.slice(3).map(devopsName).join(', ') }}</span>
											</v-tooltip>
											<span v-if="!item.assignedDevopsIds?.length" class="text-caption grey--text">-</span>
										</div>
									</template>
									<template #item.watcherIds="{ item }">
										<div class="d-flex align-center" style="overflow: hidden;">
											<v-tooltip
												v-for="(uid, idx) in (item.watcherIds || []).slice(0, 3)"
												:key="idx"
												bottom
											>
												<template #activator="{ on, attrs }">
													<v-avatar
														size="22"
														:color="userColor(uid)"
														class="mr-1 flex-shrink-0"
														v-bind="attrs"
														v-on="on"
													>
														<span class="white--text text-caption">{{ initials(devopsName(uid)) }}</span>
													</v-avatar>
												</template>
												<span>{{ devopsName(uid) }}</span>
											</v-tooltip>
											<v-tooltip
												v-if="(item.watcherIds || []).length > 3"
												bottom
											>
												<template #activator="{ on, attrs }">
													<span
														class="text-caption grey--text"
														v-bind="attrs"
														v-on="on"
													>+{{ item.watcherIds.length - 3 }}</span>
												</template>
												<span>{{ item.watcherIds.slice(3).map(devopsName).join(', ') }}</span>
											</v-tooltip>
											<span v-if="!item.watcherIds?.length" class="text-caption grey--text">-</span>
										</div>
									</template>
									<template #item.createdAt="{ item }">
										<span v-cursor-tip="formatTimeAbsolute(item.createdAt)" class="text-caption">{{ formatTime(item.createdAt) }}</span>
									</template>
								</v-data-table>
							</v-tab-item>
						</v-tabs-items>
					</v-card-text>
				</v-card>
			</v-dialog>

			<v-dialog v-model="contactDialog" max-width="540" persistent>
				<v-card v-if="contactDraft">
					<v-card-title class="text-h6">
						{{ contactDraftKind === 'admin' ? $t('ticketing.contacts.edit-admin') : $t('ticketing.contacts.edit-tech') }}
					</v-card-title>
					<v-card-text>
						<v-form ref="contactForm" v-model="contactFormValid">
							<v-autocomplete
								v-if="contactDraftKind === 'admin'"
								:items="orgUsers"
								:item-text="adminUserItemText"
								:filter="adminUserSearchFilter"
								:label="$t('ticketing.contacts.search-org-user')"
								:placeholder="$t('ticketing.contacts.search-org-user-hint')"
								return-object
								clearable
								hide-no-data
								hide-details="auto"
								dense
								outlined
								class="mb-5"
								prepend-inner-icon="mdi-magnify"
								:loading="orgUsersLoading"
								@change="applyAdminUserAutocomplete"
							>
								<template #item="{ item }">
									<v-list-item-content>
										<v-list-item-title>{{ item.fullName || $t('ticketing.contacts.unnamed') }}</v-list-item-title>
										<v-list-item-subtitle>
											<span v-if="item.email">{{ item.email }}</span>
											<span v-if="item.role"> · {{ item.role }}</span>
										</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</v-autocomplete>
							<v-divider v-if="contactDraftKind === 'admin'" class="mb-5" />

							<v-autocomplete
								v-if="contactDraftKind === 'tech'"
								:items="globalTechContacts"
								:item-text="techContactItemText"
								:filter="techContactSearchFilter"
								:label="$t('ticketing.contacts.search-existing')"
								:placeholder="$t('ticketing.contacts.search-existing-hint')"
								return-object
								clearable
								hide-no-data
								hide-details="auto"
								dense
								outlined
								class="mb-5"
								prepend-inner-icon="mdi-magnify"
								@change="applyTechAutocomplete"
							>
								<template #item="{ item }">
									<v-list-item-content>
										<v-list-item-title>{{ item.fullName || $t('ticketing.contacts.unnamed') }}</v-list-item-title>
										<v-list-item-subtitle>
											<span v-if="item.businessName">{{ item.businessName }} · </span>
											<span>{{ item.primaryValue }}</span>
											<span v-if="item.secondaryValue"> · {{ item.secondaryValue }}</span>
										</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</v-autocomplete>
							<v-divider v-if="contactDraftKind === 'tech'" class="mb-5" />

							<v-text-field
								v-if="contactDraftKind === 'tech'"
								v-model="contactDraft.businessName"
								:label="$t('ticketing.contacts.business-name')"
								dense
								outlined
								hide-details="auto"
								class="mb-3"
							/>
							<v-text-field
								v-model="contactDraft.fullName"
								:label="$t('ticketing.contacts.full-name')"
								:rules="contactDraftKind === 'admin' ? [v => !!v || $t('required')] : []"
								dense
								outlined
								hide-details="auto"
								class="mb-3"
							/>
							<v-text-field
								v-if="contactDraftKind === 'admin'"
								v-model="contactDraft.title"
								:label="$t('ticketing.contacts.contact-title')"
								dense
								outlined
								hide-details="auto"
								class="mb-5"
							/>

							<div class="text-caption font-weight-medium mb-1">
								{{ $t('ticketing.contacts.preferred-method') }}
							</div>
							<v-row dense>
								<v-col cols="4">
									<v-select
										:value="contactDraft.primaryMethod"
										:items="contactMethodOptions"
										dense
										outlined
										hide-details
										@change="(v) => onContactMethodChange('primary', v)"
									/>
								</v-col>
								<v-col cols="8">
									<v-text-field
										v-if="isPhoneMethod(contactDraft.primaryMethod)"
										v-model="contactDraft.primaryValue"
										:label="contactValueLabel(contactDraft.primaryMethod)"
										dense
										outlined
										hide-details
										class="contact-phone-field"
									>
										<template #prepend-inner>
											<vue-tel-input-vuetify
												v-model="contactDraft.primaryValue"
												:only-countries="phoneAvailableCountries"
												default-country="CA"
												append-icon="mdi-chevron-down"
												dense
												disabled-fetching-country
												select-classes="select-phone"
												text-field-classes="input-phone"
												@input="(_, obj) => onContactPhoneInput('primary', obj)"
											/>
										</template>
									</v-text-field>
									<v-text-field
										v-else
										v-model="contactDraft.primaryValue"
										:label="contactValueLabel(contactDraft.primaryMethod)"
										dense
										outlined
										hide-details
									/>
								</v-col>
								<v-col v-if="contactDraft.primaryMethod === 'call'" cols="12">
									<v-text-field
										v-model="contactDraft.primaryExtension"
										:label="$t('ticketing.contacts.extension')"
										dense
										outlined
										hide-details
									/>
								</v-col>
							</v-row>

							<div class="text-caption font-weight-medium mb-1 mt-5">
								{{ $t('ticketing.contacts.secondary-method') }}
							</div>
							<v-row dense>
								<v-col cols="4">
									<v-select
										:value="contactDraft.secondaryMethod"
										:items="contactMethodOptionsWithNone"
										dense
										outlined
										hide-details
										clearable
										@change="(v) => onContactMethodChange('secondary', v)"
									/>
								</v-col>
								<v-col cols="8">
									<v-text-field
										v-if="isPhoneMethod(contactDraft.secondaryMethod)"
										v-model="contactDraft.secondaryValue"
										:label="contactValueLabel(contactDraft.secondaryMethod)"
										dense
										outlined
										hide-details
										class="contact-phone-field"
									>
										<template #prepend-inner>
											<vue-tel-input-vuetify
												v-model="contactDraft.secondaryValue"
												:only-countries="phoneAvailableCountries"
												default-country="CA"
												append-icon="mdi-chevron-down"
												dense
												disabled-fetching-country
												select-classes="select-phone"
												text-field-classes="input-phone"
												@input="(_, obj) => onContactPhoneInput('secondary', obj)"
											/>
										</template>
									</v-text-field>
									<v-text-field
										v-else
										v-model="contactDraft.secondaryValue"
										:label="contactValueLabel(contactDraft.secondaryMethod)"
										:disabled="!contactDraft.secondaryMethod"
										dense
										outlined
										hide-details
									/>
								</v-col>
								<v-col v-if="contactDraft.secondaryMethod === 'call'" cols="12">
									<v-text-field
										v-model="contactDraft.secondaryExtension"
										:label="$t('ticketing.contacts.extension')"
										dense
										outlined
										hide-details
									/>
								</v-col>
							</v-row>

							<v-select
								v-if="contactDraftKind === 'tech'"
								v-model="contactDraft.consent"
								:items="consentOptions"
								:label="$t('ticketing.contacts.consent')"
								dense
								outlined
								hide-details
								class="mt-3"
							/>
						</v-form>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn text @click="contactDialog = false">
							{{ $t('ticketing.cancel') }}
						</v-btn>
						<v-btn
							color="primary"
							:disabled="!contactFormValid"
							:loading="contactSaving"
							@click="saveContactDraft"
						>
							{{ $t('save') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog v-model="contactDeleteConfirm" max-width="420">
				<v-card>
					<v-card-title class="text-h6">
						{{ $t('ticketing.contacts.delete-confirm') }}
					</v-card-title>
					<v-card-actions>
						<v-spacer />
						<v-btn text @click="contactDeleteConfirm = false">
							{{ $t('ticketing.cancel') }}
						</v-btn>
						<v-btn color="red" dark :loading="contactSaving" @click="deleteContactConfirmed">
							{{ $t('delete') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import { fullName, phoneNumberFormater } from "@/common/utils";
import { AVAILABLE_COUNTRIES } from "@/common/const";
import { waitForEventsAuth } from "@/composables/events/handler";
import TicketingNotificationsPanel from "@/components/support/TicketingNotificationsPanel.vue";
import {
	subscribeTickets,
	subscribeTicket,
	subscribeMessages,
	subscribeNotifications,
	subscribeVisits,
	subscribeTicketReaders,
	subscribeActivity,
	markTicketSeen,
	markConversationSeen,
	logTicketVisit,
	logFollowUp,
	joinTicketPresence,
	subscribeTicketPresence,
	subscribeAllPresence,
	setTicketTyping,
	clearTicketTyping,
	subscribeTicketTyping,
	uploadAttachment,
	deleteAttachment,
	createTicket,
	updateTicket,
	addMessage,
	toggleReaction,
	toggleActivityReaction,
	addWatcher,
	removeWatcher,
	markNotificationRead,
	markAllNotificationsRead,
	markTicketNotificationsRead,
	deleteNotification,
	deleteAllNotifications,
	subscribeTodos,
	addTodo,
	updateTodoText,
	setTodoCompleted,
	deleteTodo,
	subscribeOrgContacts,
	saveOrgContacts,
	subscribeAllOrgContacts,
	getTicketOrganizations,
	addTicketOrganization,
	removeTicketOrganization,
	updateTicketOrganizationContact,
	notifyUnassignedTicket
} from "@/composables/ticketing/ticketingStore";

const TYPES = [
	"bug-report",
	"call-quality",
	"feature-request",
	"hardware",
	"ivr-changes",
	"porting",
	"request",
	"sl1",
	"sl2",
	"sl3"
];

const PRIORITIES = ["low", "medium", "high", "urgent"];

const STATUSES = [
	"open",
	"waiting-on-customer",
	"waiting-on-technician",
	"waiting-on-vendor",
	"needs-support-action",
	"in-progress",
	"qa",
	"awaiting-deploy",
	"closed",
	"resolved"
];

const ACTIVE_STATUSES = ["open", "waiting-on-customer", "waiting-on-technician", "waiting-on-vendor", "needs-support-action", "in-progress", "qa", "awaiting-deploy"];

const FOLLOW_UP_METHODS = ["email", "phone", "voicemail", "sms", "chat", "in-person"];

const escapeHtml = (s) =>
	String(s ?? "").replace(/[&<>"']/g, (c) => ({
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		"\"": "&quot;",
		"'": "&#39;"
	})[c]);

export default {
	name: "Ticketing",

	components: {
		TicketingNotificationsPanel
	},

	directives: {
		cursorTip: {
			bind(el, binding) {
				const state = {
					text: typeof binding.value === "function" ? binding.value() : binding.value,
					tipEl: null,
					timer: null,
					x: 0,
					y: 0,
					delay: 0
				};

				const updatePosition = () => {
					if (!state.tipEl) return;
					const offset = 14;
					const pad = 6;
					const rect = state.tipEl.getBoundingClientRect();
					let left = state.x + offset;
					let top = state.y + offset;

					if (left + rect.width + pad > window.innerWidth) {
						left = state.x - rect.width - offset;
					}
					if (top + rect.height + pad > window.innerHeight) {
						top = state.y - rect.height - offset;
					}
					state.tipEl.style.left = `${Math.max(pad, left)}px`;
					state.tipEl.style.top = `${Math.max(pad, top)}px`;
				};

				const show = () => {
					if (state.tipEl || !state.text) return;
					const tip = document.createElement("div");

					tip.className = "cursor-tip-bubble";
					tip.textContent = state.text;
					document.body.appendChild(tip);
					state.tipEl = tip;
					updatePosition();
				};

				const hide = () => {
					if (state.timer) {
						clearTimeout(state.timer);
						state.timer = null;
					}
					if (state.tipEl) {
						state.tipEl.remove();
						state.tipEl = null;
					}
				};

				const onEnter = (e) => {
					state.x = e.clientX;
					state.y = e.clientY;
					if (state.timer) clearTimeout(state.timer);
					state.timer = setTimeout(show, state.delay);
				};

				const onMove = (e) => {
					state.x = e.clientX;
					state.y = e.clientY;
					if (state.tipEl) updatePosition();
				};

				el.__cursorTip = { state, hide, onEnter, onMove };
				el.addEventListener("mouseenter", onEnter);
				el.addEventListener("mousemove", onMove);
				el.addEventListener("mouseleave", hide);
				el.addEventListener("click", hide);
			},

			update(el, binding) {
				if (!el.__cursorTip) return;
				el.__cursorTip.state.text = typeof binding.value === "function" ? binding.value() : binding.value;
				if (el.__cursorTip.state.tipEl) {
					el.__cursorTip.state.tipEl.textContent = el.__cursorTip.state.text;
				}
			},

			unbind(el) {
				if (!el.__cursorTip) return;
				const { hide, onEnter, onMove } = el.__cursorTip;

				el.removeEventListener("mouseenter", onEnter);
				el.removeEventListener("mousemove", onMove);
				el.removeEventListener("mouseleave", hide);
				el.removeEventListener("click", hide);
				hide();
				delete el.__cursorTip;
			}
		}
	},

	props: {
		embedded: {
			type: Boolean,
			default: false
		},

		openTicketId: {
			type: String,
			default: null
		},

		openTicketTab: {
			type: String,
			default: null
		}
	},

	data() {
		return {
			loading: true,
			tickets: [],
			notifications: [],
			organizations: [],
			devopsUsers: [],
			orgUsers: [],
			orgUsersLoading: false,
			additionalOrgPick: null,
			affectedOrgsExpanded: true,
			affectedOrgPick: null,
			affectedOrgPickSearch: null,
			expandedAffectedOrgs: {},
			expandedPrevFollowUpsByOrg: {},
			viewOrgUsers: {},
			changeContactOpen: {},
			search: "",
			filterTab: "mine",
			orgFilter: null,
			sortBy: "createdAt",
			sortDesc: false,

			formDialog: false,
			formValid: false,
			formSaving: false,
			editingId: null,
			form: this.emptyForm(),
			formAttachmentsUploading: false,

			detailDialog: false,
			detailTab: "description",
			activeTicketId: null,
			activeTicket: null,
			navTicketIds: [],
			navFilterTab: null,
			messages: [],
			emojiPalette: ["👍", "👌", "❤️", "😂", "🤣", "😉", "😍", "😇", "😮", "😢", "🤯", "🥶", "🥵", "🤢", "🤮", "🎉", "🥳", "🚀", "✅", "❌", "⏳", "🚨", "💰", "💸", "🤑", "👀", "🫣", "🙏", "🤝", "🤞", "✋", "🔥", "⚡", "👎", "🖕", "💩", "💯", "💀", "☠️", "🪦", "⚰️", "👮‍♂️", "🚔", "🐢", "🌈", "☀️", "🧊", "🍺", "🍻", "🥃", "🍷", "🍹", "🫶", "💔", "🏴‍☠️"],
			newMessage: "",
			sendingMessage: false,
			messageAttachments: [],
			messageAttachmentsUploading: false,
			quickStatus: null,
			quickPriority: null,
			closeBtnLoading: false,
			dueDateMenu: false,
			savingDevops: false,
			savingWatchers: false,
			watchersMenuOpen: false,
			loggingFollowUpOrgId: null,
			impersonatingOrg: false,
			notifMenuOpen: false,
			clearAllConfirmOpen: false,
			externalLinkConfirmOpen: false,
			pendingExternalLink: "",
			copiedOrgId: false,
			copiedUserId: false,
			orgTicketsDialog: false,
			orgTicketsTab: 0,
			orgTicketsOrgId: null,

			contactsExpanded: false,
			orgContacts: null,
			unsubOrgContacts: null,
			allOrgContactsRaw: [],
			unsubAllOrgContacts: null,
			contactDialog: false,
			contactSaving: false,
			contactFormValid: false,
			contactDraft: null,
			contactDraftKind: null, // 'admin' | 'tech'
			contactDraftIndex: -1, // -1 for new
			contactDraftOrgId: null,
			contactDeleteConfirm: false,
			contactDeleteTarget: null, // { orgId, kind, index }

			// unread incoming messages while user scrolled up
			unreadMessageCount: 0,

			// image preview
			imagePreviewOpen: false,
			imagePreviewList: [],
			imagePreviewIndex: 0,

			// mention picker
			mentionMenuOpen: false,
			mentionMenuX: 0,
			mentionMenuY: 0,
			mentionQuery: "",
			mentionStart: -1,
			mentionIndex: 0,
			pendingMentions: [], // { id, fullName }

			// last seen update per ticket (for unread dot) - keyed by ticket id, value: ms timestamp
			lastSeen: {},
			// last time the user actually opened the Conversation tab per ticket
			lastConversationSeen: {},
			// activity log entries for the active ticket
			activity: [],
			// todos for the active ticket
			todos: [],
			unsubTodos: null,
			newTodoText: "",
			editingTodoId: null,
			editingTodoText: "",
			// guard so we only log one visit per opening of the detail dialog
			visitLogged: false,
			// Snapshot of the user's last-seen conversation timestamp captured
			// when the ticket is opened. Used to render the "unread messages
			// below this line" divider. Cleared 5s after the user has actually
			// been on the conversation tab.
			unreadBoundaryMs: 0,
			unreadBoundaryTimer: null,
			unreadBoundaryEligibleToClear: false,
			// Ensures the conversation is positioned at the unread divider (or the
			// bottom) exactly once per ticket opening, whether the messages finish
			// loading before or after the conversation tab is first shown.
			didInitialConversationScroll: false,
			// While true, the conversation keeps the unread divider anchored
			// (latest read message at the top) as images finish loading, instead
			// of snapping to the bottom. Released on the first genuine user scroll.
			unreadPinActive: false,
			// Timestamp of our last programmatic scroll, used to tell our own
			// scrolls apart from user-initiated ones.
			programmaticScrollAt: 0,

			unsubTickets: null,
			unsubTicket: null,
			unsubMessages: null,
			unsubNotifications: null,
			unsubVisits: null,
			unsubActivity: null,
			unsubPresence: null,
			leavePresence: null,
			activeViewers: [],
			unsubTyping: null,
			activeTypers: [],
			typingTimeout: null,
			typingActive: false,
			// Per-user "last read" timestamp on the conversation of the active
			// ticket. Drives the read-receipt avatars next to messages.
			ticketReaders: [],
			unsubTicketReaders: null,
			// Map of ticketId → array of { userId, name, at } for everyone
			// currently viewing any ticket. Drives the per-row "live viewer"
			// avatars in the ticket list.
			presenceByTicket: {},
			unsubAllPresence: null
		};
	},

	computed: {
		...mapGetters("login", ["profile"]),

		isMobile() {
			return this.$vuetify.breakpoint.xsOnly;
		},

		orgOpenHeaders() {
			return [
				{ text: this.$t("ticketing.title"), value: "title" },
				{ text: this.$t("ticketing.type-label"), value: "type", width: 90 },
				{ text: this.$t("ticketing.priority-label"), value: "priority" },
				{ text: this.$t("ticketing.status-label"), value: "status" },
				{ text: this.$t("ticketing.assigned-devops"), value: "assignedDevopsIds", sortable: false },
				{ text: this.$t("ticketing.watchers"), value: "watcherIds", sortable: false },
				{ text: this.$t("ticketing.created"), value: "createdAt", width: 100 }
			];
		},

		orgClosedHeaders() {
			return [
				{ text: this.$t("ticketing.title"), value: "title" },
				{ text: this.$t("ticketing.type-label"), value: "type", width: 90 },
				{ text: this.$t("ticketing.assigned-devops"), value: "assignedDevopsIds", sortable: false },
				{ text: this.$t("ticketing.watchers"), value: "watcherIds", sortable: false },
				{ text: this.$t("ticketing.created"), value: "createdAt", width: 100 }
			];
		},

		orgTicketsForActive() {
			// `orgTicketsOrgId` lets the sidebar's per-org buttons target any of
			// the affected organizations; falls back to the ticket's primary org.
			const orgId = this.orgTicketsOrgId || this.activeTicket?.organizationId;

			if (!orgId) return [];
			// Match tickets where this org is the primary OR an additional one.
			return this.tickets.filter((t) => getTicketOrganizations(t).some((o) => o.id === orgId));
		},

		orgOpenTickets() {
			return this.orgTicketsForActive.filter((t) => ACTIVE_STATUSES.includes(t.status));
		},

		orgClosedTickets() {
			return this.orgTicketsForActive.filter((t) => !ACTIVE_STATUSES.includes(t.status));
		},

		adminContacts() {
			return this.orgContacts?.admins || [];
		},

		techContacts() {
			return this.orgContacts?.technicians || [];
		},

		contactsTotalCount() {
			return this.adminContacts.length + this.techContacts.length;
		},

		contactMethodOptions() {
			return [
				{ value: "sms", text: this.$t("ticketing.contacts.method.sms") },
				{ value: "email", text: this.$t("ticketing.contacts.method.email") },
				{ value: "call", text: this.$t("ticketing.contacts.method.call") }
			];
		},

		contactMethodOptionsWithNone() {
			return this.contactMethodOptions;
		},

		consentOptions() {
			return [
				{ value: "allowed", text: this.$t("ticketing.contacts.consent-options.allowed") },
				{ value: "always-ask", text: this.$t("ticketing.contacts.consent-options.always-ask") },
				{ value: "denied", text: this.$t("ticketing.contacts.consent-options.denied") },
				{ value: "unknown", text: this.$t("ticketing.contacts.consent-options.unknown") }
			];
		},

		phoneAvailableCountries() {
			return AVAILABLE_COUNTRIES;
		},

		// Flat de-duplicated pool of all technical contacts across all
		// organizations. Two technicians are considered identical when every
		// field except `consent` matches (after normalization). Used to
		// autocomplete the technical contact dialog.
		globalTechContacts() {
			const map = new Map();

			for (const doc of this.allOrgContactsRaw || []) {
				for (const tech of doc.technicians || []) {
					const key = this.techIdentityKey(tech);

					if (!key) continue;
					if (!map.has(key)) {
						map.set(key, {
							businessName: tech.businessName || "",
							fullName: tech.fullName || "",
							primaryMethod: tech.primaryMethod || "email",
							primaryValue: tech.primaryValue || "",
							primaryExtension: tech.primaryExtension || "",
							secondaryMethod: tech.secondaryMethod || null,
							secondaryValue: tech.secondaryValue || "",
							secondaryExtension: tech.secondaryExtension || "",
							_searchBlob: this.normalizeText(
								[
									tech.businessName,
									tech.fullName,
									tech.primaryMethod,
									tech.primaryValue,
									tech.primaryExtension,
									tech.secondaryMethod,
									tech.secondaryValue,
									tech.secondaryExtension
								].filter(Boolean).join(" ")
							)
						});
					}
				}
			}
			return Array.from(map.values());
		},

		currentUser() {
			return {
				id: this.profile?.id,
				fullName: fullName(this.profile?.name) || this.profile?.email || this.profile?.id
			};
		},

		typingLabel() {
			const names = this.activeTypers.map((t) => t.name || t.userId).filter(Boolean);

			if (!names.length) return "";
			if (names.length === 1) return this.$t("ticketing.typing-one", { name: names[0] });
			if (names.length <= 3) {
				return this.$t("ticketing.typing-many", { names: names.join(", ") });
			}
			return this.$t("ticketing.typing-several");
		},

		firstUnreadMessageId() {
			if (!this.unreadBoundaryMs) return null;
			for (const m of this.messages) {
				if (m.authorId === this.currentUser.id) continue;
				const ms = m.createdAt?.toMillis ? m.createdAt.toMillis() : 0;

				if (ms && ms > this.unreadBoundaryMs) return m.id;
			}
			return null;
		},

		conversationItems() {
			// Merge messages with logged follow-ups into a single chronological
			// timeline so follow-ups appear inline between the messages they
			// happened around. Pending writes (null serverTimestamp) sort last.
			const toMs = (ts) => this.toMillis(ts) || Number.MAX_SAFE_INTEGER;
			const messages = this.messages.map((m) => ({ ...m, kind: "message" }));
			const followUps = (this.activity || [])
				.filter((a) => a.type === "follow-up")
				.map((a) => ({ ...a, kind: "follow-up" }));

			return [...messages, ...followUps].sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
		},

		/**
		 * For each other devops user who has read the conversation at least
		 * once, attach them to the latest message whose createdAt <= their
		 * seenMs. The result is a map of messageId → [{ userId, name, seenMs }].
		 */
		readersByMessageId() {
			const out = {};

			if (!this.messages.length || !this.ticketReaders.length) return out;
			// Pre-compute messages with their ms once.
			const msgMs = this.messages.map((m) => ({
				id: m.id,
				ms: m.createdAt?.toMillis ? m.createdAt.toMillis() : 0
			}));

			this.ticketReaders.forEach((r) => {
				if (!r.userId || r.userId === this.currentUser.id) return;
				if (!r.seenMs) return;
				// Find latest message whose timestamp is <= seenMs.
				let targetId = null;

				for (let i = msgMs.length - 1; i >= 0; i -= 1) {
					if (msgMs[i].ms && msgMs[i].ms <= r.seenMs) {
						targetId = msgMs[i].id;
						break;
					}
				}
				if (!targetId) return;
				const name = this.devopsName(r.userId);

				(out[targetId] = out[targetId] || []).push({
					userId: r.userId,
					name,
					seenMs: r.seenMs
				});
			});
			return out;
		},

		typeOptions() {
			return TYPES.map((v) => ({ text: this.$t(`ticketing.type.${v}`), value: v }));
		},

		/**
		 * Unique set of devops who visited the active ticket, derived from
		 * the activity feed. Ordered by first-visit ascending. Each entry
		 * carries the last-visit timestamp (ms) for tooltip display.
		 */
		ticketSeenBy() {
			if (!this.activity?.length) return [];
			const byUser = new Map();

			this.activity.forEach((a) => {
				if (a.type !== "visited" || !a.actorId) return;
				const ms = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
				const existing = byUser.get(a.actorId);

				if (!existing) {
					byUser.set(a.actorId, {
						userId: a.actorId,
						firstMs: ms,
						lastMs: ms
					});
				} else {
					if (ms && (!existing.firstMs || ms < existing.firstMs)) existing.firstMs = ms;
					if (ms && ms > existing.lastMs) existing.lastMs = ms;
				}
			});
			return [...byUser.values()].sort((a, b) => (a.firstMs || 0) - (b.firstMs || 0));
		},

		priorityOptions() {
			return PRIORITIES.map((v) => ({ text: this.$t(`ticketing.priority.${v}`), value: v }));
		},

		isTicketClosed() {
			return ["closed", "resolved"].includes(this.activeTicket?.status);
		},

		statusOptions() {
			return STATUSES.map((v) => ({ text: this.$t(`ticketing.status.${v}`), value: v }));
		},

		followUpMethods() {
			return FOLLOW_UP_METHODS.map((value) => ({
				value,
				text: this.$t(`ticketing.follow-up-method.${value}`),
				icon: this.followUpIcon(value)
			}));
		},

		headers() {
			const isResolvedTab = this.filterTab === "resolved";

			return [
				{ text: "", value: "__indicators", sortable: false, width: 56, class: "indicators-col", cellClass: "indicators-col" },
				{ text: this.$t("ticketing.title"), value: "title", cellClass: "col-flex" },
				{ text: this.$t("ticketing.organization"), value: "organizationName", cellClass: "col-flex" },
				{ text: this.$t("ticketing.type-label"), value: "type", width: 90 },
				{ text: this.$t("ticketing.priority-label"), value: "priority", sort: this.sortPriority, width: 90 },
				{ text: this.$t("ticketing.status-label"), value: "status", class: "nowrap-header", cellClass: "nowrap-header", width: 130 },
				{ text: this.$t("ticketing.assigned-devops"), value: "assignedDevopsNames", sortable: false, width: 110 },
				{ text: this.$t("ticketing.watchers"), value: "watcherIds", sortable: false, width: 110 },
				{ text: this.$t("ticketing.last-follow-up"), value: "lastFollowUp", sort: this.sortFollowUp, width: 130 },
				{ text: this.$t("ticketing.created"), value: "createdAt", width: 100 },
				isResolvedTab
					? { text: this.$t("ticketing.resolved-closed"), value: "resolvedOrClosedAt", sort: this.sortResolvedOrClosed, width: 140, class: "nowrap-header", cellClass: "nowrap-header" }
					: { text: this.$t("ticketing.updated"), value: "updatedAt", width: 100 },
				{ text: "", value: "__spacer", sortable: false, class: "spacer-col", cellClass: "spacer-col" }
			];
		},

		counts() {
			const myId = this.currentUser.id;
			const active = this.tickets.filter((t) => ACTIVE_STATUSES.includes(t.status)).length;
			const resolved = this.tickets.length - active;
			const mine = this.tickets.filter((t) =>
				(t.assignedDevopsIds || []).includes(myId) && ACTIVE_STATUSES.includes(t.status)
			).length;
			const urgent = this.tickets.filter(
				(t) => t.priority === "urgent" && ACTIVE_STATUSES.includes(t.status)
			).length;
			const unassigned = this.tickets.filter(
				(t) => !(t.assignedDevopsIds || []).length && ACTIVE_STATUSES.includes(t.status)
			).length;
			const needsSupport = this.tickets.filter(
				(t) => t.status === "needs-support-action"
			).length;
			const mentioned = this.tickets.filter(
				(t) => (t.mentionedUserIds || []).includes(myId)
			).length;
			const watching = this.tickets.filter(
				(t) => (t.watcherIds || []).includes(myId)
			).length;
			const hardware = this.tickets.filter(
				(t) => t.type === "hardware" && ACTIVE_STATUSES.includes(t.status)
			).length;
			const porting = this.tickets.filter(
				(t) => t.type === "porting" && ACTIVE_STATUSES.includes(t.status)
			).length;

			return { active, resolved, mine, urgent, unassigned, needsSupport, mentioned, watching, hardware, porting };
		},

		mentionedHasUnseen() {
			const myId = this.currentUser.id;

			if (!myId) return false;
			return this.tickets.some(
				(t) => (t.mentionedUserIds || []).includes(myId) && this.hasUnseenMentions(t)
			);
		},

		tabsWithUnseenMentions() {
			const myId = this.currentUser.id;
			const result = {
				active: false, mine: false, mentioned: false, watching: false,
				urgent: false, unassigned: false, needsSupport: false, hardware: false, porting: false,
				resolved: false, all: false
			};

			if (!myId) return result;
			for (const t of this.tickets) {
				if (!this.hasUnseenMentions(t)) continue;
				const isActive = ACTIVE_STATUSES.includes(t.status);

				result.all = true;
				if (isActive) result.active = true;
				else result.resolved = true;
				if ((t.assignedDevopsIds || []).includes(myId)) result.mine = true;
				if ((t.mentionedUserIds || []).includes(myId)) result.mentioned = true;
				if ((t.watcherIds || []).includes(myId)) result.watching = true;
				if (t.priority === "urgent" && isActive) result.urgent = true;
				if (!(t.assignedDevopsIds || []).length && isActive) result.unassigned = true;
				if (t.status === "needs-support-action") result.needsSupport = true;
				if (t.type === "hardware" && isActive) result.hardware = true;
				if (t.type === "porting" && isActive) result.porting = true;
			}
			return result;
		},

		filteredTickets() {
			const myId = this.currentUser.id;
			let list = this.tickets;

			if (this.filterTab === "active") {
				list = list.filter((t) => ACTIVE_STATUSES.includes(t.status));
			} else if (this.filterTab === "resolved") {
				list = list.filter((t) => !ACTIVE_STATUSES.includes(t.status));
			} else if (this.filterTab === "mine") {
				list = list.filter((t) =>
					(t.assignedDevopsIds || []).includes(myId) && ACTIVE_STATUSES.includes(t.status)
				);
			} else if (this.filterTab === "urgent") {
				list = list.filter(
					(t) => t.priority === "urgent" && ACTIVE_STATUSES.includes(t.status)
				);
			} else if (this.filterTab === "unassigned") {
				list = list.filter(
					(t) => !(t.assignedDevopsIds || []).length && ACTIVE_STATUSES.includes(t.status)
				);
			} else if (this.filterTab === "needs-support") {
				list = list.filter((t) => t.status === "needs-support-action");
			} else if (this.filterTab === "mentioned") {
				list = list.filter((t) => (t.mentionedUserIds || []).includes(myId));
			} else if (this.filterTab === "watching") {
				list = list.filter((t) => (t.watcherIds || []).includes(myId));
			} else if (this.filterTab === "hardware") {
				list = list.filter((t) => t.type === "hardware" && ACTIVE_STATUSES.includes(t.status));
			} else if (this.filterTab === "porting") {
				list = list.filter((t) => t.type === "porting" && ACTIVE_STATUSES.includes(t.status));
			}
			if (this.orgFilter) {
				list = list.filter((t) => getTicketOrganizations(t).some((o) => o.id === this.orgFilter));
			}
			if (this.search) {
				const s = this.search.toLowerCase();

				list = list.filter((t) => {
					const orgNames = getTicketOrganizations(t).map((o) => o.name);

					return [t.title, t.description, t.organizationName, t.assignedUserName,
						...orgNames,
						...(t.assignedDevopsNames || [])]
						.filter(Boolean)
						.some((v) => String(v).toLowerCase().includes(s));
				});
			}
			return list;
		},

		sortedFilteredTickets() {
			const list = [...this.filteredTickets];
			const sortBy = this.sortBy;

			if (!sortBy) return list;
			const header = this.headers.find((h) => h.value === sortBy);
			const customSort = header && header.sort;
			const desc = this.sortDesc;

			list.sort((a, b) => {
				let cmp;

				if (typeof customSort === "function") {
					cmp = customSort(a, b);
				} else {
					const va = a[sortBy];
					const vb = b[sortBy];

					if (va == null && vb == null) cmp = 0;
					else if (va == null) cmp = -1;
					else if (vb == null) cmp = 1;
					else if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
					else if (va && typeof va.toMillis === "function" && vb && typeof vb.toMillis === "function") cmp = va.toMillis() - vb.toMillis();
					else cmp = String(va).localeCompare(String(vb));
				}
				return desc ? -cmp : cmp;
			});
			return list;
		},

		navIndex() {
			if (!this.activeTicketId || !this.navTicketIds.length) return -1;
			return this.navTicketIds.indexOf(this.activeTicketId);
		},

		hasPrevTicket() {
			return this.navIndex > 0;
		},

		hasNextTicket() {
			return this.navIndex >= 0 && this.navIndex < this.navTicketIds.length - 1;
		},

		currentNavFilterLabel() {
			return this.navFilterTab ? this.filterTabLabel(this.navFilterTab) : "";
		},

		orgFilterItems() {
			return this.organizations.map((o) => ({
				text: this.organizationDisplayName(o.name),
				value: o.id
			}));
		},

		organizationItems() {
			return this.orgFilterItems;
		},

		additionalOrgPickerItems() {
			// Exclude the primary org and any already-added additional orgs so the
			// "add another organization" picker only lists choices that aren't yet
			// linked to this ticket.
			const taken = new Set([
				this.form.organizationId,
				...this.form.additionalOrgs.map((o) => o.id)
			].filter(Boolean));

			return this.organizationItems.filter((it) => !taken.has(it.value));
		},

		activeTicketOrganizations() {
			if (!this.activeTicket) return [];
			return getTicketOrganizations(this.activeTicket);
		},

		perOrgFollowUps() {
			if (!this.activeTicket) return [];
			return this.ticketFollowUpEntries(this.activeTicket);
		},

		lastStatusEvent() {
			if (!this.activeTicket || !Array.isArray(this.activity)) return null;
			for (let i = this.activity.length - 1; i >= 0; i--) {
				const a = this.activity[i];

				if (a.field !== "status") continue;
				const to = a.toValue;
				const from = a.fromValue;
				let kind = null;

				if (to === "resolved") kind = "resolved";
				else if (to === "closed") kind = "closed";
				else if (ACTIVE_STATUSES.includes(to) && ["closed", "resolved"].includes(from)) kind = "reopened";
				if (!kind) continue;
				return {
					kind,
					at: a.createdAt,
					actorName: a.actorName || null
				};
			}
			return null;
		},

		expandedAffectedOrgId() {
			const entry = Object.entries(this.expandedAffectedOrgs || {}).find(([, v]) => v);

			return entry ? entry[0] : null;
		},

		visibleAffectedOrganizations() {
			// While one org is expanded, hide the others so its details can
			// render full-height without competing for sidebar space.
			const expandedId = this.expandedAffectedOrgId;

			if (!expandedId) return this.activeTicketOrganizations;
			return this.activeTicketOrganizations.filter((o) => o.id === expandedId);
		},

		affectedOrgPickerItems() {
			const taken = new Set(this.activeTicketOrganizations.map((o) => o.id).filter(Boolean));

			return this.organizationItems.filter((it) => !taken.has(it.value));
		},

		unreadCount() {
			return this.notifications.filter((n) => !n.read).length;
		},

		isWatching() {
			return !!this.activeTicket?.watcherIds?.includes(this.currentUser.id);
		},

		activeOrganizationPhone() {
			const orgId = this.activeTicket?.organizationId;

			if (!orgId) return "";
			const org = this.organizations.find((o) => o.id === orgId);

			return org?.phone || "";
		},

		activeOrganizationLogo() {
			const orgId = this.activeTicket?.organizationId;

			if (!orgId) return "";
			const org = this.organizations.find((o) => o.id === orgId);

			return org?.logo || "";
		},

		canTakeDevops() {
			if (!this.activeTicket || !this.currentUser?.id) return false;
			const ids = this.activeTicket.assignedDevopsIds || [];
			// Hide if I'm already the sole assignee

			if (ids.length === 1 && ids[0] === this.currentUser.id) return false;
			return true;
		},

		isStealDevops() {
			return (this.activeTicket?.assignedDevopsIds || []).length > 0;
		},

		takeDevopsLabel() {
			return this.isStealDevops
				? this.$t("ticketing.steal-devops")
				: this.$t("ticketing.take-devops");
		},

		takeDevopsIcon() {
			return "mdi-hand-back-right";
		},

		activeUserEmail() {
			if (!this.activeTicket) return "";
			if (this.activeTicket.assignedUserEmail) return this.activeTicket.assignedUserEmail;
			const uid = this.activeTicket.assignedUserId;

			if (!uid) return "";
			return this.orgUsers.find((u) => u.id === uid)?.email || "";
		},

		activeUserRole() {
			if (!this.activeTicket) return "";
			const uid = this.activeTicket.assignedUserId;

			if (!uid) return "";
			return this.orgUsers.find((u) => u.id === uid)?.role || "";
		},

		filteredMentionCandidates() {
			const q = (this.mentionQuery || "").toLowerCase();

			return this.devopsUsers
				.filter((u) => u.fullName.toLowerCase().includes(q))
				.slice(0, 8);
		},

		impersonatedClinicId() {
			return this.profile?.clinic;
		}
	},

	watch: {
		openTicketId() {
			this.tryOpenPendingTicket();
		},

		activeTicketOrganizations: {
			immediate: true,

			handler(orgs) {
				if (!Array.isArray(orgs)) return;
				orgs.forEach((o) => {
					if (o?.id && o.assignedUserId && !this.viewOrgUsers[o.id]) {
						this.loadViewOrgUsers(o.id).catch(() => {});
					}
				});
			}
		},

		detailTab(val) {
			if (val === "activity") {
				this.$nextTick(() => this.scrollActivityToBottom());
			}
			if (val === "conversation") {
				this.markConversationSeenNow();
				this.scheduleUnreadBoundaryClear();
				// If the messages loaded while another tab was showing, the initial
				// positioning couldn't run yet — do it now that the conversation
				// list is in the DOM.
				if (!this.didInitialConversationScroll) {
					this.scrollMessagesToInitial();
				}
			} else {
				// User left the conversation tab. Only drop the divider if they
				// spent at least 5s on the tab; otherwise keep it for next visit.
				this.clearUnreadBoundaryTimer();
				if (this.unreadBoundaryEligibleToClear) {
					this.unreadBoundaryMs = 0;
					this.unreadBoundaryEligibleToClear = false;
				}
			}
		},

		messages() {
			// If new messages stream in while the user is sitting on the
			// Conversation tab, keep their "seen" marker fresh.
			if (this.detailTab === "conversation") {
				this.markConversationSeenNow();
			}
		},

		activity() {
			if (this.detailTab === "activity") {
				this.$nextTick(() => this.scrollActivityToBottom());
			}
		}
	},

	async mounted() {
		try {
			// Make sure Firebase Auth is ready before issuing Firestore queries;
			// right after login `connected` flips true before signInEvents resolves.
			await waitForEventsAuth();

			const [orgRes, devopsRes] = await Promise.all([
				this.axios.get("/devops/organizations"),
				this.axios.get("/devops/ticketing/devops-users")
			]);

			this.organizations = orgRes.data || [];
			this.devopsUsers = devopsRes.data || [];

			this.unsubTickets = subscribeTickets((tickets) => {
				this.tickets = tickets;
				this.loading = false;
				this.tryOpenPendingTicket();
			}, () => {
				this.loading = false;
				this.$error?.(this.$t("ticketing.load-error"));
			});

			this.unsubAllPresence = subscribeAllPresence((byTicket) => {
				this.presenceByTicket = byTicket;
			});

			this.unsubAllOrgContacts = subscribeAllOrgContacts((docs) => {
				this.allOrgContactsRaw = docs;
			});

			if (this.currentUser.id) {
				this.unsubNotifications = subscribeNotifications(
					this.currentUser.id,
					(items) => { this.notifications = items; }
				);

				this.unsubVisits = subscribeVisits(
					this.currentUser.id,
					({ tickets, conversations }) => {
						// Merge rather than replace so optimistic local timestamps
						// (and pending serverTimestamp() writes that snapshot as null)
						// don't briefly blank out and cause the unread dots to flash.
						this.lastSeen = this.mergeVisits(this.lastSeen, tickets);
						this.lastConversationSeen = this.mergeVisits(this.lastConversationSeen, conversations);
					}
				);
			}
		} catch (err) {
			console.error("[TICKETING] init failed", err);
			this.loading = false;
			this.$error?.(this.$t("ticketing.init-error"));
		}
	},

	beforeDestroy() {
		this.unsubTickets?.();
		this.unsubTicket?.();
		this.unsubMessages?.();
		this.unsubNotifications?.();
		this.unsubVisits?.();
		this.unsubActivity?.();
		this.unsubTodos?.();
		this.unsubPresence?.();
		this.leavePresence?.();
		this.unsubTyping?.();
		this.unsubTicketReaders?.();
		this.unsubAllPresence?.();
		this.unsubOrgContacts?.();
		this.unsubAllOrgContacts?.();
		this.stopTypingBeat();
		this.clearUnreadBoundaryTimer();
	},

	methods: {
		...mapActions("login", ["logout"]),

		emptyForm() {
			return {
				title: "",
				description: "",
				attachments: [],
				organizationId: this.profile?.clinic ?? null,
				organizationName: "",
				assignedUserId: null,
				assignedUserName: "",
				assignedUserEmail: "",
				additionalOrgs: [],
				type: "request",
				priority: "medium",
				status: "open",
				assignedDevopsIds: [],
				assignedDevopsNames: [],
				watcherIds: []
			};
		},

		organizationDisplayName(name) {
			if (!name) return "";
			if (typeof name === "string") return name;
			const [locale] = Object.keys(name);

			return name[locale] ?? "";
		},

		normalizeText(text) {
			return String(text ?? "")
				.toLowerCase()
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
		},

		filterOrganization(item, queryText) {
			const q = this.normalizeText(queryText);

			return (
				this.normalizeText(item.text).includes(q) ||
				this.normalizeText(item.value).includes(q)
			);
		},

		filterUser(item, queryText) {
			const q = this.normalizeText(queryText);

			return (
				this.normalizeText(item.fullName).includes(q) ||
				this.normalizeText(item.email).includes(q) ||
				this.normalizeText(item.id).includes(q)
			);
		},

		statusColor(status) {
			return {
				"open": "blue",
				"waiting-on-customer": "orange",
				"waiting-on-technician": "brown",
				"waiting-on-vendor": "amber darken-2",
				"needs-support-action": "purple",
				"in-progress": "deep-purple",
				"qa": "teal",
				"awaiting-deploy": "red",
				"closed": "grey",
				"resolved": "green",
				// legacy
				"pending-customer": "orange",
				"pending-support": "purple",
				"pending-devops": "deep-purple",
				"pending-hotfix": "red"
			}[status] || "grey";
		},

		statusHex(status) {
			return {
				"open": "#1976d2",
				"waiting-on-customer": "#fb8c00",
				"waiting-on-technician": "#795548",
				"waiting-on-vendor": "#c0850b",
				"needs-support-action": "#9c27b0",
				"in-progress": "#673ab7",
				"qa": "#009688",
				"awaiting-deploy": "#e53935",
				"closed": "#757575",
				"resolved": "#43a047",
				"pending-customer": "#fb8c00",
				"pending-support": "#9c27b0",
				"pending-devops": "#673ab7",
				"pending-hotfix": "#e53935"
			}[status] || "#757575";
		},

		statusEmoji(status) {
			return {
				"open": "🟢",
				"waiting-on-customer": "⏳",
				"waiting-on-technician": "🔧",
				"waiting-on-vendor": "🏢",
				"needs-support-action": "🛟",
				"in-progress": "🚧",
				"qa": "🧪",
				"awaiting-deploy": "🚀",
				"closed": "🔴",
				"resolved": "✅",
				"pending-customer": "⏳",
				"pending-support": "🛟",
				"pending-devops": "🚧",
				"pending-hotfix": "🚒"
			}[status] || "•";
		},

		priorityColor(priority) {
			return {
				"low": "blue",
				"medium": "amber",
				"high": "orange",
				"urgent": "red"
			}[priority] || "grey";
		},

		priorityHex(priority) {
			return {
				"low": "#1976d2",
				"medium": "#fbc02d",
				"high": "#fb8c00",
				"urgent": "#e53935"
			}[priority] || "#bdbdbd";
		},

		priorityEmoji(priority) {
			return {
				"low": "▼",
				"medium": "▬",
				"high": "▲",
				"urgent": "🚨"
			}[priority] || "•";
		},

		viewerInitials(name) {
			const parts = (name || "").trim().split(/\s+/).filter(Boolean);

			if (!parts.length) return "?";
			if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		},

		/**
		 * Returns the list of devops users (excluding the current one) currently
		 * viewing the given ticket, sorted by most-recent heartbeat. Used to
		 * render the live presence indicator on each row of the ticket list.
		 */
		liveViewersForTicket(ticket) {
			if (!ticket) return [];
			const viewers = this.presenceByTicket[ticket.id] || [];

			return viewers
				.filter((v) => v.userId && v.userId !== this.currentUser.id)
				.sort((a, b) => (b.at || 0) - (a.at || 0));
		},

		/**
		 * Deterministic per-user color so each devops user has a stable,
		 * recognizable avatar tint across viewers, message authors and
		 * read-receipt indicators. Uses HSL with a hashed hue so the space
		 * is effectively continuous (~32k+ distinct shades) while keeping
		 * saturation/lightness in a readable range against white text.
		 */
		userColor(seed) {
			const s = String(seed || "");
			// FNV-1a 32-bit hash → uniform spread across the hue circle.
			let hash = 0x811c9dc5;

			for (let i = 0; i < s.length; i += 1) {
				hash ^= s.charCodeAt(i);
				hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) | 0;
			}
			const u = (hash >>> 0);
			// 15 bits of hue → 32768 distinct hues; small jitter on S/L for variety.
			const hue = (u & 0x7fff) * (360 / 0x8000);
			const sat = 55 + ((u >>> 15) & 0x1f); // 55–86%
			const light = 38 + ((u >>> 20) & 0x0f); // 38–53%

			return `hsl(${hue.toFixed(2)}, ${sat}%, ${light}%)`;
		},

		sortPriority(a, b) {
			const order = { low: 0, medium: 1, high: 2, urgent: 3 };

			return (order[a] ?? -1) - (order[b] ?? -1);
		},

		sortFollowUp(a, b) {
			const toMs = (v) => {
				if (!v?.at) return 0;
				if (v.at.toMillis) return v.at.toMillis();
				if (v.at instanceof Date) return v.at.getTime();
				return Number(v.at) || 0;
			};

			return toMs(a) - toMs(b);
		},

		resolvedOrClosedAt(ticket) {
			if (!ticket) return null;
			const toMs = (v) => {
				if (!v) return 0;
				if (v.toMillis) return v.toMillis();
				if (v instanceof Date) return v.getTime();
				return Number(v) || 0;
			};
			const r = ticket.resolvedAt;
			const c = ticket.closedAt;

			if (r && c) return toMs(r) >= toMs(c) ? r : c;
			return r || c || null;
		},

		resolvedOrClosedTooltip(ticket) {
			const ts = this.resolvedOrClosedAt(ticket);

			if (!ts) return "";
			const kind = ts === ticket.resolvedAt
				? this.$t("ticketing.status-event.resolved")
				: this.$t("ticketing.status-event.closed");

			return `${kind} · ${this.formatTimeAbsolute(ts)}`;
		},

		sortResolvedOrClosed(a, b) {
			const toMs = (v) => {
				if (!v) return 0;
				if (v.toMillis) return v.toMillis();
				if (v instanceof Date) return v.getTime();
				return Number(v) || 0;
			};

			return toMs(a) - toMs(b);
		},

		ticketFollowUpEntries(ticket) {
			if (!ticket) return [];
			const orgs = getTicketOrganizations(ticket);
			const byOrg = ticket.followUpsByOrg || {};

			if (!orgs.length) {
				// No org info — fall back to legacy single lastFollowUp entry.
				return [{
					org: { id: null, name: ticket.organizationName || "" },
					lastFollowUp: ticket.lastFollowUp || null
				}];
			}

			return orgs.map((org) => {
				let entry = org.id ? byOrg[org.id] : null;

				// Legacy fallback: if no per-org entry but this ticket has the
				// pre-multi-org `lastFollowUp`, attribute it to its primary org.
				if (!entry && orgs.length === 1) entry = ticket.lastFollowUp || null;
				return { org, lastFollowUp: entry || null };
			});
		},

		ticketFollowUpDisplay(ticket) {
			// Pick the "worst" entry to drive the compact column display:
			// any org never followed up → never; otherwise the oldest follow-up.
			const entries = this.ticketFollowUpEntries(ticket);

			if (!entries.length) return { lastFollowUp: null, isStale: false };

			const never = entries.find((e) => !e.lastFollowUp);

			if (never) return { lastFollowUp: null, isStale: true };

			let oldest = entries[0];

			for (const e of entries) {
				if (this.toMillis(e.lastFollowUp.at) < this.toMillis(oldest.lastFollowUp.at)) {
					oldest = e;
				}
			}
			return {
				lastFollowUp: oldest.lastFollowUp,
				isStale: this.isFollowUpStale(oldest.lastFollowUp.at)
			};
		},

		ticketFollowUpFollowedCount(ticket) {
			return this.ticketFollowUpEntries(ticket).filter((e) => !!e.lastFollowUp).length;
		},

		activeTicketOrgFollowUp(orgId) {
			if (!this.activeTicket) return null;
			const byOrg = this.activeTicket.followUpsByOrg || {};

			if (orgId && byOrg[orgId]) return byOrg[orgId];
			// Legacy fallback: single-org ticket uses the top-level lastFollowUp.
			if (this.activeTicketOrganizations.length === 1) {
				return this.activeTicket.lastFollowUp || null;
			}
			return null;
		},

		followUpTooltip(entry) {
			if (!entry) return "";
			const parts = [];
			const contactLabel = this.followUpContactLabel(entry);

			if (contactLabel) parts.push(contactLabel);
			parts.push(this.$t("ticketing.follow-up-method." + entry.method));
			parts.push(this.formatTimeAbsolute(entry.at));
			if (entry.byUserName) parts.push(this.$t("ticketing.follow-up-by", { name: entry.byUserName }));
			return parts.join(" · ");
		},

		followUpContactLabel(entry) {
			if (!entry) return "";
			if (entry.contactKind) return this.$t("ticketing.follow-up-contact." + entry.contactKind);
			return entry.contactName || "";
		},

		// Label for a follow-up shown inline in the conversation. Prefers the
		// concrete contact name, falling back to the contact-kind label.
		followUpItemContactLabel(item) {
			if (!item) return "";
			if (item.contactName) return item.contactName;
			if (item.contactKind) return this.$t("ticketing.follow-up-contact." + item.contactKind);
			return "";
		},

		previousFollowUpsForOrg(orgId) {
			if (!Array.isArray(this.activity)) return [];
			const entries = [];

			for (let i = this.activity.length - 1; i >= 0; i--) {
				const a = this.activity[i];

				if (a.type !== "follow-up") continue;
				const aOrgId = a.organizationId ?? null;
				const matchOrg = orgId ? aOrgId === orgId : (aOrgId == null);

				if (!matchOrg) continue;
				entries.push({
					method: a.toValue,
					at: a.createdAt,
					byUserName: a.actorName ?? null,
					contactKind: a.contactKind ?? null,
					contactName: a.contactName ?? null
				});
			}
			// Skip the most-recent one (already shown) and return up to 5 prior.
			return entries.slice(1, 6);
		},

		togglePrevFollowUps(orgId, e) {
			if (e) e.stopPropagation();
			const key = orgId || "_";

			this.$set(this.expandedPrevFollowUpsByOrg, key, !this.expandedPrevFollowUpsByOrg[key]);
		},

		collapsePrevFollowUps(orgId) {
			const key = orgId || "_";

			if (this.expandedPrevFollowUpsByOrg[key]) {
				this.$set(this.expandedPrevFollowUpsByOrg, key, false);
			}
		},

		isMessagesAtBottom() {
			const el = this.$refs.messagesList;

			if (!el) return true;
			return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
		},

		scrollActivityToBottom() {
			const run = () => {
				const el = this.$refs.activityList;

				if (!el) return false;
				el.scrollTop = el.scrollHeight;
				return true;
			};

			this.$nextTick(() => {
				if (!run()) setTimeout(run, 50);
			});
		},

		scrollMessagesToBottom() {
			const run = () => {
				const el = this.$refs.messagesList;

				if (!el) return false;
				this.programmaticScrollAt = Date.now();
				el.scrollTop = el.scrollHeight;
				return true;
			};

			this.$nextTick(() => {
				if (!run()) {
					setTimeout(run, 50);
					setTimeout(run, 200);
				} else {
					setTimeout(run, 50);
				}
			});
		},

		/**
		 * Anchors the conversation so the latest read message sits at the top,
		 * the unread divider just below it, then the unread messages. Clamped to
		 * the bottom so a short unread set still shows the divider and as many
		 * read messages as fit. Returns false until the divider is in the DOM.
		 */
		applyUnreadScroll() {
			const el = this.$refs.messagesList;

			if (!el) return false;
			const divider = el.querySelector(".unread-divider");

			if (!divider) return false;
			// The latest read message sits right above the divider; pin its top to
			// the top of the list, but never scroll past the bottom.
			const target = divider.previousElementSibling || divider;
			const offset = (target.getBoundingClientRect().top - el.getBoundingClientRect().top) + el.scrollTop;
			const maxTop = el.scrollHeight - el.clientHeight;

			this.programmaticScrollAt = Date.now();
			el.scrollTop = Math.max(0, Math.min(offset, maxTop));
			return true;
		},

		scrollMessagesToInitial() {
			const place = () => {
				const el = this.$refs.messagesList;

				if (!el) return false;

				if (this.firstUnreadMessageId) {
					// Wait for the divider to render before computing offsets.
					if (!this.applyUnreadScroll()) return false;
					// Keep the divider anchored while images finish loading; released
					// on the first genuine user scroll.
					this.unreadPinActive = true;
				} else {
					this.unreadPinActive = false;
					this.programmaticScrollAt = Date.now();
					el.scrollTop = el.scrollHeight;
				}
				this.didInitialConversationScroll = true;
				return true;
			};
			// Re-apply across a few frames to defeat late layout shifts (e.g.
			// images expanding), but only while the anchor is still active so we
			// never yank back a user who has already scrolled.
			const reapply = () => {
				if (this.unreadPinActive) this.applyUnreadScroll();
			};

			this.$nextTick(() => {
				if (!place()) {
					setTimeout(place, 50);
					setTimeout(place, 200);
					setTimeout(place, 500);
				} else {
					setTimeout(reapply, 50);
					setTimeout(reapply, 200);
				}
			});
		},

		onMessageImageLoad() {
			const el = this.$refs.messagesList;

			if (!el) return;
			// While the initial unread anchor is active, keep the divider pinned
			// as images expand the list rather than snapping to the bottom.
			if (this.unreadPinActive) {
				this.applyUnreadScroll();
				return;
			}
			// Use a generous threshold so that the scrollHeight growth from a
			// just-loaded image (which itself pushed the bottom away) still counts
			// as "user was at the bottom".
			const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

			if (distanceFromBottom < 800 || el.scrollTop === 0) {
				this.scrollMessagesToBottom();
			}
		},

		onMessagesScroll() {
			// A user-initiated scroll (anything not caused by our own recent
			// programmatic scroll) releases the initial unread anchor.
			if (this.unreadPinActive && Date.now() - this.programmaticScrollAt > 150) {
				this.unreadPinActive = false;
			}
			if (this.unreadMessageCount && this.isMessagesAtBottom()) {
				this.unreadMessageCount = 0;
			}
		},

		releaseUnreadPin() {
			this.unreadPinActive = false;
		},

		onUnreadBannerClick() {
			this.unreadPinActive = false;
			this.unreadMessageCount = 0;
			this.scrollMessagesToBottom();
		},

		reactionList(message) {
			const reactions = message?.reactions ?? {};
			const myId = this.currentUser.id;

			return Object.keys(reactions)
				.map((emoji) => {
					const users = reactions[emoji] ?? {};
					const userIds = Object.keys(users);

					return {
						emoji,
						count: userIds.length,
						mine: !!users[myId],
						names: userIds.map((uid) => users[uid]?.name || uid)
					};
				})
				.filter((r) => r.count > 0)
				.sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
		},

		async onToggleReaction(item, emoji) {
			if (!this.activeTicket) return;
			try {
				if (item.kind === "follow-up") {
					await toggleActivityReaction(this.activeTicket.id, item.id, emoji, this.currentUser);
				} else {
					await toggleReaction(this.activeTicket.id, item.id, emoji, this.currentUser);
				}
			} catch (err) {
				console.warn("[TICKETING] toggleReaction failed", err);
			}
		},

		notificationIcon(type) {
			return {
				"ticket_created": "mdi-ticket-outline",
				"ticket_updated": "mdi-pencil-outline",
				"new_message": "mdi-message-text-outline",
				"mention": "mdi-at",
				"reaction": "mdi-emoticon-happy-outline",
				"added_as_watcher": "mdi-eye-plus-outline",
				"removed_as_watcher": "mdi-eye-minus-outline",
				"watcher_added": "mdi-eye-plus-outline",
				"watcher_removed": "mdi-eye-minus-outline",
				"attachments_added": "mdi-paperclip-plus",
				"attachments_removed": "mdi-paperclip-minus",
				"todo_added": "mdi-checkbox-marked-circle-plus-outline",
				"todo_edited": "mdi-pencil-outline",
				"todo_completed": "mdi-checkbox-marked-circle",
				"todo_uncompleted": "mdi-checkbox-blank-circle-outline",
				"todo_deleted": "mdi-delete-outline",
				"org_added": "mdi-domain-plus",
				"org_removed": "mdi-domain-remove",
				"org_contact_updated": "mdi-account-switch-outline",
				"ticket_unassigned": "mdi-account-question-outline",
				"follow_up_logged": "mdi-phone-log-outline"
			}[type] || "mdi-bell-outline";
		},

		notificationColor(type) {
			return {
				"ticket_created": "success",
				"ticket_updated": "orange",
				"new_message": "primary",
				"mention": "deep-purple",
				"reaction": "green",
				"added_as_watcher": "teal",
				"removed_as_watcher": "red",
				"watcher_added": "teal",
				"watcher_removed": "red",
				"attachments_added": "green",
				"attachments_removed": "red",
				"todo_added": "green",
				"todo_edited": "primary",
				"todo_completed": "green",
				"todo_uncompleted": "grey",
				"todo_deleted": "red",
				"org_added": "green",
				"org_removed": "red",
				"org_contact_updated": "primary",
				"ticket_unassigned": "orange",
				"follow_up_logged": "primary"
			}[type] || "grey";
		},

		activityIcon(a) {
			if (a.type === "created") return "mdi-plus-circle-outline";
			if (a.type === "visited") return "mdi-eye-outline";
			if (a.type === "follow-up") return this.followUpIcon(a.toValue);
			if (a.type === "attachments-added") return "mdi-paperclip-plus";
			if (a.type === "attachments-removed") return "mdi-paperclip-minus";
			if (a.type === "watcher-added") return "mdi-eye-plus-outline";
			if (a.type === "watcher-removed") return "mdi-eye-minus-outline";
			if (a.type === "org-added") return "mdi-domain-plus";
			if (a.type === "org-removed") return "mdi-domain-remove";
			if (a.type === "org-contact-updated") return "mdi-account-switch-outline";
			if (a.type === "todo-added") return "mdi-checkbox-marked-circle-plus-outline";
			if (a.type === "todo-edited") return "mdi-pencil-outline";
			if (a.type === "todo-completed") return "mdi-checkbox-marked-circle";
			if (a.type === "todo-uncompleted") return "mdi-checkbox-blank-circle-outline";
			if (a.type === "todo-deleted") return "mdi-delete-outline";
			return {
				"title": "mdi-format-title",
				"description": "mdi-text-box-outline",
				"status": "mdi-progress-check",
				"priority": "mdi-flag-outline",
				"type": "mdi-shape-outline",
				"organization": "mdi-domain",
				"assigned-user": "mdi-account-outline",
				"assigned-devops": "mdi-account-cog-outline",
				"follow-up": "mdi-phone-log-outline"
			}[a.field] || "mdi-pencil-outline";
		},

		activityIconColor(a) {
			if (a.type === "created") return "green";
			if (a.type === "visited") return "grey";
			if (a.type === "follow-up") return "teal";
			if (a.type === "attachments-added") return "green";
			if (a.type === "attachments-removed") return "red";
			if (a.type === "watcher-added") return "teal";
			if (a.type === "watcher-removed") return "red";
			if (a.type === "todo-added") return "green";
			if (a.type === "todo-edited") return "primary";
			if (a.type === "todo-completed") return "green";
			if (a.type === "todo-uncompleted") return "grey";
			if (a.type === "todo-deleted") return "red";
			return "primary";
		},

		formatTime(ts) {
			if (!ts) return "";
			let date;

			if (ts.toDate) date = ts.toDate();
			else if (typeof ts === "number") date = new Date(ts);
			else if (typeof ts === "string") date = new Date(ts);
			else if (ts instanceof Date) date = ts;
			else return "";
			return moment(date).locale(this.$i18n.locale).fromNow();
		},

		toMillis(ts) {
			if (!ts) return 0;
			if (ts.toDate) return ts.toDate().getTime();
			if (typeof ts === "number") return ts;
			if (typeof ts === "string") return new Date(ts).getTime();
			if (ts instanceof Date) return ts.getTime();
			return 0;
		},

		isFollowUpStale(ts) {
			const ms = this.toMillis(ts);

			if (!ms) return false;
			return Date.now() - ms > 24 * 60 * 60 * 1000;
		},

		formatTimeAbsolute(ts) {
			if (!ts) return "";
			let date;

			if (ts.toDate) date = ts.toDate();
			else if (typeof ts === "number") date = new Date(ts);
			else if (typeof ts === "string") date = new Date(ts);
			else if (ts instanceof Date) date = ts;
			else return "";
			return moment(date).locale(this.$i18n.locale).format("LLLL");
		},

		initials(name) {
			if (!name) return "?";
			return name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
		},

		devopsName(userId) {
			return this.devopsUsers.find((u) => u.id === userId)?.fullName || userId;
		},

		organizationLogo(orgId) {
			if (!orgId) return "";
			return this.organizations.find((o) => o.id === orgId)?.logo || "";
		},

		rowClass(item) {
			return this.hasUnseenMentions(item) ? "ticket-row--mention" : "";
		},

		hasUnreadFor(ticketId) {
			const seen = this.lastSeen[ticketId];
			const t = this.tickets.find((x) => x.id === ticketId);

			if (!t || !t.updatedAt) return false;
			const updatedMs = t.updatedAt.toMillis ? t.updatedAt.toMillis() : 0;

			if (!updatedMs) return false;
			// Don't flag tickets the current user just updated themselves as unread for them.
			return !seen || seen < updatedMs;
		},

		ticketLastMessageMs(ticket) {
			const v = ticket?.lastMessageAt;

			if (!v) return 0;
			return v.toMillis ? v.toMillis() : Number(v) || 0;
		},

		ticketLastMentionMs(ticket, userId) {
			const v = ticket?.lastMentionByUser?.[userId];

			if (!v) return 0;
			return v.toMillis ? v.toMillis() : Number(v) || 0;
		},

		hasUnseenMessages(ticket) {
			if (!ticket) return false;
			const lastMessageMs = this.ticketLastMessageMs(ticket);

			if (!lastMessageMs) return false;
			const seenMs = this.lastConversationSeen[ticket.id] || 0;

			return seenMs < lastMessageMs;
		},

		hasUnseenMentions(ticket) {
			if (!ticket || !this.currentUser.id) return false;
			const lastMentionMs = this.ticketLastMentionMs(ticket, this.currentUser.id);

			if (!lastMentionMs) return false;
			const seenMs = this.lastConversationSeen[ticket.id] || 0;

			return seenMs < lastMentionMs;
		},

		markConversationSeenNow() {
			if (!this.activeTicketId || !this.currentUser.id) return;
			// Optimistically bump the local seen timestamp so the unread dots
			// don't briefly flash while Firestore's serverTimestamp() pending
			// snapshot reports a null value.
			this.$set(this.lastConversationSeen, this.activeTicketId, Date.now());
			markConversationSeen(this.activeTicketId, this.currentUser.id).catch((err) =>
				console.warn("[TICKETING] markConversationSeen failed", err)
			);
		},

		scheduleUnreadBoundaryClear() {
			this.clearUnreadBoundaryTimer();
			if (!this.unreadBoundaryMs) return;
			// After 5s on the conversation tab, mark the divider as eligible to be
			// cleared. It will actually disappear when the user switches tab or
			// closes the ticket.
			this.unreadBoundaryTimer = setTimeout(() => {
				this.unreadBoundaryEligibleToClear = true;
				this.unreadBoundaryTimer = null;
			}, 5000);
		},

		clearUnreadBoundaryTimer() {
			if (this.unreadBoundaryTimer) {
				clearTimeout(this.unreadBoundaryTimer);
				this.unreadBoundaryTimer = null;
			}
		},

		normalizeVisits(map) {
			const out = {};

			Object.keys(map ?? {}).forEach((k) => {
				const v = map[k];

				if (!v) return;
				out[k] = v.toMillis ? v.toMillis() : Number(v) || 0;
			});
			return out;
		},

		mergeVisits(prev, incoming) {
			// Keep any previously-known timestamp (including optimistic local
			// values) when the incoming snapshot has a pending serverTimestamp()
			// represented as null.
			const out = { ...(prev || {}) };

			Object.keys(incoming ?? {}).forEach((k) => {
				const v = incoming[k];

				if (!v) return; // pending → keep existing optimistic value
				const ms = v.toMillis ? v.toMillis() : Number(v) || 0;

				if (ms) out[k] = ms;
			});
			return out;
		},

		// --- Quick add / Edit ---

		// --- Todos ---

		async submitNewTodo() {
			const text = (this.newTodoText || "").trim();

			if (!text || !this.activeTicketId) return;
			this.newTodoText = "";
			try {
				await addTodo(this.activeTicketId, text, this.currentUser);
			} catch (err) {
				console.error("[TICKETING] addTodo failed", err);
				this.newTodoText = text;
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		startEditTodo(todo) {
			if (!todo || todo.completed) return;
			this.editingTodoId = todo.id;
			this.editingTodoText = todo.text;
		},

		cancelEditTodo() {
			this.editingTodoId = null;
			this.editingTodoText = "";
		},

		async saveEditTodo(todo) {
			const text = (this.editingTodoText || "").trim();
			const wasEditingId = this.editingTodoId;

			this.editingTodoId = null;
			this.editingTodoText = "";
			if (!text || !todo || todo.completed) return;
			if (text === todo.text) return;
			try {
				await updateTodoText(this.activeTicketId, todo.id, text, this.currentUser);
			} catch (err) {
				console.error("[TICKETING] updateTodoText failed", err);
				this.editingTodoId = wasEditingId;
				this.editingTodoText = text;
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async toggleTodo(todo) {
			if (!todo) return;
			try {
				await setTodoCompleted(this.activeTicketId, todo.id, !todo.completed, this.currentUser);
			} catch (err) {
				console.error("[TICKETING] setTodoCompleted failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async removeTodo(todo) {
			if (!todo || todo.completed) return;
			try {
				await deleteTodo(this.activeTicketId, todo.id, this.currentUser);
			} catch (err) {
				console.error("[TICKETING] deleteTodo failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async shareTicket(ticket) {
			if (!ticket?.id) return;
			const { origin, pathname } = window.location;
			const url = `${origin}${pathname}?ticket=${encodeURIComponent(ticket.id)}`;

			try {
				if (navigator.clipboard?.writeText) {
					await navigator.clipboard.writeText(url);
				} else {
					const ta = document.createElement("textarea");

					ta.value = url;
					ta.style.position = "fixed";
					ta.style.opacity = "0";
					document.body.appendChild(ta);
					ta.select();
					document.execCommand("copy");
					document.body.removeChild(ta);
				}
				this.$success?.(this.$t("ticketing.share-copied"));
			} catch (err) {
				console.error("[TICKETING] share copy failed", err);
				this.$error?.(this.$t("ticketing.share-error"));
			}
		},

		async openQuickAdd(ticket = null) {
			if (ticket) {
				this.editingId = ticket.id;
				// Split the (normalized) organizations list into primary + extras.
				// Legacy single-org tickets only have a primary entry.
				const allOrgs = getTicketOrganizations(ticket);
				const primaryId = ticket.organizationId
					?? allOrgs[0]?.id
					?? null;
				const primary = allOrgs.find((o) => o.id === primaryId) ?? allOrgs[0] ?? null;
				const additional = allOrgs
					.filter((o) => o.id && o.id !== primaryId)
					.map((o) => ({ ...o, usersLoading: false, users: []}));

				this.form = {
					title: ticket.title,
					description: ticket.description ?? "",
					attachments: [...(ticket.attachments || [])],
					organizationId: primary?.id ?? ticket.organizationId,
					organizationName: primary?.name ?? ticket.organizationName,
					assignedUserId: primary?.assignedUserId ?? ticket.assignedUserId,
					assignedUserName: primary?.assignedUserName ?? ticket.assignedUserName,
					assignedUserEmail: primary?.assignedUserEmail ?? ticket.assignedUserEmail ?? "",
					additionalOrgs: additional,
					type: ticket.type,
					priority: ticket.priority,
					status: ticket.status,
					assignedDevopsIds: [...(ticket.assignedDevopsIds || [])],
					assignedDevopsNames: [...(ticket.assignedDevopsNames || [])],
					watcherIds: [...(ticket.watcherIds || [])]
				};
				if (this.form.organizationId) {
					await this.loadOrgUsers(this.form.organizationId);
				}
				// Lazily load users for each additional org so the inline
				// contact pickers work immediately.
				additional.forEach((o) => {
					if (o.id) this.loadAdditionalOrgUsers(o.id).catch(() => {});
				});
			} else {
				this.editingId = null;
				this.form = this.emptyForm();
				if (this.form.organizationId) {
					await this.loadOrgUsers(this.form.organizationId);
				}
			}
			this.formDialog = true;
		},

		closeForm() {
			this.formDialog = false;
		},

		async onOrgChange(orgId) {
			this.form.assignedUserId = null;
			if (orgId) await this.loadOrgUsers(orgId);
			else this.orgUsers = [];
		},

		async loadOrgUsers(orgId) {
			this.orgUsersLoading = true;
			try {
				const { data } = await this.axios.get("/devops/ticketing/users", {
					params: { organizationId: orgId }
				});

				this.orgUsers = data || [];
			} catch (err) {
				console.error("[TICKETING] failed to load org users", err);
				this.orgUsers = [];
			} finally {
				this.orgUsersLoading = false;
			}
		},

		// --- Multi-organization helpers (form) ---

		extraOrgCount(ticket) {
			return Math.max(0, getTicketOrganizations(ticket).length - 1);
		},

		extraOrgNames(ticket) {
			const all = getTicketOrganizations(ticket);
			const primaryId = ticket?.organizationId ?? all[0]?.id ?? null;

			return all
				.filter((o) => o.id !== primaryId)
				.map((o) => o.name)
				.filter(Boolean);
		},

		organizationCellTooltip(ticket) {
			const extras = this.extraOrgNames(ticket);

			if (!extras.length) return ticket.organizationName ?? "";
			return `${ticket.organizationName ?? ""}\n+ ${extras.join(", ")}`;
		},

		async loadAdditionalOrgUsers(orgId) {
			const entry = this.form.additionalOrgs.find((o) => o.id === orgId);

			if (!entry) return;
			this.$set(entry, "usersLoading", true);
			try {
				const { data } = await this.axios.get("/devops/ticketing/users", {
					params: { organizationId: orgId }
				});

				this.$set(entry, "users", data || []);
			} catch (err) {
				console.error("[TICKETING] failed to load additional org users", err);
				this.$set(entry, "users", []);
			} finally {
				this.$set(entry, "usersLoading", false);
			}
		},

		addAdditionalOrgFromPicker(orgId) {
			if (!orgId) return;
			if (orgId === this.form.organizationId) return;
			if (this.form.additionalOrgs.some((o) => o.id === orgId)) return;
			const org = this.organizations.find((o) => o.id === orgId);

			if (!org) return;
			this.form.additionalOrgs.push({
				id: org.id,
				name: this.organizationDisplayName(org.name) || org.name || "",
				assignedUserId: null,
				assignedUserName: null,
				assignedUserEmail: null,
				usersLoading: false,
				users: []
			});
			this.additionalOrgPick = null;
			this.loadAdditionalOrgUsers(org.id).catch(() => {});
		},

		removeAdditionalOrg(orgId) {
			const idx = this.form.additionalOrgs.findIndex((o) => o.id === orgId);

			if (idx !== -1) this.form.additionalOrgs.splice(idx, 1);
		},

		onAdditionalOrgUserChange(entry, userId) {
			const user = (entry.users || []).find((u) => u.id === userId);

			this.$set(entry, "assignedUserId", user?.id ?? null);
			this.$set(entry, "assignedUserName", user?.fullName ?? null);
			this.$set(entry, "assignedUserEmail", user?.email ?? null);
		},

		// --- Multi-organization helpers (sidebar) ---

		async onAffectedOrgAdd(orgId) {
			if (!orgId || !this.activeTicket) return;
			const org = this.organizations.find((o) => o.id === orgId);

			if (!org) return;
			// Clear value, search text, and blur the field so Vuetify drops
			// the lingering selected-item label from the input on the next tick.
			this.affectedOrgPick = null;
			this.affectedOrgPickSearch = null;
			this.$nextTick(() => {
				const picker = this.$refs.affectedOrgPicker;

				if (picker) {
					picker.lazySearch = "";
					picker.internalValue = null;
					picker.blur();
				}
			});
			try {
				await addTicketOrganization(this.activeTicket.id, {
					id: org.id,
					name: this.organizationDisplayName(org.name) || org.name || ""
				}, this.currentUser);
			} catch (err) {
				console.error("[TICKETING] add org failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async removeAffectedOrg(orgId) {
			if (!orgId || !this.activeTicket) return;
			try {
				await removeTicketOrganization(this.activeTicket.id, orgId, this.currentUser);
				// Clear the expanded flag so siblings reappear after deletion.
				if (this.expandedAffectedOrgs[orgId]) {
					this.$set(this.expandedAffectedOrgs, orgId, false);
				}
			} catch (err) {
				console.error("[TICKETING] remove org failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		toggleAffectedOrgExpand(orgId) {
			if (!orgId) return;
			const isOpen = !!this.expandedAffectedOrgs[orgId];

			// Only one org expanded at a time — collapse any others so the
			// expanded view fully replaces the list (no scroll, no siblings).
			if (!isOpen) {
				const next = {};

				next[orgId] = true;
				this.expandedAffectedOrgs = next;
			} else {
				this.$set(this.expandedAffectedOrgs, orgId, false);
			}
			// Lazy-load the org's user list the first time the row is expanded so
			// the change-contact picker is populated when shown.
			if (!isOpen && !this.viewOrgUsers[orgId]) {
				this.loadViewOrgUsers(orgId).catch(() => {});
			}
		},

		async loadViewOrgUsers(orgId) {
			this.$set(this.viewOrgUsers, orgId, { loading: true, items: []});
			try {
				const { data } = await this.axios.get("/devops/ticketing/users", {
					params: { organizationId: orgId }
				});

				this.$set(this.viewOrgUsers, orgId, { loading: false, items: data || []});
			} catch (err) {
				console.error("[TICKETING] failed to load view org users", err);
				this.$set(this.viewOrgUsers, orgId, { loading: false, items: []});
			}
		},

		async onAffectedOrgContactChange(org, userId) {
			if (!this.activeTicket || !org?.id) return;
			const bucket = this.viewOrgUsers[org.id];
			const user = userId ? (bucket?.items || []).find((u) => u.id === userId) : null;

			try {
				await updateTicketOrganizationContact(
					this.activeTicket.id,
					org.id,
					user ? { id: user.id, fullName: user.fullName, email: user.email } : null,
					this.currentUser
				);
				// Close the change-contact picker once a selection has been made.
				// Empty selections (cleared) also close — the user is opting out of
				// editing further.
				this.closeChangeContact(org.id);
			} catch (err) {
				console.error("[TICKETING] update org contact failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		openChangeContact(orgId) {
			if (!orgId) return;
			this.$set(this.changeContactOpen, orgId, true);
			// Ensure the user list is loaded for the picker.
			if (!this.viewOrgUsers[orgId]) {
				this.loadViewOrgUsers(orgId).catch(() => {});
			}
		},

		closeChangeContact(orgId) {
			if (!orgId) return;
			this.$set(this.changeContactOpen, orgId, false);
		},

		openOrgTicketsFor(orgId) {
			if (!orgId) return;
			this.orgTicketsOrgId = orgId;
			this.orgTicketsDialog = true;
		},

		orgAdminContacts(orgId) {
			if (!orgId) return [];
			const doc = (this.allOrgContactsRaw || []).find((d) => d.id === orgId);

			return doc?.admins || [];
		},

		affectedOrgUserRole(org) {
			if (!org || !org.assignedUserId) return null;
			const items = (this.viewOrgUsers[org.id] || {}).items || [];

			return items.find((u) => u.id === org.assignedUserId)?.role || null;
		},

		userRoleColor(role) {
			if (role === "devops") return "red--text";
			if (role === "admin") return "orange--text";
			if (role === "user") return "primary--text";
			return "grey--text";
		},

		orgFollowUpContacts(org) {
			if (!org) return [];
			const list = [];

			list.push({
				kind: "reporting-user",
				name: org.assignedUserName || null,
				contactId: org.assignedUserId || null,
				kindLabel: this.$t("ticketing.follow-up-contact.reporting-user")
			});

			const admins = this.orgAdminContacts(org.id);

			if (admins.length) {
				for (const c of admins) {
					list.push({
						kind: "admin",
						name: c.fullName || this.$t("ticketing.contacts.unnamed"),
						contactId: null,
						kindLabel: this.$t("ticketing.follow-up-contact.admin")
					});
				}
			} else {
				list.push({
					kind: "admin",
					name: null,
					contactId: null,
					kindLabel: this.$t("ticketing.follow-up-contact.admin")
				});
			}

			const techs = this.orgTechContacts(org.id);

			if (techs.length) {
				for (const c of techs) {
					list.push({
						kind: "tech",
						name: c.fullName || c.businessName || this.$t("ticketing.contacts.unnamed"),
						contactId: null,
						kindLabel: this.$t("ticketing.follow-up-contact.tech")
					});
				}
			} else {
				list.push({
					kind: "tech",
					name: null,
					contactId: null,
					kindLabel: this.$t("ticketing.follow-up-contact.tech")
				});
			}
			return list;
		},

		expandAffectedOrgFromMenu(orgId, kind) {
			if (!orgId) return;
			if (!this.expandedAffectedOrgs[orgId]) {
				this.toggleAffectedOrgExpand(orgId);
			}
			if (kind === "admin" || kind === "tech") {
				this.$nextTick(() => this.addContact(orgId, kind));
			} else if (kind === "reporting-user") {
				this.$nextTick(() => this.openChangeContact(orgId));
			}
		},

		orgTechContacts(orgId) {
			if (!orgId) return [];
			const doc = (this.allOrgContactsRaw || []).find((d) => d.id === orgId);

			return doc?.technicians || [];
		},

		async submitForm() {
			if (!this.$refs.form.validate()) return;

			const org = this.organizations.find((o) => o.id === this.form.organizationId);
			const user = this.orgUsers.find((u) => u.id === this.form.assignedUserId);
			const devNames = this.devopsUsers
				.filter((u) => this.form.assignedDevopsIds.includes(u.id))
				.map((u) => u.fullName);
			const primaryName = this.organizationDisplayName(org?.name) || this.form.organizationName;
			// Build the multi-org `organizations` array. The first entry mirrors
			// the legacy top-level fields and stays in sync with them so older
			// code paths/queries that read organizationId/Name keep working.
			const organizations = [
				{
					id: this.form.organizationId,
					name: primaryName,
					assignedUserId: user?.id ?? null,
					assignedUserName: user?.fullName ?? null,
					assignedUserEmail: user?.email ?? null
				},
				...this.form.additionalOrgs.map((o) => ({
					id: o.id,
					name: o.name,
					assignedUserId: o.assignedUserId ?? null,
					assignedUserName: o.assignedUserName ?? null,
					assignedUserEmail: o.assignedUserEmail ?? null
				}))
			].filter((o) => o.id || o.name);

			// Strip the UI-only form helper before persisting.
			const { additionalOrgs: _ignored, ...formForPayload } = this.form;
			const payload = {
				...formForPayload,
				organizationName: primaryName,
				assignedUserName: user?.fullName ?? null,
				assignedUserEmail: user?.email ?? null,
				assignedDevopsNames: devNames,
				organizations
			};

			this.formSaving = true;
			try {
				if (this.editingId) {
					const previous = this.tickets.find((t) => t.id === this.editingId);

					await updateTicket(this.editingId, payload, this.currentUser, previous);
					this.$success?.(this.$t("ticketing.update-success"));
				} else {
					const newId = await createTicket(payload, this.currentUser);

					// If the new ticket has no devops assigned, broadcast to all
					// devops so someone can pick it up.
					if (!(payload.assignedDevopsIds || []).length) {
						notifyUnassignedTicket(
							newId,
							payload.title,
							(this.devopsUsers || []).map((u) => u.id),
							this.currentUser,
							payload.organizationName
						).catch((err) => console.error("[TICKETING] notifyUnassignedTicket failed", err));
					}

					this.$success?.(this.$t("ticketing.create-success"));
					this.formDialog = false;
					// Optimistically open the freshly created ticket. The subscribeTicket
					// inside openTicket will replace this stub with the live data.
					this.openTicket({
						id: newId,
						...payload,
						watcherIds: Array.from(new Set([
							...(payload.watcherIds ?? []),
							this.currentUser.id
						].filter(Boolean))),
						createdById: this.currentUser.id,
						createdByName: this.currentUser.fullName
					});
					return;
				}
				this.formDialog = false;
			} catch (err) {
				console.error("[TICKETING] save failed", err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.formSaving = false;
			}
		},

		// --- Detail / messages ---

		tryOpenPendingTicket() {
			const id = this.openTicketId;

			if (!id) return;
			const tab = this.openTicketTab;

			if (this.activeTicketId === id) {
				if (tab) this.detailTab = tab;
				this.$emit("ticket-opened", id);
				return;
			}
			const ticket = this.tickets.find((t) => t.id === id);

			if (!ticket) return;
			this.openTicket(ticket, { tab });
			this.$emit("ticket-opened", id);
		},

		openTicketFromOrgList(ticket) {
			if (!ticket) return;
			this.orgTicketsDialog = false;
			if (ticket.id === this.activeTicketId) return;
			this.openTicket(ticket);
		},

		openTicket(ticket, options = {}) {
			this.activeTicketId = ticket.id;
			this.activeTicket = ticket;
			this.quickStatus = ticket.status;
			this.quickPriority = ticket.priority;
			this.detailDialog = true;
			this.detailTab = options.tab || "description";
			this.messages = [];
			this.activity = [];
			this.newMessage = "";
			this.messageAttachments = [];
			this.pendingMentions = [];
			this.visitLogged = false;
			this.expandedAffectedOrgs = {};
			this.expandedPrevFollowUpsByOrg = {};
			this.changeContactOpen = {};
			// Snapshot the navigation list from the table's current filter+sort
			// so the prev/next arrows iterate the order the user just saw, even
			// if they change the filter/search/sort while the dialog is open.
			// `keepNav: true` preserves the existing snapshot for prev/next.
			if (!options.keepNav) {
				this.navTicketIds = this.sortedFilteredTickets.map((t) => t.id).filter(Boolean);
				this.navFilterTab = this.filterTab;
			}
			// Freeze the user's last-seen timestamp at open time so the
			// "unread below" divider stays put even after we update the
			// stored value while the user is on the Conversation tab.
			this.unreadBoundaryMs = this.lastConversationSeen[ticket.id] || 0;
			this.unreadBoundaryEligibleToClear = false;
			this.didInitialConversationScroll = false;
			this.unreadPinActive = false;
			this.clearUnreadBoundaryTimer();

			// Opening a ticket clears its pending notifications for the current
			// user, regardless of which tab is shown.
			if (this.currentUser.id) {
				markTicketNotificationsRead(this.currentUser.id, ticket.id).catch((err) =>
					console.warn("[TICKETING] markTicketNotificationsRead failed", err)
				);
			}

			// Watcher might not fire if the tab value happens to match the
			// previously-active tab; mark explicitly here when opening on
			// the conversation tab.
			if (this.detailTab === "conversation") {
				this.markConversationSeenNow();
				this.scheduleUnreadBoundaryClear();
			}

			// Ensure we have the org's users loaded so we can show the customer email
			// underneath the name and power the admin-contact search picker.
			if (ticket.organizationId) {
				this.loadOrgUsers(ticket.organizationId).catch(() => {});
			}

			this.unsubTicket?.();
			this.unsubMessages?.();
			this.unsubActivity?.();
			this.unsubTodos?.();
			this.unsubPresence?.();
			this.leavePresence?.();
			this.unsubTyping?.();
			this.unsubTicketReaders?.();
			this.stopTypingBeat();

			this.activeViewers = [];
			this.activeTypers = [];
			this.ticketReaders = [];
			this.todos = [];
			this.newTodoText = "";
			this.editingTodoId = null;
			this.editingTodoText = "";
			this.leavePresence = joinTicketPresence(ticket.id, this.currentUser);
			this.unsubPresence = subscribeTicketPresence(ticket.id, (viewers) => {
				this.activeViewers = viewers.filter((v) => v.userId !== this.currentUser.id);
			});
			this.unsubTyping = subscribeTicketTyping(ticket.id, (typers) => {
				this.activeTypers = typers.filter((t) => t.userId !== this.currentUser.id);
			});
			this.unsubTicketReaders = subscribeTicketReaders(ticket.id, (readers) => {
				this.ticketReaders = readers;
			});

			this.subscribeToOrgContacts(ticket.organizationId);

			this.unsubTicket = subscribeTicket(ticket.id, (t) => {
				if (t) {
					const wasUnread = this.hasUnreadFor(t.id);

					this.activeTicket = t;
					this.quickStatus = t.status;
					this.quickPriority = t.priority;
					// Persist that the current user has seen this state
					if (this.currentUser.id) {
						markTicketSeen(t.id, this.currentUser.id).catch((err) =>
							console.warn("[TICKETING] markTicketSeen failed", err)
						);
						// Only log a visit when there was something new since last seen,
						// and only once per opening of the dialog (not on every snapshot).
						if (wasUnread && !this.visitLogged) {
							this.visitLogged = true;
							logTicketVisit(t.id, this.currentUser).catch((err) =>
								console.warn("[TICKETING] logTicketVisit failed", err)
							);
						}
					}
				}
			});
			this.unsubMessages = subscribeMessages(ticket.id, (msgs) => {
				const wasAtBottom = this.isMessagesAtBottom();
				const hadMessages = this.messages.length > 0;
				const prevLastId = this.messages.length ? this.messages[this.messages.length - 1].id : null;

				this.messages = msgs;
				if (!hadMessages) {
					// First batch for this ticket: position at the unread divider, or
					// the bottom when everything has already been read.
					this.unreadMessageCount = 0;
					this.scrollMessagesToInitial();
				} else if (wasAtBottom) {
					this.unreadMessageCount = 0;
					this.scrollMessagesToBottom();
				} else {
					// Count messages added after prevLastId that aren't from me.
					let startIdx = 0;

					if (prevLastId) {
						const i = msgs.findIndex((m) => m.id === prevLastId);

						startIdx = i >= 0 ? i + 1 : msgs.length;
					}
					const added = msgs.slice(startIdx).filter((m) => m.authorId !== this.currentUser.id);

					if (added.length) this.unreadMessageCount += added.length;
				}
			});
			this.unsubActivity = subscribeActivity(ticket.id, (items) => {
				this.activity = items;
			});
			this.unsubTodos = subscribeTodos(ticket.id, (items) => {
				this.todos = items;
			});
		},

		filterTabLabel(tab) {
			switch (tab) {
				case "active": return this.$t("ticketing.active");
				case "mine": return this.$t("ticketing.mine");
				case "mentioned": return this.$t("ticketing.mentioned");
				case "watching": return this.$t("ticketing.watching");
				case "urgent": return this.$t("ticketing.urgent");
				case "unassigned": return this.$t("ticketing.unassigned");
				case "needs-support": return this.$t("ticketing.status.needs-support-action");
				case "hardware": return this.$t("ticketing.type.hardware");
				case "porting": return this.$t("ticketing.type.porting");
				case "resolved": return this.$t("ticketing.resolved");
				case "all": return this.$t("ticketing.all");
				default: return tab || "";
			}
		},

		goToTicketDelta(delta) {
			if (!this.navTicketIds.length) return;
			const idx = this.navIndex;

			if (idx < 0) return;
			const nextIdx = idx + delta;

			if (nextIdx < 0 || nextIdx >= this.navTicketIds.length) return;
			const nextId = this.navTicketIds[nextIdx];
			const ticket = this.tickets.find((t) => t.id === nextId);

			if (!ticket) return;
			// Tear down current ticket's subscriptions and state, but preserve
			// the navigation snapshot so prev/next keep working through the
			// originally-iterated list.
			const keptIds = this.navTicketIds;
			const keptFilter = this.navFilterTab;

			this.closeDetail();
			this.navTicketIds = keptIds;
			this.navFilterTab = keptFilter;
			this.openTicket(ticket, { keepNav: true });
		},

		closeDetail() {
			this.detailDialog = false;
			this.activeTicketId = null;
			this.activeTicket = null;
			this.navTicketIds = [];
			this.navFilterTab = null;
			this.activity = [];
			this.todos = [];
			this.editingTodoId = null;
			this.editingTodoText = "";
			this.newTodoText = "";
			this.messageAttachments = [];
			this.unreadMessageCount = 0;
			this.unsubTicket?.();
			this.unsubMessages?.();
			this.unsubActivity?.();
			this.unsubTodos?.();
			this.unsubPresence?.();
			this.leavePresence?.();
			this.unsubTyping?.();
			this.unsubTicketReaders?.();
			this.unsubOrgContacts?.();
			this.unsubOrgContacts = null;
			this.orgContacts = null;
			this.contactsExpanded = false;
			this.unsubTyping = null;
			this.unsubTicketReaders = null;
			this.unsubPresence = null;
			this.leavePresence = null;
			this.activeViewers = [];
			this.activeTypers = [];
			this.ticketReaders = [];
			this.stopTypingBeat();
			this.unreadBoundaryMs = 0;
			this.unreadBoundaryEligibleToClear = false;
			this.unreadPinActive = false;
			this.clearUnreadBoundaryTimer();
		},

		onDetailDialogInput(open) {
			if (!open && this.activeTicketId) this.closeDetail();
		},

		async changeStatus(newStatus) {
			if (!this.activeTicket || newStatus === this.activeTicket.status) return;
			try {
				await updateTicket(
					this.activeTicket.id,
					{ status: newStatus },
					this.currentUser,
					this.activeTicket
				);
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async toggleTicketClosed() {
			if (!this.activeTicket || this.closeBtnLoading) return;
			const next = this.isTicketClosed ? "open" : "closed";

			await this.setTicketStatus(next);
		},

		async setTicketStatus(next) {
			if (!this.activeTicket || this.closeBtnLoading) return;
			if (next === this.activeTicket.status) return;

			this.closeBtnLoading = next;
			try {
				await updateTicket(
					this.activeTicket.id,
					{ status: next },
					this.currentUser,
					this.activeTicket
				);
				this.quickStatus = next;
				// When the user marks the ticket as closed or resolved from the
				// header buttons, dismiss the detail dialog so they can move on.
				if (next === "closed" || next === "resolved") {
					this.closeDetail();
				}
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.closeBtnLoading = false;
			}
		},

		async changePriority(newPriority) {
			if (!this.activeTicket || newPriority === this.activeTicket.priority) return;
			try {
				await updateTicket(
					this.activeTicket.id,
					{ priority: newPriority },
					this.currentUser,
					this.activeTicket
				);
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		async changeDueDate(newDate) {
			if (!this.activeTicket) return;
			const current = this.activeTicket.dueAt || null;
			const next = newDate || null;

			if (current === next) return;
			try {
				await updateTicket(
					this.activeTicket.id,
					{ dueAt: next },
					this.currentUser,
					this.activeTicket
				);
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			}
		},

		formatDueDate(d) {
			if (!d) return "";
			const date = typeof d === "string" ? new Date(d + "T00:00:00") : new Date(d);

			if (isNaN(date.getTime())) return "";
			return moment(date).locale(this.$i18n.locale).format("ll");
		},

		isOverdue(ticket) {
			if (!ticket?.dueAt) return false;
			const closed = ["closed", "resolved"].includes(ticket.status);

			if (closed) return false;
			const today = moment().format("YYYY-MM-DD");

			return ticket.dueAt < today;
		},

		async impersonateOrganization(organizationId) {
			if (!organizationId || this.impersonatingOrg) return;
			this.impersonatingOrg = true;
			try {
				await this.axios.post("/devops/impersonate", { organization: organizationId });
				localStorage.setItem("triggerPasskeyLogin", "true");
				await this.logout();
			} catch (err) {
				console.error("Failed to impersonate:", err);
				this.$error?.(this.$t("ticketing.save-error"));
				this.impersonatingOrg = false;
			}
		},

		async copyOrganizationId() {
			const id = this.activeTicket?.organizationId;

			if (!id) return;
			try {
				await navigator.clipboard.writeText(id);
				this.copiedOrgId = true;
				setTimeout(() => { this.copiedOrgId = false; }, 1500);
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		async copyAssignedUserId() {
			const id = this.activeTicket?.assignedUserId;

			if (!id) return;
			try {
				await navigator.clipboard.writeText(id);
				this.copiedUserId = true;
				setTimeout(() => { this.copiedUserId = false; }, 1500);
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		async copyText(value) {
			if (!value) return;
			try {
				await navigator.clipboard.writeText(value);
				this.$success?.(this.$t("copied"));
			} catch (err) {
				console.error("Failed to copy:", err);
			}
		},

		techIdentityKey(tech) {
			if (!tech) return "";
			const parts = [
				tech.businessName,
				tech.fullName,
				tech.primaryMethod,
				tech.primaryValue,
				tech.primaryExtension,
				tech.secondaryMethod,
				tech.secondaryValue,
				tech.secondaryExtension
			].map((v) => this.normalizeText(v));
			// Drop entirely empty entries

			if (!parts.some((p) => p.length)) return "";
			return parts.join("|");
		},

		techContactSearchFilter(item, queryText) {
			if (!queryText) return true;
			const needle = this.normalizeText(queryText);

			return item._searchBlob.includes(needle);
		},

		techContactItemText(item) {
			if (!item) return "";
			const parts = [item.fullName, item.businessName, item.primaryValue].filter(Boolean);

			return parts.join(" — ");
		},

		applyTechAutocomplete(picked) {
			if (!picked || !this.contactDraft) return;
			// Copy every field EXCEPT consent (and don't overwrite the index/kind)
			const consent = this.contactDraft.consent;

			this.contactDraft = {
				businessName: picked.businessName || "",
				fullName: picked.fullName || "",
				primaryMethod: picked.primaryMethod || "email",
				primaryValue: picked.primaryValue || "",
				primaryExtension: picked.primaryExtension || "",
				secondaryMethod: picked.secondaryMethod || null,
				secondaryValue: picked.secondaryValue || "",
				secondaryExtension: picked.secondaryExtension || "",
				consent
			};
		},

		adminUserItemText(item) {
			if (!item) return "";
			return [item.fullName, item.email].filter(Boolean).join(" — ");
		},

		adminUserSearchFilter(item, queryText) {
			if (!queryText) return true;
			const needle = this.normalizeText(queryText);
			const blob = this.normalizeText(
				[item.fullName, item.email, item.role].filter(Boolean).join(" ")
			);

			return blob.includes(needle);
		},

		applyAdminUserAutocomplete(picked) {
			if (!picked || !this.contactDraft) return;
			this.contactDraft.fullName = picked.fullName || this.contactDraft.fullName || "";
			if (!this.contactDraft.title) {
				this.contactDraft.title = this.$t("ticketing.contacts.default-admin-title");
			}
			const orgPhone = this.activeOrganizationPhone || "";
			// Default: phone (organization) as primary, email as secondary.

			if (orgPhone) {
				this.contactDraft.primaryMethod = "call";
				this.contactDraft.primaryValue = orgPhone;
				if (picked.email) {
					this.contactDraft.secondaryMethod = "email";
					this.contactDraft.secondaryValue = picked.email;
				}
			} else if (picked.email) {
				// No org phone known: just prefill the email as primary.
				if (this.contactDraft.primaryMethod === "email") {
					this.contactDraft.primaryValue = picked.email;
				} else {
					this.contactDraft.secondaryMethod = "email";
					this.contactDraft.secondaryValue = picked.email;
				}
			}
		},

		contactMethodIcon(method) {
			switch (method) {
				case "sms": return "mdi-message-text-outline";
				case "email": return "mdi-email-outline";
				case "call": return "mdi-phone-outline";
				default: return "mdi-help-circle-outline";
			}
		},

		isPhoneMethod(method) {
			return method === "sms" || method === "call";
		},

		formatContactValue(method) {
			if (!method) return "";
			if (this.isPhoneMethod(method.method)) {
				return phoneNumberFormater(method.value) || method.value;
			}
			return method.value;
		},

		formatPhoneDisplay(value) {
			if (!value) return "";
			return phoneNumberFormater(value) || value;
		},

		onContactMethodChange(slot, newMethod) {
			if (!this.contactDraft) return;
			const methodKey = slot === "primary" ? "primaryMethod" : "secondaryMethod";
			const valueKey = slot === "primary" ? "primaryValue" : "secondaryValue";
			const extKey = slot === "primary" ? "primaryExtension" : "secondaryExtension";
			const oldMethod = this.contactDraft[methodKey];
			const wasPhone = this.isPhoneMethod(oldMethod);
			const isPhone = this.isPhoneMethod(newMethod);

			this.contactDraft[methodKey] = newMethod;
			// Switching between phone-style and email (or to/from null) makes
			// the previous value meaningless; clear it.
			if (wasPhone !== isPhone || !newMethod) {
				this.contactDraft[valueKey] = "";
				this.contactDraft[extKey] = "";
			}
		},

		onContactPhoneInput(slot, phoneObject) {
			if (!this.contactDraft) return;
			const input = phoneObject?.number?.input;
			const dialCode = phoneObject?.country?.dialCode;
			let value = "";

			if (input) {
				if (input.includes("+")) {
					value = input.replace(/[ -]/g, "");
				} else {
					value = `+${dialCode}${input.replace(/[() -]/g, "")}`;
				}
			}
			if (slot === "primary") {
				this.contactDraft.primaryValue = value;
			} else {
				this.contactDraft.secondaryValue = value;
			}
		},

		contactValueLabel(method) {
			switch (method) {
				case "sms": return this.$t("ticketing.contacts.value-phone");
				case "email": return this.$t("ticketing.contacts.value-email");
				case "call": return this.$t("ticketing.contacts.value-phone");
				default: return this.$t("ticketing.contacts.value-info");
			}
		},

		contactMethodsOf(c) {
			const out = [];

			if (c.primaryMethod && c.primaryValue) {
				out.push({
					method: c.primaryMethod,
					value: c.primaryValue,
					extension: c.primaryExtension || null
				});
			}
			if (c.secondaryMethod && c.secondaryValue) {
				out.push({
					method: c.secondaryMethod,
					value: c.secondaryValue,
					extension: c.secondaryExtension || null
				});
			}
			return out;
		},

		consentColor(consent) {
			switch (consent) {
				case "allowed": return "green";
				case "always-ask": return "orange";
				case "denied": return "red";
				case "unknown":
				default: return "grey";
			}
		},

		emptyContact(kind) {
			const base = {
				fullName: "",
				primaryMethod: "call",
				primaryValue: "",
				primaryExtension: "",
				secondaryMethod: null,
				secondaryValue: "",
				secondaryExtension: ""
			};

			if (kind === "admin") {
				return { ...base, title: "" };
			}
			return { ...base, businessName: "", consent: "unknown" };
		},

		addContact(orgId, kind) {
			if (!orgId) return;
			this.contactDraftOrgId = orgId;
			this.contactDraftKind = kind;
			this.contactDraftIndex = -1;
			this.contactDraft = this.emptyContact(kind);
			this.contactDialog = true;
		},

		editContact(orgId, kind, index) {
			if (!orgId) return;
			const list = kind === "admin" ? this.orgAdminContacts(orgId) : this.orgTechContacts(orgId);
			const source = list[index];

			if (!source) return;
			this.contactDraftOrgId = orgId;
			this.contactDraftKind = kind;
			this.contactDraftIndex = index;
			this.contactDraft = { ...this.emptyContact(kind), ...source };
			this.contactDialog = true;
		},

		confirmDeleteContact(orgId, kind, index) {
			if (!orgId) return;
			this.contactDeleteTarget = { orgId, kind, index };
			this.contactDeleteConfirm = true;
		},

		async saveContactDraft() {
			const orgId = this.contactDraftOrgId;

			if (!orgId || !this.contactDraft) return;
			this.contactSaving = true;
			try {
				const admins = [...this.orgAdminContacts(orgId)];
				const technicians = [...this.orgTechContacts(orgId)];
				const list = this.contactDraftKind === "admin" ? admins : technicians;
				const draft = { ...this.contactDraft };
				// Strip empty extension fields for cleanliness

				if (draft.primaryMethod !== "call") draft.primaryExtension = "";
				if (draft.secondaryMethod !== "call") draft.secondaryExtension = "";
				if (this.contactDraftIndex === -1) {
					list.push(draft);
				} else {
					list.splice(this.contactDraftIndex, 1, draft);
				}
				await saveOrgContacts(orgId, { admins, technicians }, this.currentUser);
				this.contactDialog = false;
				this.contactDraft = null;
				this.contactDraftOrgId = null;
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.contactSaving = false;
			}
		},

		async deleteContactConfirmed() {
			if (!this.contactDeleteTarget) return;
			const { orgId, kind, index } = this.contactDeleteTarget;

			if (!orgId) return;
			this.contactSaving = true;
			try {
				const admins = [...this.orgAdminContacts(orgId)];
				const technicians = [...this.orgTechContacts(orgId)];
				const list = kind === "admin" ? admins : technicians;

				list.splice(index, 1);
				await saveOrgContacts(orgId, { admins, technicians }, this.currentUser);
				this.contactDeleteConfirm = false;
				this.contactDeleteTarget = null;
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.contactSaving = false;
			}
		},

		subscribeToOrgContacts(orgId) {
			if (this.unsubOrgContacts) {
				this.unsubOrgContacts();
				this.unsubOrgContacts = null;
			}
			this.orgContacts = null;
			if (!orgId) return;
			this.unsubOrgContacts = subscribeOrgContacts(orgId, (data) => {
				this.orgContacts = data;
			});
		},

		async changeAssignedDevops(newIds) {
			if (!this.activeTicket) return;
			const current = this.activeTicket.assignedDevopsIds || [];
			const sameLen = current.length === newIds.length;
			const sameSet = sameLen && current.every((id) => newIds.includes(id));

			if (sameSet) return;

			const names = this.devopsUsers
				.filter((u) => newIds.includes(u.id))
				.map((u) => u.fullName);

			this.savingDevops = true;
			try {
				await updateTicket(
					this.activeTicket.id,
					{ assignedDevopsIds: newIds, assignedDevopsNames: names },
					this.currentUser,
					this.activeTicket
				);

				// If the ticket went from having devops to none, broadcast to all
				// devops so the now-unassigned ticket gets picked up.
				if (current.length > 0 && newIds.length === 0) {
					notifyUnassignedTicket(
						this.activeTicket.id,
						this.activeTicket.title,
						(this.devopsUsers || []).map((u) => u.id),
						this.currentUser,
						this.activeTicket.organizationName
					).catch((err) => console.error("[TICKETING] notifyUnassignedTicket failed", err));
				}
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.savingDevops = false;
			}
		},

		async takeDevops() {
			if (!this.activeTicket || !this.currentUser?.id) return;
			await this.changeAssignedDevops([this.currentUser.id]);
		},

		async watchTicket() {
			try {
				await addWatcher(this.activeTicket.id, this.currentUser.id, this.currentUser, this.currentUser.fullName);
			} catch (err) {
				console.error(err);
			}
		},

		onWatchersMenuToggle(open) {
			if (!open) return;
			this.$nextTick(() => {
				const ac = this.$refs.watchersAutocomplete;

				if (!ac) return;
				ac.focus?.();
				ac.activateMenu?.();
				ac.isMenuActive = true;
			});
		},

		async changeWatchers(newIds) {
			if (!this.activeTicket) return;
			const current = this.activeTicket.watcherIds || [];
			const added = newIds.filter((id) => !current.includes(id));
			const removed = current.filter((id) => !newIds.includes(id));

			if (!added.length && !removed.length) return;

			this.savingWatchers = true;
			try {
				for (const uid of added) {
					await addWatcher(this.activeTicket.id, uid, this.currentUser, this.devopsName(uid));
				}
				for (const uid of removed) {
					await removeWatcher(this.activeTicket.id, uid, this.currentUser, this.devopsName(uid));
				}
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.savingWatchers = false;
			}
		},

		async unwatchTicket() {
			try {
				await removeWatcher(this.activeTicket.id, this.currentUser.id, this.currentUser, this.currentUser.fullName);
			} catch (err) {
				console.error(err);
			}
		},

		async removeWatcherClick(userId) {
			try {
				await removeWatcher(this.activeTicket.id, userId, this.currentUser, this.devopsName(userId));
			} catch (err) {
				console.error(err);
			}
		},

		followUpIcon(method) {
			return {
				"email": "mdi-email-outline",
				"phone": "mdi-phone-outline",
				"voicemail": "mdi-voicemail",
				"sms": "mdi-message-text-outline",
				"chat": "mdi-message-processing-outline",
				"in-person": "mdi-account-voice"
			}[method] || "mdi-phone-log-outline";
		},

		async logFollowUpClick(method, organization = null, contact = null) {
			if (!this.activeTicket) return;
			const orgKey = organization?.id || "_";

			if (this.loggingFollowUpOrgId) return;
			this.loggingFollowUpOrgId = orgKey;
			try {
				await logFollowUp(this.activeTicket.id, method, this.currentUser, organization, contact);
				this.$success?.(this.$t("ticketing.follow-up-logged"));
			} catch (err) {
				console.error(err);
				this.$error?.(this.$t("ticketing.save-error"));
			} finally {
				this.loggingFollowUpOrgId = null;
			}
		},

		async sendMessage() {
			if (this.sendingMessage) return;
			const body = this.newMessage.trim();

			if (!body && !this.messageAttachments.length) return;

			this.sendingMessage = true;

			// Resolve mentions against pendingMentions, then re-scan body for any @Name still present.
			const mentionIds = [];

			(this.pendingMentions || []).forEach((m) => {
				if (body.includes(`@${m.fullName}`) && !mentionIds.includes(m.id)) {
					mentionIds.push(m.id);
				}
			});

			this.stopTypingBeat();

			// Snapshot the outgoing payload, then clear the composer immediately
			// so the input feels instant instead of waiting on the Firestore
			// write. Everything is restored if the send fails.
			const attachments = this.messageAttachments;
			const previousMessage = this.newMessage;
			const previousMentions = this.pendingMentions;

			this.newMessage = "";
			this.pendingMentions = [];
			this.messageAttachments = [];

			try {
				await addMessage(
					this.activeTicket.id,
					body,
					mentionIds,
					this.currentUser,
					attachments
				);
				// Sending implies the user has caught up on the thread.
				this.unreadBoundaryMs = 0;
				this.unreadBoundaryEligibleToClear = false;
				this.unreadPinActive = false;
				this.clearUnreadBoundaryTimer();
				this.scrollMessagesToBottom();
			} catch (err) {
				console.error(err);
				// Restore the composer so the user can retry.
				this.newMessage = previousMessage;
				this.pendingMentions = previousMentions;
				this.messageAttachments = attachments;
				this.$error?.(this.$t("ticketing.send-error"));
			} finally {
				this.sendingMessage = false;
			}
		},

		// --- Attachments ---

		isImage(att) {
			return att?.contentType?.startsWith("image/");
		},

		attachmentBaseName(name) {
			if (!name) return "";
			const i = name.lastIndexOf(".");

			return i > 0 ? name.slice(0, i) : name;
		},

		attachmentExtension(name) {
			if (!name) return "";
			const i = name.lastIndexOf(".");

			return i > 0 ? name.slice(i) : "";
		},

		openImagePreview(attachments, idx) {
			const images = (attachments || []).filter((a) => this.isImage(a));
			const target = (attachments || [])[idx];
			const i = images.indexOf(target);

			if (i < 0) return;
			this.imagePreviewList = images;
			this.imagePreviewIndex = i;
			this.imagePreviewOpen = true;
		},

		prevImagePreview() {
			if (this.imagePreviewIndex > 0) this.imagePreviewIndex--;
		},

		nextImagePreview() {
			if (this.imagePreviewIndex < this.imagePreviewList.length - 1) this.imagePreviewIndex++;
		},

		onImagePreviewKey(e) {
			if (e.key === "ArrowLeft") this.prevImagePreview();
			else if (e.key === "ArrowRight") this.nextImagePreview();
			else if (e.key === "Escape") this.imagePreviewOpen = false;
		},

		attachmentIcon(att) {
			const ct = att?.contentType || "";

			if (ct.startsWith("image/")) return "mdi-file-image-outline";
			if (ct === "application/pdf") return "mdi-file-pdf-box";
			if (ct.startsWith("video/")) return "mdi-file-video-outline";
			if (ct.startsWith("audio/")) return "mdi-file-music-outline";
			if (ct.includes("word") || ct.includes("document")) return "mdi-file-word-outline";
			if (ct.includes("sheet") || ct.includes("excel") || ct === "text/csv") return "mdi-file-excel-outline";
			if (ct.startsWith("text/")) return "mdi-file-document-outline";
			return "mdi-file-outline";
		},

		formatSize(bytes) {
			if (!bytes) return "";
			const units = ["B", "KB", "MB", "GB"];
			let v = bytes;
			let i = 0;

			while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
			return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
		},

		async uploadFiles(fileList, target) {
			const ticketId = target === "message" ? this.activeTicket?.id : (this.editingId || `draft-${this.currentUser.id}`);
			const arr = Array.from(fileList || []);

			if (!arr.length) return;

			const uploadingFlag = target === "message" ? "messageAttachmentsUploading" : "formAttachmentsUploading";
			const list = target === "message" ? this.messageAttachments : this.form.attachments;

			this[uploadingFlag] = true;
			try {
				for (const file of arr) {
					try {
						const meta = await uploadAttachment(ticketId, file);

						list.push(meta);
					} catch (err) {
						console.error("[TICKETING] upload failed", err);
						this.$error?.(this.$t("ticketing.attach-error"));
					}
				}
			} finally {
				this[uploadingFlag] = false;
			}
		},

		async onFormFilesPicked(e) {
			const files = e.target.files;

			await this.uploadFiles(files, "form");
			e.target.value = "";
		},

		async onMessageFilesPicked(e) {
			const files = e.target.files;

			await this.uploadFiles(files, "message");
			e.target.value = "";
		},

		removeFormAttachment(idx) {
			const att = this.form.attachments[idx];

			this.form.attachments.splice(idx, 1);
			if (att?.path) deleteAttachment(att.path).catch(() => {});
		},

		removeMessageAttachment(idx) {
			const att = this.messageAttachments[idx];

			this.messageAttachments.splice(idx, 1);
			if (att?.path) deleteAttachment(att.path).catch(() => {});
		},

		renderMessage(body) {
			let html = escapeHtml(body);
			const meName = this.currentUser.fullName;
			// Linkify URLs (http(s):// or www.) BEFORE mention substitution.
			// We match on the already-escaped text so URLs containing & / etc. are safe.
			const urlRe = /\b((?:https?:\/\/|www\.)[^\s<]+[^\s<.,:;!?)\]}'"])/gi;

			html = html.replace(urlRe, (match) => {
				const href = match.startsWith("www.") ? "http://" + match : match;

				return `<a href="${href}" class="msg-link" target="_blank" rel="noopener noreferrer">${match}</a>`;
			});
			// Highlight @mentions

			(this.devopsUsers || []).forEach((u) => {
				const safe = escapeHtml(u.fullName);
				const escaped = safe.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
				const re = new RegExp("@" + escaped, "g");
				const isMe = meName && u.fullName === meName;
				const cls = isMe ? "mention-tag mention-tag--me" : "mention-tag";

				html = html.replace(re, `<span class="${cls}">@${safe}</span>`);
			});
			return html.replace(/\n/g, "<br>");
		},

		onMessagesClick(ev) {
			const link = ev.target.closest && ev.target.closest("a.msg-link");

			if (!link) return;
			ev.preventDefault();
			ev.stopPropagation();
			this.pendingExternalLink = link.getAttribute("href") || "";
			this.externalLinkConfirmOpen = true;
		},

		confirmExternalLink() {
			const url = this.pendingExternalLink;

			this.externalLinkConfirmOpen = false;
			this.pendingExternalLink = "";
			if (url) window.open(url, "_blank", "noopener,noreferrer");
		},

		formatUrlForBreaking(url) {
			// Insert <wbr> at natural URL separators so the browser prefers
			// breaking after /, ?, &, =, #, . rather than mid-word.
			const safe = escapeHtml(url || "");

			return safe.replace(/([/?&=#.])/g, "$1<wbr>");
		},

		// --- mention autocomplete ---

		onMessageKeydown(e) {
			if (!this.mentionMenuOpen) {
				// Enter sends, Shift+Enter inserts a newline (default behavior).
				if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !e.isComposing) {
					e.preventDefault();
					this.sendMessage();
				}
				return;
			}
			if (e.key === "ArrowDown") {
				e.preventDefault();
				this.mentionIndex = Math.min(
					this.mentionIndex + 1,
					this.filteredMentionCandidates.length - 1
				);
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				this.mentionIndex = Math.max(this.mentionIndex - 1, 0);
			} else if (e.key === "Enter" || e.key === "Tab") {
				const u = this.filteredMentionCandidates[this.mentionIndex];

				if (u) {
					e.preventDefault();
					this.insertMention(u);
				}
			} else if (e.key === "Escape") {
				this.mentionMenuOpen = false;
			}
		},

		beatTyping() {
			if (!this.activeTicketId) return;
			if (!this.newMessage.trim()) {
				this.stopTypingBeat();
				return;
			}
			this.typingActive = true;
			setTicketTyping(this.activeTicketId, this.currentUser);
			if (this.typingTimeout) clearTimeout(this.typingTimeout);
			this.typingTimeout = setTimeout(() => {
				this.stopTypingBeat();
			}, 5000);
		},

		stopTypingBeat() {
			if (this.typingTimeout) {
				clearTimeout(this.typingTimeout);
				this.typingTimeout = null;
			}
			if (this.typingActive && this.activeTicketId) {
				clearTicketTyping(this.activeTicketId, this.currentUser);
			}
			this.typingActive = false;
		},

		onMessageKeyup(e) {
			this.beatTyping();
			const ta = e.target;
			const value = ta.value || "";
			const caret = ta.selectionStart || 0;
			const before = value.slice(0, caret);
			const match = before.match(/(?:^|\s)@([\w\-.\s]{0,40})$/);

			if (match) {
				this.mentionStart = before.lastIndexOf("@");
				this.mentionQuery = match[1] || "";
				this.mentionIndex = 0;
				const rect = ta.getBoundingClientRect();

				this.mentionMenuX = rect.left + 20;
				this.mentionMenuY = rect.top + 30;
				this.mentionMenuOpen = true;
			} else {
				this.mentionMenuOpen = false;
			}
		},

		insertMention(user) {
			const before = this.newMessage.slice(0, this.mentionStart);
			const after = this.newMessage.slice(
				this.mentionStart + 1 + (this.mentionQuery?.length || 0)
			);

			this.newMessage = `${before}@${user.fullName} ${after}`;
			if (!this.pendingMentions.find((m) => m.id === user.id)) {
				this.pendingMentions.push({ id: user.id, fullName: user.fullName });
			}
			this.mentionMenuOpen = false;
			this.mentionQuery = "";
			this.mentionStart = -1;
		},

		// --- notifications ---

		async handleNotificationClick(n) {
			this.notifMenuOpen = false;
			if (!n.read) {
				try { await markNotificationRead(this.currentUser.id, n.id); } catch (_) {}
			}
			const ticket = this.tickets.find((t) => t.id === n.ticketId);

			if (!ticket) return;
			const convTypes = ["new_message", "mention", "reaction"];
			const tab = convTypes.includes(n.type) ? "conversation" : null;

			if (this.activeTicketId === ticket.id) {
				if (tab) this.detailTab = tab;
				return;
			}
			this.openTicket(ticket, { tab });
		},

		async markAllRead() {
			try { await markAllNotificationsRead(this.currentUser.id); } catch (_) {}
		},

		async removeNotification(id) {
			try { await deleteNotification(this.currentUser.id, id); } catch (_) {}
		},

		async clearAllNotifications() {
			this.clearAllConfirmOpen = false;
			try { await deleteAllNotifications(this.currentUser.id); } catch (_) {}
		}
	}
};
</script>

<style>
.cursor-tip-bubble {
	position: fixed;
	z-index: 10000;
	pointer-events: none;
	background: rgba(33, 33, 33, 0.92);
	color: #fff;
	font-size: 12px;
	line-height: 1.3;
	padding: 4px 8px;
	border-radius: 4px;
	white-space: nowrap;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.v-tooltip__content.follow-up-tooltip-dark {
	background: rgba(20, 20, 20, 0.98) !important;
	opacity: 1 !important;
}

/* Fullscreen ticket-detail dialog is teleported outside the component
   tree, so scoped styles can't reach it. Lock it in place to prevent the
   persistent-dialog shake animation from drifting it a few pixels. */
.dialog-ticket-detail.v-dialog--fullscreen {
	top: 0 !important;
	left: 0 !important;
	transform: none !important;
	animation: none !important;
}

/* v-menu content is teleported to the body, so scoped styles don't reach
   the follow-up contacts menu. Trim the default top whitespace above the
   "follow-up with" subheader. */
.follow-up-contacts-menu.v-list {
	padding-top: 0;
}
.follow-up-contacts-menu .v-subheader {
	height: auto;
	min-height: 0;
	padding-top: 4px;
	padding-bottom: 4px;
}
.follow-up-contact-link,
.follow-up-contact-link:hover,
.follow-up-contact-link:focus {
	text-decoration: none;
}
</style>

<style scoped>
.ticketing-table >>> tbody tr {
	cursor: pointer;
}

.additional-orgs-section {
	background: #F5F6FF;
	border: 1px dashed rgba(60, 65, 195, 0.35);
	border-radius: 8px;
}

.follow-up-toggle-btn {
	min-width: auto !important;
	height: 18px !important;
	letter-spacing: 0 !important;
}

.prev-follow-ups {
	padding-left: 16px;
	border-left: 2px solid rgba(0, 0, 0, 0.08);
	margin-left: -19px;
	margin-top: 2px;
}

.additional-org-row {
	background: #fff;
	border: 1px solid #e8e9f5;
	border-radius: 6px;
	padding: 6px 8px;
}

.affected-org-card {
	background: #F5F6FF;
	border: 1px solid #e8e9f5;
	border-radius: 6px;
	padding: 6px 8px;
}

/* Cap the visible list at ~5 collapsed rows; scroll past that so the
   sidebar doesn't grow unbounded when many orgs are affected. Each row
   (incl. mb-2 spacing) is ~48px tall. */
.affected-orgs-list {
	max-height: 260px;
	overflow-y: auto;
	padding-right: 4px;
}

.follow-ups-list {
	max-height: 320px;
	overflow-y: auto;
	padding-right: 4px;
}

.follow-up-card {
	border: 1px solid #e8e9f5;
	border-radius: 6px;
	background: #fff;
}

.follow-up-contacts-menu {
	min-width: 360px;
}

.follow-up-contact-item {
	min-height: 44px;
}

/* When an org is expanded we collapse the others away, so let the
   remaining card grow naturally without a scrollbar. */
.affected-orgs-list.is-expanded {
	max-height: none;
	overflow-y: visible;
	padding-right: 0;
}

.affected-org-card .affected-org-row {
	cursor: pointer;
}

.affected-org-card .affected-org-details {
	background: #fff;
	border: 1px solid #e8e9f5;
	border-radius: 6px;
	margin-top: 6px;
}

.affected-contact-row {
	padding: 4px 6px;
	border: 1px solid #eef0fb;
	border-radius: 4px;
	background: #fafbff;
}

.extra-orgs-chip {
	cursor: pointer;
}

.ticketing-table-wrap {
	padding-left: 16px;
	padding-right: 16px;
}

.ticketing-table >>> tbody td {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	padding-top: 10px !important;
	padding-bottom: 10px !important;
	padding-left: 8px !important;
	padding-right: 8px !important;
	height: auto !important;
	text-align: left;
}

.ticketing-table >>> thead th {
	padding-left: 8px !important;
	padding-right: 8px !important;
	text-align: left !important;
}

.ticketing-table >>> thead th:first-child,
.ticketing-table >>> tbody td:first-child {
	padding-left: 0 !important;
}

.ticketing-table >>> thead th:last-child,
.ticketing-table >>> tbody td:last-child {
	padding-right: 0 !important;
}

.ticketing-table >>> table {
	table-layout: auto;
	width: 100%;
}

/* Phone input: country selector embedded in prepend-inner of v-text-field */
.contact-phone-field >>> .v-input__prepend-inner {
	margin: 0 !important;
	margin-top: 0 !important;
	align-self: center;
}
.contact-phone-field >>> .v-input__prepend-inner .v-input__append-inner {
	margin-top: 5px !important;
}
.contact-phone-field >>> .v-input__prepend-inner .v-select__selections {
	padding-top: 4px !important;
}
.contact-phone-field >>> .select-phone .v-input__slot {
	background-color: transparent !important;
}
.contact-phone-field >>> .select-phone .v-input__slot::before,
.contact-phone-field >>> .select-phone .v-input__slot::after,
.contact-phone-field >>> .select-phone .v-text-field__details,
.contact-phone-field >>> .input-phone {
	display: none !important;
}

.take-devops-btn {
	align-self: stretch;
	height: calc(100% - 8px) !important;
	min-height: 0 !important;
	margin: 4px -4px 4px 0 !important;
	border-radius: 4px;
}

.contact-phone-link {
	cursor: pointer;
	text-decoration: none;
}
.contact-phone-link:hover {
	text-decoration: underline;
}
.affected-org-id {
	cursor: pointer;
	font-family: monospace;
	font-size: 0.7rem;
}
.affected-org-id:hover {
	text-decoration: underline;
}

.org-logo-avatar {
	background-color: #f5f5f5 !important;
	flex-shrink: 0;
}

/* Stretch the append slot only for inputs that contain the take/steal button so the dropdown arrow alignment is unaffected elsewhere */
.field-block >>> .v-input__append-inner:has(.take-devops-btn) {
	align-self: stretch;
	margin-top: 0 !important;
	display: flex;
	align-items: stretch;
}

.ticketing-table >>> td.col-flex,
.ticketing-table >>> th.col-flex {
	white-space: nowrap;
	max-width: none;
	overflow: visible;
	text-overflow: clip;
}

.ticketing-table >>> .v-data-table__wrapper {
	overflow-x: auto;
}

.ticketing-table >>> th.nowrap-header,
.ticketing-table >>> td.nowrap-header {
	white-space: nowrap;
}

.ticketing-table >>> th.spacer-col,
.ticketing-table >>> td.spacer-col {
	width: 100% !important;
	min-width: 0;
	padding: 0 !important;
}

.ticketing-table >>> tbody tr:hover:not(.v-data-table__expanded__content) > td {
	background-color: rgba(0, 0, 0, 0.04) !important;
}

.ticketing-table >>> tr.ticket-row--mention > td {
	background-color: #fff8c4 !important;
}
.ticketing-table >>> tr.ticket-row--mention:hover > td {
	background-color: #fff1a8 !important;
}

.mobile-ticket-row.ticket-row--mention {
	background-color: #fff8c4;
}

.unread-notif {
	background-color: rgba(33, 150, 243, 0.05);
}

.notif-list >>> .notif-item {
	min-height: 0;
	padding-top: 4px;
	padding-bottom: 4px;
	padding-left: 10px;
	padding-right: 6px;
}

.notif-list >>> .v-list-item {
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.notif-list >>> .v-list-item:last-child {
	border-bottom: none;
}

.notif-context {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	color: rgba(0, 0, 0, 0.87) !important;
	font-size: 12px !important;
	line-height: 1.25;
	margin-bottom: 2px !important;
	min-width: 0;
}

.notif-context__row {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
}

.notif-context__row .notif-ticket {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
}

.notif-emoji {
	font-size: 12px;
	line-height: 1;
	margin-right: 4px;
}

.notif-time,
.notif-time-inline {
	flex: 0 0 auto;
	font-size: 10px;
	color: rgba(0, 0, 0, 0.55);
	white-space: nowrap;
}

.notif-time-inline {
	margin-left: 6px;
}

.notif-context .font-weight-medium {
	color: #1565c0;
}

.notif-org {
	font-weight: 700;
	color: rgba(0, 0, 0, 0.87);
	min-width: 0;
	overflow: hidden;
	display: flex;
	align-items: center;
	gap: 4px;
}

.notif-ticket {
	font-weight: 500;
	color: #1565c0;
}

.notif-title {
	font-size: 12.5px !important;
	line-height: 1.35 !important;
	color: rgba(0, 0, 0, 0.6) !important;
	white-space: normal !important;
	overflow: visible !important;
	text-overflow: clip !important;
	word-break: break-word;
}

.notif-title .font-weight-medium {
	color: rgba(0, 0, 0, 0.78);
}

.message-row {
	display: grid;
	grid-template-columns: 32px minmax(0, min(80%, 600px));
	grid-column-gap: 8px;
	align-items: start;
	justify-content: start;
}

.message-row--mine {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
}

.message-row--follow-up {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.follow-up-bubble {
	display: flex;
	align-items: center;
	gap: 10px;
	max-width: min(92%, 460px);
	padding: 7px 12px 7px 8px;
	background: linear-gradient(135deg, #f7f8ff 0%, #eef0fc 100%);
	border: 1px solid #dfe3f7;
	border-radius: 16px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.follow-up-bubble__icon {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	background: linear-gradient(135deg, #3c41c3 0%, #5a5fe0 100%);
	box-shadow: 0 1px 3px rgba(60, 65, 195, 0.35);
}

.follow-up-bubble__content {
	flex: 1 1 auto;
	min-width: 0;
}

.follow-up-bubble__line {
	font-size: 12.5px;
	line-height: 1.35;
	color: rgba(0, 0, 0, 0.78);
}

.follow-up-bubble__verb {
	color: rgba(0, 0, 0, 0.5);
}

.follow-up-bubble__method {
	font-weight: 600;
	color: #3c41c3;
	text-transform: capitalize;
}

.follow-up-bubble__meta {
	display: flex;
	align-items: center;
	margin-top: 1px;
	font-size: 11px;
	color: rgba(0, 0, 0, 0.55);
	min-width: 0;
}

.follow-up-bubble__meta .v-icon {
	color: rgba(0, 0, 0, 0.4) !important;
}

.follow-up-bubble__time {
	flex-shrink: 0;
	align-self: flex-start;
	color: rgba(0, 0, 0, 0.45);
	white-space: nowrap;
}

.reactions-row--center {
	justify-content: center;
}

.message-avatar {
	grid-column: 1;
	grid-row: 2;
	align-self: start;
}

.message-column {
	grid-column: 2;
	grid-row: 2;
	display: flex;
	flex-direction: column;
	min-width: 0;
	align-items: flex-start;
}

.message-column .message-item {
	width: auto;
	max-width: 100%;
}

.message-column .message-meta-other,
.message-column .reactions-row {
	width: 100%;
}

.message-row--mine .message-column {
	max-width: min(80%, 600px);
	width: auto;
	align-items: flex-end;
}

.message-meta-other {
	grid-column: 2;
	grid-row: 1;
	display: flex;
	align-items: center;
	padding: 0 4px 2px 4px;
	min-width: 0;
}

.message-item {
	background-color: #ffffff;
	border-radius: 18px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
	max-width: 100%;
	padding: 8px 12px;
}

.message-item--mine {
	background-color: #e3f2fd;
}

.reactions-row {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
	margin-top: 4px;
}

.message-row--mine .reactions-row {
	justify-content: flex-end;
}

.message-row--mine .reaction-chip--add {
	order: -1;
}

.reaction-chip {
	display: inline-flex;
	align-items: center;
	justify-content: flex-start;
	gap: 3px;
	width: 48px;
	height: 32px;
	padding: 0 8px;
	border-radius: 8px;
	background-color: #f0f2f5;
	border: 0.625px solid rgb(31, 31, 31);
	font-size: 12px;
	line-height: 1.4;
	cursor: pointer;
	transition: background-color 0.15s, border-color 0.15s;
}

.reaction-chip:hover {
	background-color: #e4e6eb;
}

.reaction-chip--mine {
	background-color: #e3f2fd;
	border-color: #1976d2;
	color: #1565c0;
}

.reaction-chip__emoji {
	font-size: 15px;
	line-height: 1;
}

.reaction-chip__count {
	margin-left: auto;
	font-family: "Roboto", sans-serif;
	font-weight: 400;
	font-size: 15px;
}

.reaction-chip--add {
	padding: 1px 4px;
	color: rgba(0, 0, 0, 0.55);
}

.reaction-chip--picker {
	width: 32px;
	height: 32px;
	padding: 0;
	border-radius: 50%;
	border: none;
	background-color: transparent;
	justify-content: center;
}

.reaction-chip--picker:hover {
	background-color: #e4e6eb;
}

.reaction-chip--picker .v-icon {
	font-size: 16px !important;
}

.emoji-picker {
	display: grid;
	grid-template-columns: repeat(9, 1fr);
	gap: 2px;
	padding: 6px;
	background-color: #fff;
	max-width: 320px;
}

.emoji-picker__item {
	font-size: 20px;
	line-height: 1;
	padding: 4px;
	border: none;
	background: transparent;
	cursor: pointer;
	border-radius: 4px;
}

.emoji-picker__item:hover {
	background-color: #f0f2f5;
}

.messages-list {
	max-height: 55vh;
	overflow-y: auto;
	padding: 12px;
	background-color: #f5f7fa;
	border: 1px solid #e0e4ea;
	border-radius: 8px;
}

.messages-list-wrap {
	position: relative;
}

.ticket-detail-grid {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.ticket-detail-grid > .td-left { display: contents; }
.td-left > .td-desc { order: 0; }
.ticket-detail-grid > .td-side { order: 1; }
.td-left > .td-tabs { order: 2; }

@media (min-width: 960px) {
	.ticket-detail-card-text {
		display: flex;
		flex-direction: column;
		overflow: hidden !important;
	}
	.ticket-detail-grid {
		display: grid;
		grid-template-columns: 8fr 4fr;
		grid-template-rows: 1fr;
		grid-template-areas: "left side";
		column-gap: 16px;
		row-gap: 0;
		flex: 1 1 auto;
		min-height: 0;
		height: 100%;
	}
	.ticket-detail-grid > .td-left {
		display: block;
		grid-area: left;
		overflow-y: auto;
		min-height: 0;
		padding-right: 4px;
		overscroll-behavior: contain;
	}
	.ticket-detail-grid > .td-side {
		grid-area: side;
		overflow-y: auto;
		min-height: 0;
		height: 100%;
		overscroll-behavior: contain;
	}
}

.unread-banner {
	position: absolute !important;
	bottom: 16px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 2;
	text-transform: none !important;
	letter-spacing: 0 !important;
}

.ticket-tabs-items {
	background-color: transparent !important;
	overflow: visible !important;
}

.message-meta-mine {
	display: flex;
	justify-content: flex-end;
	padding: 0 4px 1px 0;
	line-height: 1;
}

.conversation-header {
	display: flex;
	align-items: center;
	font-size: 13px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.75);
	text-transform: uppercase;
	letter-spacing: 0.06em;
	padding: 6px 4px;
	margin-bottom: 8px;
	border-bottom: 2px solid #3C41C3;
}

.message-composer {
	margin-top: 12px;
	padding: 10px;
	background-color: #fafbfc;
	border: 1px solid #e0e4ea;
	border-radius: 8px;
}

.typing-indicator {
	margin-top: 8px;
	padding: 0 4px;
	font-style: italic;
	display: flex;
	align-items: center;
	min-height: 16px;
}

.message-body >>> .mention-tag {
	background-color: #e3f2fd;
	color: #1565c0;
	padding: 1px 4px;
	border-radius: 4px;
	font-weight: 500;
}

.message-body >>> .mention-tag--me {
	background-color: #fff176;
	color: #6d4c00;
	font-weight: 700;
}

.message-body >>> .msg-link {
	color: #3C41C3;
	text-decoration: underline;
	text-decoration-color: rgba(60, 65, 195, 0.4);
	text-underline-offset: 2px;
	word-break: break-word;
	cursor: pointer;
}

.message-body >>> .msg-link:hover {
	color: #2d3199;
	text-decoration-color: #2d3199;
}

.message-item--mine .message-body >>> .msg-link {
	color: #3C41C3;
	text-decoration-color: rgba(60, 65, 195, 0.5);
}

.message-item--mine .message-body >>> .msg-link:hover {
	color: #2d3199;
	text-decoration-color: #2d3199;
}

.external-link-card {
	border-radius: 12px !important;
	overflow: hidden;
}

.external-link-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 20px;
	background: linear-gradient(135deg, #3C41C3 0%, #2d3199 100%);
	color: #fff;
}

.external-link-icon-wrap {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.18);
	flex-shrink: 0;
}

.external-link-icon {
	color: #fff !important;
}

.external-link-titles {
	min-width: 0;
}

.external-link-title {
	font-size: 16px;
	font-weight: 600;
	color: #fff;
	line-height: 1.2;
}

.external-link-subtitle {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.85);
	margin-top: 2px;
}

.external-link-body {
	padding: 18px 20px !important;
}

.external-link-warning {
	font-size: 13px;
	color: #424242;
	margin: 0 0 12px 0;
	line-height: 1.5;
}

.external-link-url-wrap {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	background: #f5f6ff;
	border: 1px solid #d7d9f2;
	border-radius: 8px;
	padding: 8px 12px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	color: #2d3199;
	max-height: 140px;
	overflow-y: auto;
}

.external-link-url-icon {
	flex-shrink: 0;
	color: #3C41C3 !important;
	margin-top: 1px;
}

.external-link-url {
	flex: 1 1 auto;
	min-width: 0;
	white-space: pre-wrap;
	word-break: normal;
	overflow-wrap: anywhere;
	line-height: 1.5;
}

.unread-divider {
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 12px 0;
	position: relative;
	color: #e53935;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
}
.unread-divider::before,
.unread-divider::after {
	content: "";
	flex: 1;
	height: 1px;
	background-color: #e53935;
	opacity: 0.6;
}
.unread-divider__label {
	padding: 0 10px;
	background-color: transparent;
	white-space: nowrap;
}

.read-receipts {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	gap: 2px;
	align-items: center;
	align-self: flex-end;
}
.read-receipts .read-receipt-avatar {
	flex: 0 0 auto;
}
.bubble-row {
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: 4px;
	max-width: 100%;
	min-width: 0;
}
.message-row--mine .bubble-row {
	flex-direction: row-reverse;
}
.read-receipt-avatar {
	border: 1px solid #fff;
}
.read-receipt-avatar__initials {
	font-size: 8px;
	line-height: 1;
	font-weight: 600;
}

.unread-dot {
	margin-right: 1px;
}
.unread-dot + .unread-dot {
	margin-left: 0;
}
.unread-dot--unread.unread-dot--unread {
	margin-left: 0 !important;
}
.indicators-col {
	padding-left: 8px !important;
	padding-right: 4px !important;
}
.indicators-cell {
	min-height: 16px;
}
.unread-dot:last-of-type {
	margin-right: 4px;
}

.live-viewer-avatar {
	--live-color: #4caf50;
	box-shadow: 0 0 0 2px var(--live-color);
	animation: live-viewer-pulse 1.6s ease-in-out infinite;
}
.live-viewer-avatar--lg {
	animation-name: live-viewer-pulse-lg;
}
.live-viewer-avatar__initials {
	font-size: 8px;
	line-height: 1;
	font-weight: 600;
}
@keyframes live-viewer-pulse {
	0%, 100% { box-shadow: 0 0 0 2px var(--live-color), 0 0 0 0 var(--live-color); }
	50%      { box-shadow: 0 0 0 3px var(--live-color), 0 0 6px 4px var(--live-color); }
}
@keyframes live-viewer-pulse-lg {
	0%, 100% { box-shadow: 0 0 0 2px var(--live-color), 0 0 0 0 var(--live-color); }
	50%      { box-shadow: 0 0 0 3px var(--live-color), 0 0 10px 6px var(--live-color); }
}

.field-label {
	font-size: 11px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.6);
	margin-bottom: 4px;
}

.support-contacts-header {
	cursor: pointer;
	user-select: none;
}
.support-contacts-header:hover .field-label {
	color: rgba(0, 0, 0, 0.87);
}

.contact-card {
	background-color: #f5f7fa;
	border-left: 3px solid #cfd8dc;
	border-radius: 4px;
	padding: 6px 10px;
}

.field-value {
	background-color: #f5f7fa;
	border-left: 3px solid #cfd8dc;
	border-radius: 4px;
	padding: 6px 10px;
	color: rgba(0, 0, 0, 0.87);
	min-height: 32px;
}

.field-value--empty {
	color: rgba(0, 0, 0, 0.38);
	font-style: italic;
}

.field-block {
	margin-bottom: 12px;
}

.todos-block {
	margin-top: 16px;
}

.todo-item {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	padding: 4px 0;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.todo-item__check {
	flex: 0 0 auto;
	margin-top: -2px !important;
}

.todo-item__body {
	flex: 1 1 auto;
	min-width: 0;
}

.todo-item__text {
	word-break: break-word;
	cursor: text;
}

.todo-item__meta {
	margin-top: 2px;
	font-size: 11px;
	line-height: 1.3;
}

.todo-item__actions {
	flex: 0 0 auto;
	display: flex;
	gap: 2px;
}

.todo-item--completed .todo-item__text {
	text-decoration: line-through;
	color: rgba(0, 0, 0, 0.5);
}

.todo-add__input {
	margin-top: 0;
	padding-top: 0;
}

.due-date--overdue >>> .v-input__slot {
	border-color: #d32f2f !important;
}
.due-date--overdue >>> input,
.due-date--overdue >>> .v-icon {
	color: #d32f2f !important;
}

.ticketing-toolbar--desktop {
	padding-top: 8px;
	padding-bottom: 4px;
	margin-top: 4px;
	padding-left: 8px;
	padding-right: 8px;
}

.ticketing-toolbar__row--secondary {
	margin-top: 8px;
	padding-left: 8px;
	padding-right: 8px;
}

.ticketing-toolbar__filters {
	max-width: 100%;
}

.ticket-detail-card {
	position: relative;
}

.ticket-detail-close {
	position: absolute;
	top: 34px;
	right: 8px;
	transform: translateY(-50%);
	z-index: 2;
}

.ticket-detail-title {
	padding-right: 48px !important;
}

.ticket-detail-nav__count {
	min-width: 44px;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.ticket-status-break {
	display: none;
}

.ticket-detail-nav-break {
	display: none;
}

@media (max-width: 599px) {
	.ticket-detail-title {
		padding-right: 48px !important;
	}

	.ticket-status-break {
		display: block;
		flex-basis: 100%;
		width: 100%;
		height: 0;
	}

	.ticket-detail-nav-break {
		display: block;
		flex-basis: 100%;
		width: 100%;
		height: 0;
	}

	.ticket-detail-nav {
		margin-left: auto;
	}

	.ticket-status-actions {
		margin-left: auto;
		margin-right: -4px;
	}
}

.ticket-close-btn {
	text-transform: none;
	letter-spacing: 0;
	font-weight: 500;
	border-radius: 16px;
	padding: 0 9px 0 6px !important;
	min-width: 0 !important;
}

.ticket-close-btn >>> .v-btn__content {
	align-items: center;
	line-height: 1;
}

.ticket-close-btn >>> .v-btn__content .v-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
	height: 1em;
	vertical-align: middle;
	margin-right: 4px !important;
	position: relative;
	top: 1px;
}

.ticket-close-btn >>> .v-btn__content .v-icon.v-icon--left {
	margin-left: 0 !important;
}

.ticketing-filter-toggle {
	max-width: 100%;
	overflow-x: auto;
	flex-wrap: nowrap;
	-webkit-overflow-scrolling: touch;
}

.ticketing-filter-toggle >>> .v-btn {
	flex: 0 0 auto;
}

.activity-list {
	max-height: 60vh;
	overflow-y: auto;
	padding-right: 4px;
}

.activity-item {
	display: flex;
	gap: 10px;
	padding: 6px 0;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.activity-item:last-child {
	border-bottom: none;
}

.activity-dot {
	margin-top: 4px;
	flex: 0 0 auto;
}

.activity-body {
	flex: 1 1 auto;
	min-width: 0;
	word-break: break-word;
}

.activity-value {
	background: #eef2f7;
	border-radius: 3px;
	padding: 0 4px;
	font-family: monospace;
	font-size: 0.85em;
}

.attachment-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.attachment-image-link {
	display: inline-block;
	border-radius: 6px;
	overflow: hidden;
	border: 1px solid rgba(0, 0, 0, 0.08);
	background: #fafafa;
}

.attachment-image {
	display: block;
	max-width: 140px;
	max-height: 110px;
	object-fit: cover;
}

.attachment-file {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	padding: 4px 8px;
	background: #f5f7fa;
	border: 1px solid rgba(0, 0, 0, 0.08);
	border-radius: 4px;
	color: rgba(0, 0, 0, 0.87);
	text-decoration: none;
	font-size: 13px;
}

.attachment-file__icon {
	flex: 0 0 auto;
}

.attachment-file__name {
	flex: 1 1 auto;
	min-width: 0;
	word-break: break-all;
	overflow-wrap: anywhere;
}

.attachment-file__size {
	flex: 0 0 auto;
	white-space: nowrap;
}

.attachment-file--tile {
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	width: 140px;
	height: 110px;
	max-width: 140px;
	padding: 8px;
	gap: 2px;
}

.attachment-file--tile .attachment-file__name {
	flex: 0 0 auto;
	width: 100%;
	display: flex;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: baseline;
	min-width: 0;
	font-weight: 500;
	font-size: 12px;
	line-height: 1.2;
}

.attachment-file--tile .attachment-file__name-base {
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.attachment-file--tile .attachment-file__name-ext {
	flex: 0 0 auto;
	white-space: nowrap;
}

.attachment-file__icon-large {
	font-size: 22px !important;
	color: rgba(0, 0, 0, 0.54);
}

.attachment-file:hover {
	background: #e8eef5;
}

.mobile-ticket-list {
	display: flex;
	flex-direction: column;
}

.mobile-ticket-row {
	display: flex;
	align-items: stretch;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	cursor: pointer;
	background-color: #fff;
}

.mobile-ticket-row:active {
	background-color: rgba(0, 0, 0, 0.04);
}

.mobile-ticket-priority {
	width: 4px;
	flex-shrink: 0;
}

.mobile-ticket-body {
	flex: 1 1 auto;
	min-width: 0;
	padding: 8px 10px;
}

.mobile-ticket-title {
	font-size: 13px;
	font-weight: 600;
	line-height: 1.2;
	flex: 1 1 auto;
	min-width: 0;
}

.mobile-ticket-status {
	white-space: nowrap;
}

.mobile-ticket-meta {
	font-size: 11px;
	line-height: 1.2;
	margin-top: 2px;
}

.mobile-ticket-chip {
	height: 18px !important;
	font-size: 10px !important;
}

.viewers-stack .viewer-avatar {
	border: 2px solid #fff;
	margin-left: -8px;
	font-size: 10px !important;
}
.viewers-stack .viewer-avatar:first-child {
	margin-left: 0;
}

/* Image preview dialog */
.image-preview-dialog {
	background: #000 !important;
	box-shadow: none !important;
	overflow: hidden !important;
}
.image-preview-container {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: center;
	width: 100vw;
	height: 100vh;
	background: #000;
	overflow: hidden;
}
.image-preview-img {
	display: block;
	flex: 1 1 auto;
	min-height: 0;
	max-width: 100vw;
	max-height: 100%;
	width: auto;
	height: auto;
	object-fit: contain;
	background: #000;
	align-self: center;
}
.image-preview-close {
	position: absolute !important;
	top: 8px;
	right: 8px;
	background: rgba(0, 0, 0, 0.4) !important;
	z-index: 2;
}
.image-preview-nav {
	position: absolute !important;
	top: 50%;
	transform: translateY(-50%);
	background: rgba(0, 0, 0, 0.4) !important;
	z-index: 2;
}
.image-preview-nav--prev {
	left: 8px;
}
.image-preview-nav--next {
	right: 8px;
}
.image-preview-caption {
	display: flex;
	align-items: center;
	width: 100%;
	flex: 0 0 auto;
	padding: 6px 12px;
	color: #fff;
	background: rgba(0, 0, 0, 0.6);
}
</style>
